// A sauna / steam room's ceiling (docs/claude/ceilings.md, *Shaped ceilings*). Pure and
// DOM-free (unit-tested). Only PARAMETERS are stored (`inst.ceiling`); every
// figure is derived here on each read. A flat room keeps the flat code path of
// every consumer (`isFlatCeiling`), so a drawing without the field draws exactly
// as it always did.
//
// Every shaped surface is ONE rule: value(p) = max over index sets G of (min over
// j in G of piece_j(p)) — a max–min lattice of planes and cylinders, built per
// shape by `resolveCeiling` in the room's LOCAL frame, on `wallView` (jambs
// dropped). The outer and the inner rings are `resolvedOutline(view, 0 | w)`.

import type {
  CeilingShape,
  ProductInstance,
  Vec2,
} from '../shared/types';
import { traitsOf } from '../shared/categories';
import { pointInPolygon } from './geometry';
import {
  effectiveCornerRadii,
  heaterWallBands,
  isPolyRoom,
  mitreAt,
  polyBenchBands,
  polyBenchLifts,
  polyEdges,
  polygonArea,
  polyInnerBenchBands,
  polyInnerWallFill,
  resolvedOutlinePaths,
  wallRuns,
  wallView,
} from './polyRoom';
import {
  benchHeight,
  ceilingThicknessOf,
  doorTop,
  externalHeight,
  glassPaneTop,
  heaterWallTop,
  innerWallTop,
  interiorHeight,
  openingArchitrave,
} from './heights';
import {
  wallThicknessOf,
  wallThicknessRange,
} from './dent';
import {
  convexMerge,
  earClip,
} from '../shared/triangulate';
import {
  glassPolysLocal,
  openingBandLocal,
} from './glass';
import { roomDoors } from './door';
import { roomDoor } from './roomDoor';

/** Every shape, in the order the Shape selector lists them. */
export const CEILING_SHAPES: readonly CeilingShape[] = ['flat', 'slope', 'gable', 'frustum', 'cove', 'barrel', 'pyramid', 'dome'];
/** The shapes that store the INSIDE springing height. */
export const SPRINGING_SHAPES: readonly CeilingShape[] = ['barrel', 'cove', 'dome'];
/** The shapes with a flat part set back from every wall (`setbacks`). */
export const SETBACK_SHAPES: readonly CeilingShape[] = ['cove', 'frustum'];
/** The shapes that store the OUTSIDE eave height. */
export const EAVE_SHAPES: readonly CeilingShape[] = ['slope', 'gable', 'frustum', 'pyramid'];
/** Each shape's English name (the selector's option text). */
export const CEILING_SHAPE_LABELS: Readonly<Record<CeilingShape, string>> = {
  flat: 'Flat', slope: 'Single slope', gable: 'Gable', frustum: 'Truncated pyramid',
  cove: 'Coved', barrel: 'Barrel', pyramid: 'Pyramid', dome: 'Dome',
};
/** The Shape selector's options: every shape (each drawn everywhere). */
export const CEILING_SHAPE_OPTIONS: ReadonlyArray<readonly [CeilingShape, string]> =
  CEILING_SHAPES.map((s) => [s, CEILING_SHAPE_LABELS[s]] as const);

/** The first switch from flat raises the crown this much; the walls stay. */
export const FIRST_SWITCH_RISE = 400;
/** mm of inside span a gable's ridge keeps from each side. */
export const MIN_RIDGE_SIDE = 200;
/** mm a pyramid's / dome's apex keeps from every inner face. */
export const APEX_CLEARANCE = 50;
/** mm inside its kernel a pyramid's / dome's apex stands with none stored, where
 *  the kernel is that deep (`defaultApexIn`). */
export const APEX_DEPTH = 600;
/** A truncated pyramid's setback on the first switch, and for a missing entry. */
export const FRUSTUM_SETBACK = 600;
/** A truncated pyramid's flat is never shorter, nor narrower, than this. */
export const MIN_FLAT_SIDE = 100;
/** The least POSITIVE setback: a sloped face's numeric floor. */
export const MIN_SLOPED_SETBACK = 1;
/** Chord tolerances (mm) of curved surfaces: sections, meshes, the volume. */
export const SECTION_SAG = 1.5;
export const MESH_SAG = 2;
export const VOLUME_SAG = 0.5;
/** A fall arrow's shaft in a DXF export (mm); the canvas draws it 28 px long. */
export const CEILING_ARROW_MM = 400;

/** The least setback a SLOPED face may have: a face of true normal thickness T
 *  needs its distance to its flat edge (w + d) above T, so only a ceiling
 *  thicker than its walls raises it past 1 mm. */
export function leastSlopedSetback(w: number, T: number): number {
  return Math.max(MIN_SLOPED_SETBACK, T + 1 - w);
}

/** A message for the command bar: an English key of `CEILING_STRINGS` and the
 *  values of its placeholders — `{n}` a length (shown in the drawing's dimension
 *  style), `{limit}` the length `{n}` falls short of (in that style too, and
 *  `{n}` then rounded DOWN, so it never reads as the limit itself), `{min}` /
 *  `{max}` plain numbers in the unit the text writes beside them —, and `then` a
 *  second message shown after it (what else moved, before why the typed figure
 *  was clamped). */
export interface CeilingNote { text: string; n?: number; limit?: number; min?: number; max?: number; then?: CeilingNote }

const MSG = {
  thickness: 'The ceiling thickness is kept between {min} and {max} mm.',
  crownBelowEave: 'The crown can’t be lower than the eave.',
  crownBelowSpringing: 'The crown can’t be lower than the springing height plus the ceiling thickness.',
  barrelCrownHeld: 'A barrel rises at most half its span — the crown is held at {n}.',
  domeCrownHeld: 'A dome rises at most a quarter circle from its nearest wall — the crown is held at {n}.',
  eaveAboveCrown: 'The eave can’t be higher than the crown.',
  eaveBelowThickness: 'The eave has to be higher than the ceiling thickness.',
  springingAboveCrown: 'The springing can’t be higher than the crown less the ceiling thickness.',
  barrelSpringingHeld: 'A barrel rises at most half its span — the springing is held at {n}.',
  domeSpringingHeld: 'A dome rises at most a quarter circle from its nearest wall — the springing is held at {n}.',
  springingFloor: 'The springing has to be above the floor.',
  riseNegative: 'The rise can’t be negative.',
  pitchRange: 'The pitch has to be at least 0° and less than 90°.',
  crownMoved: 'The crown moved with the ceiling thickness, to {n}.',
  apexKept: 'The apex was kept where it sees every wall.',
  ridgeSide: 'The ridge stays at least 200 mm from the walls.',
  ridgeMiddle: 'The ridge stays in the middle of a room this narrow.',
  noKernel: 'No point of this room sees every wall',
  noFlat: 'No setbacks keep a flat of at least 100 mm in this room',
  pyramidInvalid: 'This pyramid needs a point that sees every wall — drawn flat at {n} until the room is reshaped.',
  domeInvalid: 'This dome needs a point that sees every wall — drawn flat at {n} until the room is reshaped.',
  frustumInvalid: 'This truncated pyramid no longer fits the room — drawn flat at {n} until the room is reshaped.',
  coveInvalid: 'This coved ceiling no longer fits the room — drawn flat at {n} until the room is reshaped.',
  barrelCapped: 'Rise capped at a semicircle — the crown is {n}.',
  domeCapped: 'Rise capped at a quarter circle on the nearest wall — the crown is {n}.',
  cantRise: 'This wall can’t rise straight to the crown: the room continues past its line or one of its corners is rounded — its setback is kept at {n}.',
  thickSetback: 'Under a ceiling thicker than its walls a sloped face needs a setback of at least {n}; a smaller one makes the wall rise straight to the crown.',
  setbacksAdjusted: 'The setbacks were adjusted to keep the flat part at least 100 mm wide.',
  setbacksIncreased: 'The setbacks were increased to the least the walls and rounded corners allow.',
  flatNarrow: 'The flat part can’t be narrower than 100 mm.',
  setbackKept: 'The setback is kept at least {n} from the walls.',
  headroom: 'Headroom above the top bench is {n} — less than {limit}.',
  drip: 'A ceiling face slopes less than {min}° — condensation will drip.',
} as const;

/** The Ceiling card's direction row, by the shapes that have a direction wall. */
export const DIRECTION_LABELS: Readonly<Partial<Record<CeilingShape, string>>> = {
  slope: 'Falls toward', gable: 'Ridge along', barrel: 'Runs along',
};
/** The Ceiling card's Pick on plan button, armed and not. */
export const CEILING_PICK_LABELS = { pick: 'Pick on plan', cancel: 'Cancel pick' } as const;
/** The command bar's prompt while a direction wall is picked on the plan. */
export const CEILING_PICK_HINTS: Readonly<Partial<Record<CeilingShape, string>>> = {
  slope: 'ceiling — click the wall it falls toward',
  gable: 'ceiling — click the wall the ridge runs along',
  barrel: 'ceiling — click the wall the barrel runs along',
};
/** The multi-selection's message when some rooms can't take a Shape. */
export const SHAPE_REFUSED = '{n} of the selected rooms can’t take this shape ({reason}) and were left as they were.';

/** Every English string this module hands to tr(). They reach it through a
 *  variable, which the i18n gate cannot see: test/ceiling.test.mjs checks each
 *  one has its DICT entry. */
export const CEILING_STRINGS: readonly string[] = [
  ...Object.values(MSG), ...Object.values(CEILING_SHAPE_LABELS), ...Object.values(DIRECTION_LABELS) as string[], SHAPE_REFUSED,
  ...Object.values(CEILING_PICK_LABELS), ...Object.values(CEILING_PICK_HINTS) as string[],
];

const note = (text: string, n?: number): CeilingNote => (n === undefined ? { text } : { text, n });

// ---- The resolver's types --------------------------------------------------
// Every point is in the room's LOCAL frame, every height in mm above the room floor.

/** A planar piece: z = gx·x + gy·y + c. */
export interface Plane { kind: 'plane'; gx: number; gy: number; c: number }
/** A one-sided cylinder, clamped at `top` (a dome's face): σ = n·p − d0, value
 *  zc + √(r² − (top − min(σ, top))²) — it rises to zc + r at σ = top, then stays. */
export interface Cyl { kind: 'cyl'; nx: number; ny: number; d0: number; top: number; zc: number; r: number }
/** A symmetric cylinder about the axis at n·p = um (the barrel). */
export interface Vault { kind: 'vault'; nx: number; ny: number; um: number; zc: number; r: number }
/** A coved ceiling's side, one-sided like a `Cyl`: o = top − σ (σ = n·p − d0) is
 *  the offset from its crown line toward its wall, and the profile the quarter
 *  ellipse of half-axes a (across) and b (up) about (0, zc), pushed out SQUARE to
 *  itself by t (the outer surface's true normal thickness; 0 for the underside):
 *  it leaves zc vertically at o = a + t and meets zc + b + t level at o = 0
 *  (`coveZ`). Past its crown line it rises on, unlike a `Cyl`, so the flat at
 *  zc + b + t is the lower there and no tie hands the flat to a cove. */
export interface Cove { kind: 'cove'; nx: number; ny: number; d0: number; top: number; zc: number; a: number; b: number; t: number }
export type Piece = Plane | Cyl | Vault | Cove;
/** value(p) = max over sets S of (min over j in S of piece_j(p)). */
export interface Surface { pieces: Piece[]; sets: number[][] }

/** A CONVEX piece of a surface's tiling. */
export interface CeilingPiece { piece: number; ring: Vec2[]; curved: boolean }
/** One triangle of the same tiling (conforming, counter-clockwise). */
export interface CeilingTri { piece: number; a: Vec2; b: Vec2; c: Vec2 }
/** Where two pieces meet. */
export interface CreaseSeg { a: Vec2; b: Vec2; pieces: [number, number]; kind: 'crease' | 'ridge' }
/** A point of a profile along a chord: `s` the distance from its start. */
export interface ProfilePt { s: number; h: number; sample?: boolean; curveNext?: boolean }

/** A room's resolved ceiling. `flat` says the room draws FLAT (a flat room, or
 *  a shape that could not be built); the frustum's flat central polygon is
 *  `flatRing`, not a second `flat` (the plan's §2.1 declared both). */
export interface CeilingField {
  /** The STORED shape. */
  shape: CeilingShape;
  flat: boolean;
  /** Why the stored shape could not be built (the panel's warning). */
  invalid?: CeilingNote;
  /** A barrel / dome rise held at its cap. */
  capped?: { crown: number; reason: CeilingNote };
  /** The OUTSIDE eave: stored for slope / gable / frustum / pyramid, derived for barrel / dome. */
  eave: number;
  /** Barrel / dome: the INSIDE springing height (stored). */
  springing?: number;
  /** The effective external crown. */
  crown: number;
  /** The ceiling's thickness T and the walls' w. */
  thickness: number;
  wallT: number;
  outerRing: Vec2[];
  innerRing: Vec2[];
  outer: Surface;
  under: Surface;
  wall?: number;
  uAxis?: { n: Vec2; a: Vec2; u0Out: number; u1Out: number; u0In: number; u1In: number };
  /** Pyramid / dome: the effective (clamped) inner apex and its height. */
  apex?: Vec2;
  apexHeight?: number;
  /** Where the outer surface reaches the crown. */
  crownAt?: Vec2;
  /** Gable: u of the inner / outer ridge. */
  ridgeIn?: number;
  ridgeOut?: number;
  /** Frustum: the setbacks by wall (0 = a vertical wall). */
  setbacks?: number[];
  /** Frustum: the underside's flat central polygon, one corner per wall. */
  flatRing?: Vec2[];
  /** Barrel / dome: the inside rise it may reach (the semicircle / quarter circle). */
  riseCap?: number;
  /** The underside's lowest point along the inner wall faces (= S for barrel / dome). */
  interiorEave: number;
  /** The underside's highest point. */
  interiorCrown: number;
  /** Each planar piece's pitch (index = piece); [] for curved shapes. */
  facePitchDeg: number[];
  /** The longest wall's direction and inward normal: level boards run along it. */
  boards: { u: Vec2; n: Vec2 };
}

/** A room that has a ceiling at all: a walled sauna or steam room. */
export function hasCeiling(inst: ProductInstance): boolean {
  return !!inst.autoBenches && traitsOf(inst.category).room && isPolyRoom(inst);
}

/** The stored shape; absent or unknown reads as flat. */
export function ceilingShapeOf(inst: ProductInstance): CeilingShape {
  const s = inst.ceiling?.shape;
  return s && CEILING_SHAPES.includes(s) ? s : 'flat';
}

/** No ceiling, or a flat one: every consumer keeps its flat path. */
export function isFlatCeiling(inst: ProductInstance): boolean {
  return !hasCeiling(inst) || ceilingShapeOf(inst) === 'flat';
}

/** The shapes whose stored figure is the inside springing (barrel, cove, dome). */
export function springsInside(shape: CeilingShape): boolean {
  return SPRINGING_SHAPES.includes(shape);
}

/** The shapes whose flat is set back from the walls: the truncated pyramid and
 *  the coved ceiling share the setbacks, their clamp and their grips. */
export function hasSetbacks(shape: CeilingShape): boolean {
  return SETBACK_SHAPES.includes(shape);
}

const COLOR_RE = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;

/** The ceiling's OWN colour (`#rrggbb`, or `#rrggbbaa`), or null while it follows
 *  the walls — anything else stored there reads as following them. The ONE
 *  reader of `ceiling.color`: the panel and Copy colours read it, and
 *  `ceilingColorOf` falls back from it. */
export function ownCeilingColor(inst: ProductInstance): string | null {
  const c = inst.ceiling?.color;
  return typeof c === 'string' && COLOR_RE.test(c) ? c : null;
}

/** The ceiling's colour: its own, else the caller's wall default (section and
 *  3D each have their own). */
export function ceilingColorOf(inst: ProductInstance, wallDefault: string): string {
  return ownCeilingColor(inst) ?? wallDefault;
}

/** `ceilingColorOf` in six hex digits, for a section, which paints opaque: an
 *  own `#rrggbbaa` drops its alpha there, as in 3D. The wall default is passed
 *  through as it is. */
export function ceilingHex6(inst: ProductInstance, wallDefault: string): string {
  return ownCeilingColor(inst)?.slice(0, 7) ?? wallDefault;
}

/** A room's effective external top: its stored Height while flat, the shape's
 *  crown otherwise. */
export function roomCrown(inst: ProductInstance): number {
  return isFlatCeiling(inst) ? externalHeight(inst) : resolveCeiling(inst).crown;
}

// ---- Small geometry --------------------------------------------------------

const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
const mul = (a: Vec2, s: number): Vec2 => ({ x: a.x * s, y: a.y * s });
const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
const crs = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x;
const len = (a: Vec2): number => Math.hypot(a.x, a.y);
const lerp = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const sec = (t: number): number => Math.sqrt(1 + t * t);
const deg = (r: number): number => (r * 180) / Math.PI;
const rad = (d: number): number => (d * Math.PI) / 180;
const clampN = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function area2(poly: readonly Vec2[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) { const p = poly[i], q = poly[(i + 1) % poly.length]; a += p.x * q.y - q.x * p.y; }
  return a;
}

function centroidOf(poly: readonly Vec2[]): Vec2 {
  let x = 0, y = 0;
  for (const p of poly) { x += p.x; y += p.y; }
  return { x: x / poly.length, y: y / poly.length };
}


/** The centroid of a polygon's largest earClip triangle — a point guaranteed
 *  inside it, even when the polygon's own centroid falls in a notch. */
export function insidePoint(poly: readonly Vec2[]): Vec2 {
  let best: Vec2 = centroidOf(poly), bestA = -1;
  for (const t of earClip(poly)) {
    const a = Math.abs(area2(t));
    if (a > bestA) { bestA = a; best = centroidOf(t); }
  }
  return best;
}

/** Signed distance of `p` inward from a line (`a`, inward unit `n`). */
const sig = (e: { a: Vec2; n: Vec2 }, p: Vec2): number => e.n.x * (p.x - e.a.x) + e.n.y * (p.y - e.a.y);

/** Keep the part of a convex polygon where n·p ≥ c (Sutherland–Hodgman). */
function clipHalfPlane(poly: readonly Vec2[], n: Vec2, c: number): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i], q = poly[(i + 1) % poly.length];
    const fp = dot(n, p) - c, fq = dot(n, q) - c;
    if (fp >= 0) out.push(p);
    if ((fp > 0 && fq < 0) || (fp < 0 && fq > 0)) out.push(lerp(p, q, fp / (fp - fq)));
  }
  return out;
}

// ---- Rings and frames ------------------------------------------------------

/** One edge of a ring: `wall` its wallView wall for a straight run (−1 for an arc
 *  chord), `corner` the rounded corner a chord belongs to (−1 for a run). */
interface RingEdge { a: Vec2; b: Vec2; u: Vec2; n: Vec2; len: number; wall: number; corner: number }
interface TRing { pts: Vec2[]; edges: RingEdge[] }

function ringEdgeOf(a: Vec2, b: Vec2, ccw: boolean, wall: number, corner: number): RingEdge {
  const d = sub(b, a), L = len(d) || 1, u = mul(d, 1 / L);
  return { a, b, u, n: ccw ? { x: -u.y, y: u.x } : { x: u.y, y: -u.x }, len: L, wall, corner };
}

/** `resolvedOutline(view, inset)` with every edge tagged, points closer than
 *  1e-6 dropped (a straight run two rounded corners used up). */
function taggedRing(view: ProductInstance, inset: number, ccw: boolean): TRing {
  const pts: Vec2[] = [], wall: number[] = [], corner: number[] = [];
  resolvedOutlinePaths(view, inset).forEach((path, v) => path.forEach((p, i) => {
    const last = i === path.length - 1;
    pts.push(p); wall.push(last ? v : -1); corner.push(last ? -1 : v);
  }));
  const P: Vec2[] = [], W: number[] = [], C: number[] = [];
  for (let i = 0; i < pts.length; i++) {
    if (len(sub(pts[(i + 1) % pts.length], pts[i])) < 1e-6) continue;
    P.push(pts[i]); W.push(wall[i]); C.push(corner[i]);
  }
  return { pts: P, edges: P.map((a, i) => ringEdgeOf(a, P[(i + 1) % P.length], ccw, W[i], C[i])) };
}

/** The SHARP polygon's walls: direction, inward normal, length. */
interface Frame { n: number; ccw: boolean; E: Array<{ a: Vec2; b: Vec2; u: Vec2; n: Vec2; L: number }> }

function frameOf(view: ProductInstance): Frame {
  const edges = polyEdges(view);
  return {
    n: edges.length,
    ccw: polygonArea(view.polygon!) > 0,
    E: edges.map((e) => ({ a: e.a, b: e.b, u: e.dir, n: e.normal, L: e.len })),
  };
}

/** Corner v of the sharp polygon's walls pushed in by off[v − 1] and off[v]. */
function flatOf(fr: Frame, off: readonly number[]): Vec2[] {
  const { n, E } = fr;
  return E.map((e, v) => {
    const pe = E[(v - 1 + n) % n];
    return mitreAt(add(pe.a, mul(pe.n, off[(v - 1 + n) % n])), pe.u, add(e.a, mul(e.n, off[v])), e.u);
  });
}

function isReflexAt(fr: Frame, v: number): boolean {
  const c = crs(fr.E[(v - 1 + fr.n) % fr.n].u, fr.E[v].u);
  return fr.ccw ? c < -1e-12 : c > 1e-12;
}

/** Whether walls v − 1 and v run straight on through corner v. */
function inLineAt(fr: Frame, v: number): boolean {
  const a = fr.E[(v - 1 + fr.n) % fr.n].u, b = fr.E[v].u;
  return Math.abs(crs(a, b)) < 1e-9 && dot(a, b) > 0;
}

/** The run of collinear walls each wall belongs to. */
function runsOf(fr: Frame): number[] {
  const n = fr.n, id = new Array<number>(n).fill(0);
  let s = 0;
  while (s < n && inLineAt(fr, s)) s++;
  if (s >= n) return id;
  let r = -1;
  for (let k = 0; k < n; k++) { const v = (s + k) % n; if (!inLineAt(fr, v)) r++; id[v] = r; }
  return id;
}

/** Every wall of a run takes the run's MEAN value. */
function meanRuns(runs: readonly number[], d: readonly number[]): number[] {
  const sum = new Map<number, number>(), cnt = new Map<number, number>();
  runs.forEach((r, k) => { sum.set(r, (sum.get(r) ?? 0) + d[k]); cnt.set(r, (cnt.get(r) ?? 0) + 1); });
  return d.map((_, k) => sum.get(runs[k])! / cnt.get(runs[k])!);
}

/** The longest wall (lowest index on a tie). */
function longestWall(view: ProductInstance): number {
  const e = polyEdges(view);
  let best = 0;
  for (let k = 1; k < e.length; k++) if (e[k].len > e[best].len + 1e-6) best = k;
  return best;
}

/** The stored direction wall, or the longest when absent / out of range. */
function directionWall(view: ProductInstance, c: ProductInstance['ceiling']): number {
  const n = view.polygon?.length ?? 0;
  const w = c?.wall;
  return Number.isInteger(w) && w! >= 0 && w! < n ? w! : longestWall(view);
}

// ---- Pieces and the lattice -------------------------------------------------

const plane = (gx: number, gy: number, c: number): Plane => ({ kind: 'plane', gx, gy, c });
const level = (z: number): Plane => plane(0, 0, z);

/** A curved piece's distance from its crown line across the arc: |x| for a
 *  vault, top − min(σ, top) for a cylinder or a cove. */
function arcOffset(pc: Cyl | Vault | Cove, p: Vec2): number {
  if (pc.kind === 'vault') return Math.abs(pc.nx * p.x + pc.ny * p.y - pc.um);
  return pc.top - Math.min(pc.nx * p.x + pc.ny * p.y - pc.d0, pc.top);
}

/** The parameter s = sin θ of the point of a cove's profile (`Cove`) at offset
 *  o < a + t: the ellipse's point (a·s, b·cos θ) moved t along its normal is at
 *  o(s) = s·(a + t·b/N), N = √(b²s² + a²cos²θ), whose slope a·(1 + t·a·b/N³)
 *  never falls to 0 — Newton from the concentric guess. */
function coveParam(o: number, a: number, b: number, t: number): number {
  let s = clampN(o / (a + t), 0, 1);
  if (t === 0) return s;
  for (let it = 0; it < 30; it++) {
    const N2 = b * b * s * s + a * a * Math.max(0, 1 - s * s), N = Math.sqrt(N2);
    const ds = (s * (a + (t * b) / N) - o) / (a * (1 + (t * a * b) / (N2 * N)));
    s = clampN(s - ds, 0, 1);
    if (Math.abs(ds) < 1e-15) break;
  }
  return s;
}

/** A cove's height at offset o from its crown line; past its reach it falls on
 *  below zc, so the springing plane wins there, and past its crown line it rises
 *  on above the flat. A point a nanometre from its reach is on it: the profile
 *  stands vertical there, so σ's last digit (an arc chord's slanted normal) would
 *  lift a wall's inner face off the springing by 10⁻⁵ mm. */
function coveZ(o: number, a: number, b: number, t: number, zc: number): number {
  const R = a + t;
  if (o >= R - 1e-9) return zc - Math.max(0, o - R);
  if (o <= 0) return zc + b + t - o;
  const s = coveParam(o, a, b, t), c = Math.sqrt(Math.max(0, 1 - s * s));
  return t === 0 ? zc + b * c : zc + c * (b + (t * a) / Math.sqrt(b * b * s * s + a * a * c * c));
}

/** A piece's height at p. Past its reach (offset > r) a curved piece falls on
 *  below its centre line, so the springing plane through that line — a barrel or
 *  dome at its cap, zc = S — wins the tie there: a ledge is the level piece. */
function pieceValue(pc: Piece, p: Vec2): number {
  if (pc.kind === 'plane') return pc.gx * p.x + pc.gy * p.y + pc.c;
  if (pc.kind === 'cove') return coveZ(pc.top - (pc.nx * p.x + pc.ny * p.y - pc.d0), pc.a, pc.b, pc.t, pc.zc);
  const d = arcOffset(pc, p);
  return d <= pc.r ? pc.zc + Math.sqrt(pc.r * pc.r - d * d) : pc.zc - (d - pc.r);
}

/** A piece's unit normal (pointing up) at a plan point. */
function pieceNormal(pc: Piece, p: Vec2): [number, number, number] {
  const norm = (v: [number, number, number]): [number, number, number] => { const l = Math.hypot(...v) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
  if (pc.kind === 'plane') return norm([0 - pc.gx, 0 - pc.gy, 1]);
  // The direction the arc falls away in plan, and the offset along it.
  const d = arcOffset(pc, p);
  if (pc.kind === 'cove') {
    // An offset curve's normal is its ellipse's at the same parameter.
    if (d >= pc.a + pc.t || pc.nx * p.x + pc.ny * p.y - pc.d0 > pc.top) return norm([-pc.nx, -pc.ny, 1]);
    const s = coveParam(d, pc.a, pc.b, pc.t);
    return norm([-pc.b * s * pc.nx, -pc.b * s * pc.ny, pc.a * Math.sqrt(Math.max(0, 1 - s * s))]);
  }
  const sx = pc.kind === 'vault' ? Math.sign(pc.nx * p.x + pc.ny * p.y - pc.um) : -1;
  return d > pc.r ? norm([sx * pc.nx, sx * pc.ny, 1]) : norm([sx * d * pc.nx, sx * d * pc.ny, Math.sqrt(pc.r * pc.r - d * d)]);
}

/** A piece's texture coordinates (mm, true surface length): boards run DOWN a
 *  slope and ACROSS a vault; a level piece's along the longest wall. */
function pieceUV(cf: CeilingField, pc: Piece, p: Vec2): [number, number] {
  if (pc.kind === 'plane') {
    const g = Math.hypot(pc.gx, pc.gy);
    if (g < 1e-12) return [p.x * cf.boards.u.x + p.y * cf.boards.u.y, p.x * cf.boards.n.x + p.y * cf.boards.n.y];
    const d = { x: -pc.gx / g, y: -pc.gy / g }, c = { x: -d.y, y: d.x };
    return [p.x * c.x + p.y * c.y, -(p.x * d.x + p.y * d.y) * Math.sqrt(1 + g * g)];
  }
  const axial = -pc.ny * p.x + pc.nx * p.y;
  if (pc.kind === 'cove') return [axial, coveArc(pc, Math.min(arcOffset(pc, p), pc.a + pc.t))];
  const x = pc.kind === 'vault' ? pc.nx * p.x + pc.ny * p.y - pc.um : arcOffset(pc, p);
  return [axial, pc.r * Math.asin(Math.max(-1, Math.min(1, x / pc.r)))];
}

/** The length along a cove's profile from its crown line to offset o: the
 *  ellipse's arc to the same parameter (Simpson) plus t times the angle its
 *  normal has turned. */
function coveArc(pc: Cove, o: number): number {
  const s = coveParam(o, pc.a, pc.b, pc.t), th = Math.asin(s), K = 16;
  const speed = (u: number): number => Math.hypot(pc.a * Math.cos(u), pc.b * Math.sin(u));
  let sum = speed(0) + speed(th);
  for (let k = 1; k < K; k++) sum += (k % 2 ? 4 : 2) * speed((th * k) / K);
  return (sum * th) / (3 * K) + pc.t * Math.atan2(pc.b * s, pc.a * Math.cos(th));
}

/** A surface's pieces as flat arrays — a kind code (0 plane, 1 cylinder, 2
 *  vault, 3 cove) and the figures `pieceValue` reads (a cove's half-axes in `r`
 *  and `rz`, its push in `t`) —, so the tilings and the evaluator work out a
 *  value with `pieceValue`'s very arithmetic, without a property lookup on
 *  pieces of four shapes. */
interface Packed { kind: Int8Array; a: Float64Array; b: Float64Array; c: Float64Array; top: Float64Array; zc: Float64Array; r: Float64Array; rz: Float64Array; t: Float64Array }
const PACKED = new WeakMap<Surface, Packed>();
function packed(S: Surface): Packed {
  let P = PACKED.get(S);
  if (P) return P;
  const m = S.pieces.length, F = (): Float64Array => new Float64Array(m);
  P = { kind: new Int8Array(m), a: F(), b: F(), c: F(), top: F(), zc: F(), r: F(), rz: F(), t: F() };
  S.pieces.forEach((pc, j) => {
    if (pc.kind === 'plane') { P!.a[j] = pc.gx; P!.b[j] = pc.gy; P!.c[j] = pc.c; return; }
    P!.kind[j] = pc.kind === 'cyl' ? 1 : pc.kind === 'vault' ? 2 : 3;
    P!.a[j] = pc.nx; P!.b[j] = pc.ny; P!.c[j] = pc.kind === 'vault' ? pc.um : pc.d0;
    if (pc.kind !== 'vault') P!.top[j] = pc.top;
    P!.zc[j] = pc.zc;
    if (pc.kind === 'cove') { P!.r[j] = pc.a; P!.rz[j] = pc.b; P!.t[j] = pc.t; } else P!.r[j] = pc.r;
  });
  PACKED.set(S, P);
  return P;
}
/** `pieceValue` of piece j at (x, y). */
function packedValue(P: Packed, j: number, x: number, y: number): number {
  const k = P.kind[j];
  if (k === 0) return P.a[j] * x + P.b[j] * y + P.c[j];
  const s = P.a[j] * x + P.b[j] * y - P.c[j], r = P.r[j];
  if (k === 3) return coveZ(P.top[j] - s, r, P.rz[j], P.t[j], P.zc[j]);
  const d = k === 2 ? Math.abs(s) : P.top[j] - Math.min(s, P.top[j]);
  return d <= r ? P.zc[j] + Math.sqrt(r * r - d * d) : P.zc[j] - (d - r);
}
/** Every piece's `pieceValue` at (x, y), into `out` from `at` on. */
function packedValues(P: Packed, x: number, y: number, out: Float64Array, at = 0): void {
  const { kind, a, b, c, top, zc, r: R } = P;
  for (let j = 0; j < kind.length; j++) {
    const k = kind[j];
    if (k === 0) { out[at + j] = a[j] * x + b[j] * y + c[j]; continue; }
    const s = a[j] * x + b[j] * y - c[j], r = R[j];
    if (k === 3) { out[at + j] = coveZ(top[j] - s, r, P.rz[j], P.t[j], zc[j]); continue; }
    const d = k === 2 ? Math.abs(s) : top[j] - Math.min(s, top[j]);
    out[at + j] = d <= r ? zc[j] + Math.sqrt(r * r - d * d) : zc[j] - (d - r);
  }
}

interface SurfEval { value(p: Vec2): number; active(p: Vec2): number; both(p: Vec2): [number, number] }
const EVALS = new WeakMap<Surface, SurfEval>();

function evaluator(S: Surface): SurfEval {
  const hit = EVALS.get(S);
  if (hit) return hit;
  const m = S.pieces.length, vals = new Float64Array(m), P = packed(S);
  const both = (p: Vec2): [number, number] => {
    packedValues(P, p.x, p.y, vals);
    let best = -Infinity, arg = -1;
    for (const G of S.sets) {
      let mn = Infinity, am = -1;
      for (const j of G) { const v = vals[j]; if (v < mn || (v === mn && j < am)) { mn = v; am = j; } }
      if (mn > best || (mn === best && am < arg)) { best = mn; arg = am; }
    }
    return [best, arg];
  };
  const e: SurfEval = { both, value: (p) => both(p)[0], active: (p) => both(p)[1] };
  EVALS.set(S, e);
  return e;
}

/** Drop duplicate sets, and every set that contains another: a superset's
 *  minimum is never the larger, so it never wins the max. */
function minimalSets(all: number[][]): number[][] {
  const uniq = [...new Map(all.map((s) => { const k = [...s].sort((a, b) => a - b); return [k.join(','), k] as const; })).values()];
  const has = uniq.map((s) => new Set(s));
  return uniq.filter((S, i) => !uniq.some((Q, j) => j !== i && Q.length < S.length && Q.every((x) => has[i].has(x))));
}

/** The T = 0 tent's index sets over a face ring from apex A (§2.3): the faces on
 *  or above face k over its fan sector (A, v_k, v_k+1). */
function tentSets(ring: TRing, A: Vec2): number[][] {
  const F = ring.edges, n = F.length;
  const g = F.map((f) => { const d = sig(f, A); return { x: f.n.x / d, y: f.n.y / d }; });
  const rays = ring.pts.map((v) => { const d = sub(v, A), L = len(d) || 1; return { x: d.x / L, y: d.y / L }; });
  const S: number[][] = [];
  for (let i = 0; i < n; i++) {
    const r0 = rays[i], r1 = rays[(i + 1) % n], set: number[] = [];
    for (let j = 0; j < n; j++) {
      const dg = sub(g[j], g[i]);
      if (dot(dg, r0) >= -1e-12 && dot(dg, r1) >= -1e-12) set.push(j);
    }
    S.push(set);
  }
  return minimalSets(S);
}

/** A face's slope whose inner plane meets its target at height Λ above the eave
 *  while its outer twin, T square to it, meets the face line at the eave. */
function tentSlope(delta: number, Lam: number, T: number): number {
  return (delta * Lam + T * Math.sqrt(Math.max(0, delta * delta + Lam * Lam - T * T))) / (delta * delta - T * T);
}

/** max over q of min over j in `set` of (c_j + g_j·q), by its LP dual: the least
 *  barycentric combination of c over the triples (and opposite pairs) whose
 *  gradients surround the origin. Also returns the optimum point. */
function lpMax(set: readonly number[], g: readonly Vec2[], c: readonly number[]): { v: number; q: Vec2 } {
  let best = Infinity, q: Vec2 = { x: 0, y: 0 };
  const m = set.length;
  for (let a = 0; a < m; a++) for (let b = a + 1; b < m; b++) {
    const i = set[a], j = set[b];
    const cr = crs(g[i], g[j]);
    if (Math.abs(cr) < 1e-15 * len(g[i]) * len(g[j]) && dot(g[i], g[j]) < 0) {
      const li = len(g[i]), lj = len(g[j]);
      const v = (lj * c[i] + li * c[j]) / (li + lj);
      if (v < best) { best = v; const s = (c[j] - c[i]) / (li + lj); q = mul(g[i], s / (li || 1)); }
    }
  }
  for (let a = 0; a < m; a++) for (let b = a + 1; b < m; b++) for (let k = b + 1; k < m; k++) {
    const i = set[a], j = set[b], l = set[k];
    const gi = g[i], gj = g[j], gl = g[l];
    const d = (gj.x - gi.x) * (gl.y - gi.y) - (gl.x - gi.x) * (gj.y - gi.y);
    if (Math.abs(d) < 1e-30) continue;
    const lj = (-gi.x * (gl.y - gi.y) + (gl.x - gi.x) * gi.y) / d;
    const ll = ((gj.x - gi.x) * -gi.y + gi.x * (gj.y - gi.y)) / d;
    const li = 1 - lj - ll;
    if (li < -1e-12 || lj < -1e-12 || ll < -1e-12) continue;
    const v = li * c[i] + lj * c[j] + ll * c[l];
    if (v < best) {
      best = v;
      const a1 = gi.x - gj.x, b1 = gi.y - gj.y, r1 = c[j] - c[i], a2 = gi.x - gl.x, b2 = gi.y - gl.y, r2 = c[l] - c[i];
      const det = a1 * b2 - a2 * b1;
      if (Math.abs(det) > 1e-30) q = { x: (r1 * b2 - r2 * b1) / det, y: (a1 * r2 - a2 * r1) / det };
    }
  }
  return { v: best, q };
}

/** Brent's root of f on [a, b] (f(a), f(b) of opposite signs). */
function brent(f: (x: number) => number, a: number, b: number, tol = 1e-6, maxIt = 80): number {
  let fa = f(a), fb = f(b);
  if (fa === 0) return a;
  if (fb === 0) return b;
  if (fa * fb > 0) return Math.abs(fa) < Math.abs(fb) ? a : b;
  let c = a, fc = fa, d = b - a, e = d;
  for (let it = 0; it < maxIt; it++) {
    if (fb * fc > 0) { c = a; fc = fa; d = b - a; e = d; }
    if (Math.abs(fc) < Math.abs(fb)) { a = b; b = c; c = a; fa = fb; fb = fc; fc = fa; }
    const tol1 = 2e-16 * Math.abs(b) + tol / 2, xm = (c - b) / 2;
    if (Math.abs(xm) <= tol1 || fb === 0) return b;
    if (Math.abs(e) >= tol1 && Math.abs(fa) > Math.abs(fb)) {
      const s = fb / fa;
      let p: number, q: number;
      if (a === c) { p = 2 * xm * s; q = 1 - s; } else {
        const qq = fa / fc, r = fb / fc;
        p = s * (2 * xm * qq * (qq - r) - (b - a) * (r - 1));
        q = (qq - 1) * (r - 1) * (s - 1);
      }
      if (p > 0) q = -q; else p = -p;
      if (2 * p < Math.min(3 * xm * q - Math.abs(tol1 * q), Math.abs(e * q))) { e = d; d = p / q; } else { d = xm; e = d; }
    } else { d = xm; e = d; }
    a = b; fa = fb;
    b += Math.abs(d) > tol1 ? d : xm > 0 ? tol1 : -tol1;
    fb = f(b);
  }
  return b;
}

// ---- The resolver's inputs -------------------------------------------------

interface Common {
  view: ProductInstance;
  ccw: boolean;
  w: number;
  T: number;
  H: number;
  out: TRing;
  inn: TRing;
}

function commonOf(inst: ProductInstance): Common {
  const view = wallView(inst);
  const ccw = polygonArea(view.polygon!) > 0;
  const w = wallThicknessOf(inst);
  return {
    view, ccw, w,
    T: ceilingThicknessOf(inst),
    H: externalHeight(inst),
    out: taggedRing(view, 0, ccw),
    inn: taggedRing(view, w, ccw),
  };
}

/** The effective outside eave of slope / gable / frustum / pyramid. */
function eaveOf(inst: ProductInstance, H: number, T: number): number {
  const e = inst.ceiling?.eave;
  return Math.max(T + 1, finite(e) ? e : H - FIRST_SWITCH_RISE);
}

/** The effective inside springing of barrel / dome. */
function springingOf(inst: ProductInstance, H: number, T: number): number {
  const s = inst.ceiling?.springing;
  return Math.max(1, finite(s) ? s : H - T - FIRST_SWITCH_RISE);
}

/** The u-axis across wall `wall`: u(p) = n·(p − a), its extremes over both rings. */
function axisOf(cm: Common, wall: number): NonNullable<CeilingField['uAxis']> {
  const e = polyEdges(cm.view)[wall];
  const n = e.normal, a = e.a;
  const u = (p: Vec2): number => dot(n, sub(p, a));
  const uo = cm.out.pts.map(u), ui = cm.inn.pts.map(u);
  return { n, a, u0Out: Math.min(...uo), u1Out: Math.max(...uo), u0In: Math.min(...ui), u1In: Math.max(...ui) };
}

/** A plane z = z0 + t·u(p) along an axis, as a Plane. */
function axisPlane(ax: { n: Vec2; a: Vec2 }, t: number, z0: number): Plane {
  return plane(t * ax.n.x, t * ax.n.y, z0 - t * dot(ax.n, ax.a));
}

// ---- Internal model kept beside each field (tiling, setters) ----------------

interface TentModel { faces: RingEdge[]; delta: number[]; sets: number[][]; crownOf(Lam: number): { crown: number; q: Vec2 } }
interface CurveModel {
  kind: 'barrel' | 'cove' | 'dome';
  /** Per curved piece of each surface, its angle step and the coordinate its iso-lines run on. */
  lines(which: 'outer' | 'under', piece: number, sag: number): { coord(p: Vec2): number; at: number[] } | null;
}
interface FrustumModel { fr: Frame; d: number[]; vert: boolean[]; F: Vec2[]; Fp: Vec2[]; regions: Array<{ piece: number; ring: Vec2[] }>; wallFace: number[]; delta: number[] }
interface Model {
  cm: Common;
  E?: number;
  S?: number;
  tent?: TentModel;
  curve?: CurveModel;
  frustum?: FrustumModel;
  springPiece?: number;
  wallPieces?: number[];
}
// Kept ON the field (not in a module map): a test bundle, or a module bundled
// twice, reads a field another copy of this module built.
const MODEL_KEY = '__ceilingModel';
function setModel(f: CeilingField, m: Model): void {
  // A curve's iso-lines are read once per cell of every tiling: worked out once.
  if (m.curve) {
    const lines = m.curve.lines, memo = new Map<string, ReturnType<CurveModel['lines']>>();
    m.curve.lines = (which, piece, sag) => {
      const k = `${which}:${piece}:${sag}`;
      let v = memo.get(k);
      if (v === undefined) { v = lines(which, piece, sag); memo.set(k, v); }
      return v;
    };
  }
  Object.defineProperty(f, MODEL_KEY, { value: m, enumerable: false });
}
function modelOf(f: CeilingField): Model { return (f as unknown as Record<string, Model>)[MODEL_KEY]; }

function baseField(inst: ProductInstance, cm: Common, shape: CeilingShape): Omit<CeilingField, 'outer' | 'under' | 'eave' | 'crown' | 'interiorEave' | 'interiorCrown' | 'facePitchDeg' | 'flat'> {
  const e = polyEdges(cm.view)[longestWall(cm.view)];
  return { shape, thickness: cm.T, wallT: cm.w, outerRing: cm.out.pts, innerRing: cm.inn.pts, boards: { u: e.dir, n: e.normal } };
}

/** A shape that could not be built draws flat on its walls, where they stand. */
function flatField(inst: ProductInstance, cm: Common, shape: CeilingShape, why?: string): CeilingField {
  const spring = springsInside(shape);
  const S = spring ? springingOf(inst, cm.H, cm.T) : 0;
  const E = spring ? S + cm.T : shape === 'flat' ? cm.H : eaveOf(inst, cm.H, cm.T);
  const top = E, under = spring ? S : E - cm.T;
  const f: CeilingField = {
    ...baseField(inst, cm, shape),
    flat: true,
    eave: top, crown: top,
    outer: { pieces: [level(top)], sets: [[0]] },
    under: { pieces: [level(under)], sets: [[0]] },
    interiorEave: under, interiorCrown: under,
    facePitchDeg: [0],
  };
  if (spring) f.springing = S;
  if (why) f.invalid = note(why, top);
  setModel(f, { cm, E: spring ? undefined : E, S: spring ? S : undefined });
  return f;
}

/** The lowest underside point along the inner wall faces. */
function lowestAlong(f: CeilingField, ring: readonly Vec2[], which: 'outer' | 'under'): number {
  let m = Infinity;
  for (let i = 0; i < ring.length; i++) {
    for (const p of profileAlong(f, ring[i], ring[(i + 1) % ring.length], which)) m = Math.min(m, p.h);
  }
  return m;
}

// ---- Slope and gable -------------------------------------------------------

function slopeField(inst: ProductInstance, cm: Common): CeilingField {
  const E = eaveOf(inst, cm.H, cm.T), He = Math.max(cm.H, E), wall = directionWall(cm.view, inst.ceiling);
  const ax = axisOf(cm, wall), L = Math.max(1e-9, ax.u1Out - ax.u0Out), t = (He - E) / L;
  const outer = axisPlane(ax, t, E - t * ax.u0Out);
  const under = { ...outer, c: outer.c - cm.T * sec(t) };
  // The crown point: the middle of the longest outer edge along the high side
  // (an edge's middle is on the roof, where the middle of two far points of a
  // concave room — a U's two arms — may fall in its notch).
  const high = (p: Vec2): boolean => dot(ax.n, sub(p, ax.a)) > ax.u1Out - 1e-6;
  let crownAt: Vec2 | undefined, best = -1;
  cm.out.edges.forEach((e) => { if (high(e.a) && high(e.b) && e.len > best + 1e-9) { best = e.len; crownAt = lerp(e.a, e.b, 0.5); } });
  crownAt ??= cm.out.pts.find(high);
  const f: CeilingField = {
    ...baseField(inst, cm, 'slope'), flat: false, wall, uAxis: ax,
    eave: E, crown: He,
    outer: { pieces: [outer], sets: [[0]] },
    under: { pieces: [under], sets: [[0]] },
    crownAt,
    interiorEave: 0, interiorCrown: Math.max(...cm.inn.pts.map((p) => pieceValue(under, p))),
    facePitchDeg: [deg(Math.atan(t))],
  };
  setModel(f, { cm, E });
  f.interiorEave = lowestAlong(f, cm.inn.pts, 'under');
  return f;
}

/** The gable's outer ridge u for a rise Δ and an inner ridge at `uri`. */
function gableRidge(ax: NonNullable<CeilingField['uAxis']>, uri: number, D: number, T: number): number {
  if (D < 1e-9) return uri;
  const f = (ur: number): number => {
    const tL = D / (ur - ax.u0Out), tR = D / (ax.u1Out - ur);
    return tL * (uri - ax.u0Out) - T * sec(tL) - (tR * (ax.u1Out - uri) - T * sec(tR));
  };
  let lo = ax.u0Out + 1, hi = ax.u1Out - 1;
  if (f(lo) * f(hi) > 0) return uri;
  for (let i = 0; i < 60; i++) { const mid = (lo + hi) / 2; if (f(lo) * f(mid) <= 0) hi = mid; else lo = mid; }
  return (lo + hi) / 2;
}

/** The inner ridge u and its clamp: never closer than `side` to the inside faces. */
function gableInnerRidge(ax: NonNullable<CeilingField['uAxis']>, ridge: number): { uri: number; side: number; mid: number; span: number } {
  const span = ax.u1In - ax.u0In, mid = (ax.u0In + ax.u1In) / 2, side = Math.min(MIN_RIDGE_SIDE, span / 2);
  return { uri: clampN(mid + ridge * span, ax.u0In + side, ax.u1In - side), side, mid, span };
}

function gableField(inst: ProductInstance, cm: Common): CeilingField {
  const E = eaveOf(inst, cm.H, cm.T), He = Math.max(cm.H, E), D = He - E, wall = directionWall(cm.view, inst.ceiling);
  const ax = axisOf(cm, wall);
  const { uri } = gableInnerRidge(ax, finite(inst.ceiling?.ridge) ? inst.ceiling!.ridge! : 0);
  const ur = gableRidge(ax, uri, D, cm.T);
  const tL = D / Math.max(1e-9, ur - ax.u0Out), tR = D / Math.max(1e-9, ax.u1Out - ur);
  const OL = axisPlane(ax, tL, E - tL * ax.u0Out), OR = axisPlane(ax, -tR, E + tR * ax.u1Out);
  const IL = { ...OL, c: OL.c - cm.T * sec(tL) }, IR = { ...OR, c: OR.c - cm.T * sec(tR) };
  const onRidge = ridgeChord(cm.out.pts, ax, ur);
  const f: CeilingField = {
    ...baseField(inst, cm, 'gable'), flat: false, wall, uAxis: ax,
    eave: E, crown: He, ridgeIn: uri, ridgeOut: ur,
    outer: { pieces: [OL, OR], sets: [[0, 1]] },
    under: { pieces: [IL, IR], sets: [[0, 1]] },
    crownAt: onRidge ?? undefined,
    interiorEave: 0, interiorCrown: pieceValue(IL, add(ax.a, mul(ax.n, uri))),
    facePitchDeg: [deg(Math.atan(tL)), deg(Math.atan(tR))],
  };
  setModel(f, { cm, E });
  f.interiorEave = lowestAlong(f, cm.inn.pts, 'under');
  return f;
}

type Stretch = { o: Vec2; dir: Vec2; t0: number; t1: number };

/** Every stretch of the line u = `at` inside a ring, in order along it: from
 *  `o + dir·t0` to `o + dir·t1` (two for a ridge across both arms of a U). */
function ringStretches(ring: readonly Vec2[], ax: { n: Vec2; a: Vec2 }, at: number): Stretch[] {
  const dir = { x: -ax.n.y, y: ax.n.x }, o = add(ax.a, mul(ax.n, at));
  const ts: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i], q = ring[(i + 1) % ring.length];
    const fp = dot(ax.n, sub(p, o)), fq = dot(ax.n, sub(q, o));
    if ((fp > 0) !== (fq > 0)) ts.push(dot(dir, sub(lerp(p, q, fp / (fp - fq)), o)));
  }
  ts.sort((a, b) => a - b);
  const out: Stretch[] = [];
  for (let i = 0; i + 1 < ts.length; i += 2) if (ts[i + 1] - ts[i] > 1e-6) out.push({ o, dir, t0: ts[i], t1: ts[i + 1] });
  return out;
}

/** The longest of them (the first, on a tie). */
function ringStretch(ring: readonly Vec2[], ax: { n: Vec2; a: Vec2 }, at: number): Stretch | null {
  let best: Stretch | null = null;
  for (const s of ringStretches(ring, ax, at)) if (!best || s.t1 - s.t0 > best.t1 - best.t0) best = s;
  return best;
}

/** The middle of the longest stretch of the line u = `at` inside a ring. */
function ridgeChord(ring: readonly Vec2[], ax: { n: Vec2; a: Vec2 }, at: number): Vec2 | null {
  const s = ringStretch(ring, ax, at);
  return s && add(s.o, mul(s.dir, (s.t0 + s.t1) / 2));
}

// ---- The apex and its kernel (pyramid, dome) ---------------------------------

function bboxOf(poly: readonly Vec2[]): { x0: number; y0: number; x1: number; y1: number } {
  const xs = poly.map((p) => p.x), ys = poly.map((p) => p.y);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

function kernelOf(cm: Common): Vec2[] {
  const b = bboxOf(cm.view.polygon!);
  const m = APEX_CLEARANCE + Math.max(0, cm.T - cm.w);
  let K: Vec2[] = [{ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 }];
  for (const e of cm.out.edges) { K = clipHalfPlane(K, e.n, dot(e.n, e.a) + cm.w + m); if (K.length < 3) return []; }
  for (const e of cm.inn.edges) { K = clipHalfPlane(K, e.n, dot(e.n, e.a) + m); if (K.length < 3) return []; }
  return Math.abs(area2(K)) / 2 < 1 ? [] : K;
}

/** The convex region an apex may take (maybe empty). */
export function apexKernel(inst: ProductInstance): Vec2[] {
  return hasCeiling(inst) ? kernelOf(commonOf(inst)) : [];
}

/** What a stored apex is measured from and in: the middle of the room outline's
 *  bounding box (local) and its width and depth. The ONE reader of that origin. */
function apexFrame(view: ProductInstance): { c: Vec2; W: number; D: number } {
  const b = bboxOf(view.polygon!);
  return { c: { x: (b.x0 + b.x1) / 2, y: (b.y0 + b.y1) / 2 }, W: b.x1 - b.x0 || 1, D: b.y1 - b.y0 || 1 };
}

/** The stored apex (fractions off the outline's middle) clamped into the
 *  kernel: the point and whether it moved. With none stored, `defaultApexIn`. */
function apexIn(cm: Common, K: readonly Vec2[], apex: { x: number; y: number } | undefined): { A: Vec2; clamped: boolean } {
  const { c, W, D } = apexFrame(cm.view);
  if (!apex) return { A: defaultApexIn(K, c), clamped: false };
  const fx = finite(apex.x) ? apex.x : 0, fy = finite(apex.y) ? apex.y : 0;
  return nearestIn(K, { x: c.x + fx * W, y: c.y + fy * D });
}

/** A convex polygon shrunk by `r`: every side moved `r` inward (maybe empty). */
function shrinkConvex(K: readonly Vec2[], r: number): Vec2[] {
  const s = area2(K) > 0 ? 1 : -1;
  let out: Vec2[] = K.slice();
  for (let i = 0; i < K.length && out.length >= 3; i++) {
    const a = K[i], d = sub(K[(i + 1) % K.length], a), L = len(d);
    if (L < 1e-9) continue;
    const n = { x: (-d.y * s) / L, y: (d.x * s) / L };
    out = clipHalfPlane(out, n, dot(n, a) + r);
  }
  return out.length >= 3 ? out : [];
}

/** Where the apex stands with none stored: the outline's middle `c` projected
 *  into the kernel shrunk by `APEX_DEPTH` — or, in a kernel not that deep, shrunk
 *  as far as it goes (to its deepest points, the Chebyshev set, by bisection to
 *  well under a micrometre). So the middle stays wherever it lies that deep (the
 *  square, the oblong, any room symmetric about its middle), and a middle
 *  nearer a kernel side, or outside it (an L's), moves in only as far as that
 *  depth — never left at the clearance from a wall, where a dome is all but
 *  flat and a pyramid's face near vertical. A projection onto a convex set
 *  moves continuously with the room: a resize never makes the apex jump. */
function defaultApexIn(K: readonly Vec2[], c: Vec2): Vec2 {
  let depth = Infinity;
  const s = area2(K) > 0 ? 1 : -1;
  for (let i = 0; i < K.length; i++) {
    const a = K[i], d = sub(K[(i + 1) % K.length], a), L = len(d);
    if (L > 1e-9) depth = Math.min(depth, (crs(d, sub(c, a)) * s) / L);
  }
  if (depth >= APEX_DEPTH) return c;
  let r = APEX_DEPTH, Kr = shrinkConvex(K, r);
  if (!Kr.length) {
    let hi = r;
    r = 0; Kr = K.slice();
    for (let it = 0; it < 50; it++) {
      const mid = (r + hi) / 2, Km = shrinkConvex(K, mid);
      if (Km.length) { r = mid; Kr = Km; } else hi = mid;
    }
  }
  // c lies `depth` inside K, so inside the shrunk kernel exactly when depth ≥ r:
  // otherwise its nearest point is on that kernel's boundary (no inside test on
  // what may be a sliver).
  return depth >= r ? c : nearestOnRing(Kr, c);
}

function nearestOnRing(K: readonly Vec2[], P: Vec2): Vec2 {
  let best = K[0], bd = Infinity;
  for (let i = 0; i < K.length; i++) {
    const a = K[i], ab = sub(K[(i + 1) % K.length], a), L2 = dot(ab, ab);
    const t = L2 < 1e-18 ? 0 : clampN(dot(sub(P, a), ab) / L2, 0, 1), q = add(a, mul(ab, t)), d = len(sub(P, q));
    if (d < bd) { bd = d; best = q; }
  }
  return best;
}

function nearestIn(K: readonly Vec2[], P: Vec2): { A: Vec2; clamped: boolean } {
  const ccw = area2(K) > 0;
  let inside = true;
  for (let i = 0; i < K.length; i++) {
    const c = crs(sub(K[(i + 1) % K.length], K[i]), sub(P, K[i]));
    if (ccw ? c < -1e-9 : c > 1e-9) { inside = false; break; }
  }
  return inside ? { A: P, clamped: false } : { A: nearestOnRing(K, P), clamped: true };
}

// ---- Pyramid (tent) --------------------------------------------------------

function tentModel(ring: TRing, A: Vec2, E: number, T: number): TentModel {
  const faces = ring.edges, delta = faces.map((f) => sig(f, A)), sets = tentSets(ring, A);
  const crownOf = (Lam: number): { crown: number; q: Vec2 } => {
    const t = delta.map((d) => tentSlope(d, Lam, T));
    const g = faces.map((f, k) => mul(f.n, t[k])), c = t.map((tk) => T * sec(tk));
    let best = -Infinity, q: Vec2 = { x: 0, y: 0 };
    for (const S of sets) { const r = lpMax(S, g, c); if (Number.isFinite(r.v) && r.v > best) { best = r.v; q = r.q; } }
    return { crown: E + Lam + best, q };
  };
  return { faces, delta, sets, crownOf };
}

function pyramidField(inst: ProductInstance, cm: Common): CeilingField {
  const E = eaveOf(inst, cm.H, cm.T), He = Math.max(cm.H, E);
  const K = kernelOf(cm);
  if (!K.length) return flatField(inst, cm, 'pyramid', MSG.pyramidInvalid);
  const { A } = apexIn(cm, K, inst.ceiling?.apex);
  const tm = tentModel(cm.out, A, E, cm.T);
  const Lam = He - E < 1e-9 ? -cm.T : brent((L) => tm.crownOf(L).crown - He, -cm.T, He - E);
  const t = tm.delta.map((d) => tentSlope(d, Lam, cm.T)), h = E + Lam;
  const outer = tm.faces.map((f, k) => plane(t[k] * f.n.x, t[k] * f.n.y, E - t[k] * dot(f.n, f.a)));
  const under = tm.faces.map((f, k) => plane(t[k] * f.n.x, t[k] * f.n.y, h - t[k] * dot(f.n, A)));
  const f: CeilingField = {
    ...baseField(inst, cm, 'pyramid'), flat: false, apex: A, apexHeight: h,
    eave: E, crown: He,
    outer: { pieces: outer, sets: tm.sets },
    under: { pieces: under, sets: tm.sets },
    crownAt: add(A, tm.crownOf(Lam).q),
    interiorEave: 0, interiorCrown: h,
    facePitchDeg: t.map((x) => deg(Math.atan(x))),
  };
  setModel(f, { cm, E, tent: tm, wallPieces: tm.faces.map((e) => e.wall) });
  f.crownAt = climb(f.outer, f.crownAt!);
  f.interiorEave = lowestAlong(f, cm.inn.pts, 'under');
  return f;
}

// ---- Barrel ----------------------------------------------------------------

function angleStep(R: number, sag: number): number {
  return R > sag / 2 ? 2 * Math.acos(clampN(1 - sag / R, -1, 1)) : Math.PI / 2;
}

/** A cove's samples: the offsets from its crown line of its profile's points at
 *  every step of the ellipse's parameter, strictly between its crown line and its
 *  reach. A step's chord stands off the ellipse by at most step²·max(a, b)/8, off
 *  the curve t outside it by at most step²·(max(a, b) + t·(max/min)²)/8. */
function coveOffsets(pc: Cove, sag: number): number[] {
  const hi = Math.max(pc.a, pc.b), lo = Math.min(pc.a, pc.b);
  const dt = angleStep(hi + pc.t * (hi / lo) ** 2, sag), out: number[] = [];
  for (let j = 1; j * dt < Math.PI / 2 - 1e-9; j++) {
    const sn = Math.sin(j * dt), cs = Math.cos(j * dt), N = Math.hypot(pc.b * sn, pc.a * cs);
    out.push(sn * (pc.a + (pc.t * pc.b) / N));
  }
  return out;
}

/** u_j = um + R sin(jΔθ) for |u_j − um| < lim. */
function vaultLines(um: number, R: number, lim: number, sag: number): number[] {
  const dt = angleStep(R, sag), out: number[] = [];
  for (let j = -Math.ceil(Math.PI / 2 / dt); j <= Math.ceil(Math.PI / 2 / dt); j++) {
    const th = j * dt;
    if (Math.abs(th) > Math.PI / 2) continue;
    const x = R * Math.sin(th);
    if (Math.abs(x) < lim - 1e-9) out.push(um + x);
  }
  return out;
}

function barrelField(inst: ProductInstance, cm: Common): CeilingField {
  const T = cm.T, S = springingOf(inst, cm.H, T), wall = directionWall(cm.view, inst.ceiling);
  const ax = axisOf(cm, wall);
  const um = (ax.u0In + ax.u1In) / 2, b = (ax.u1In - ax.u0In) / 2, cap = b;
  const Hc = clampN(cm.H, S + T, S + T + cap), rho = Hc - T - S;
  const capped = cm.H > S + T + cap + 1e-9 ? { crown: Hc, reason: note(MSG.barrelCapped, Hc) } : undefined;
  const base = { ...baseField(inst, cm, 'barrel'), wall, uAxis: ax, springing: S, riseCap: cap, capped };
  if (rho < 0.5) {
    const f: CeilingField = {
      ...base, flat: false, eave: S + T, crown: Hc,
      outer: { pieces: [level(S + T)], sets: [[0]] },
      under: { pieces: [level(S)], sets: [[0]] },
      crownAt: ridgeChord(cm.out.pts, ax, um) ?? undefined,
      interiorEave: S, interiorCrown: S, facePitchDeg: [],
    };
    setModel(f, { cm, S });
    return f;
  }
  const Ri = (b * b + rho * rho) / (2 * rho), zc = Hc - T - Ri, Ro = Ri + T;
  const umAbs = dot(ax.n, ax.a) + um;
  const vault = (r: number): Vault => ({ kind: 'vault', nx: ax.n.x, ny: ax.n.y, um: umAbs, zc, r });
  const xs = Math.sqrt(b * b + T * (2 * Ri + T));
  const eaveAt = (xo: number): number => (xs > xo ? zc + Math.sqrt(Math.max(0, Ro * Ro - xo * xo)) : S);
  const f: CeilingField = {
    ...base, flat: false,
    eave: Math.min(eaveAt(um - ax.u0Out), eaveAt(ax.u1Out - um)), crown: Hc,
    outer: { pieces: [vault(Ro), level(S)], sets: [[0], [1]] },
    under: { pieces: [vault(Ri), level(S)], sets: [[0], [1]] },
    crownAt: ridgeChord(cm.out.pts, ax, um) ?? undefined,
    interiorEave: S, interiorCrown: Hc - T, facePitchDeg: [],
  };
  const curve: CurveModel = {
    kind: 'barrel',
    lines: (which, piece, sag) => {
      if (piece !== 0) return null;
      const R = which === 'outer' ? Ro : Ri, lim = which === 'outer' ? xs : b;
      const at = [...vaultLines(umAbs, R, lim, sag), umAbs, umAbs - lim, umAbs + lim];
      return { coord: (p) => dot(ax.n, p), at: [...new Set(at)].sort((p, q) => p - q) };
    },
  };
  setModel(f, { cm, S, curve, springPiece: 1 });
  return f;
}

// ---- Dome (cloister) ---------------------------------------------------------

function domeField(inst: ProductInstance, cm: Common): CeilingField {
  const T = cm.T, S = springingOf(inst, cm.H, T);
  const K = kernelOf(cm);
  if (!K.length) return flatField(inst, cm, 'dome', MSG.domeInvalid);
  const { A } = apexIn(cm, K, inst.ceiling?.apex);
  const faces = cm.inn.edges, delta = faces.map((e) => sig(e, A));
  const cap = Math.min(...delta);
  const Hc = clampN(cm.H, S + T, S + T + cap), rho = Hc - T - S;
  const capped = cm.H > S + T + cap + 1e-9 ? { crown: Hc, reason: note(MSG.domeCapped, Hc) } : undefined;
  const sets = tentSets(cm.inn, A), m = faces.length;
  const base = { ...baseField(inst, cm, 'dome'), apex: A, apexHeight: Hc - T, springing: S, riseCap: cap, capped };
  if (rho < 0.5) {
    const f: CeilingField = {
      ...base, flat: false, eave: S + T, crown: Hc,
      outer: { pieces: [level(S + T)], sets: [[0]] },
      under: { pieces: [level(S)], sets: [[0]] },
      interiorEave: S, interiorCrown: S, facePitchDeg: [],
    };
    setModel(f, { cm, S });
    return f;
  }
  const Ri = delta.map((bk) => (bk * bk + rho * rho) / (2 * rho));
  const cyl = (k: number, r: number): Cyl => ({ kind: 'cyl', nx: faces[k].n.x, ny: faces[k].n.y, d0: dot(faces[k].n, faces[k].a), top: delta[k], zc: Hc - T - Ri[k], r });
  const allSets = [...sets, [m]];
  const outer: Surface = { pieces: [...faces.map((_, k) => cyl(k, Ri[k] + T)), level(S)], sets: allSets };
  const under: Surface = { pieces: [...faces.map((_, k) => cyl(k, Ri[k])), level(S)], sets: allSets };
  const f: CeilingField = {
    ...base, flat: false, eave: 0, crown: Hc, outer, under, crownAt: A,
    interiorEave: S, interiorCrown: Hc - T, facePitchDeg: [],
  };
  const curve: CurveModel = {
    kind: 'dome',
    lines: (which, piece, sag) => {
      if (piece >= m) return null;
      const c = (which === 'outer' ? outer : under).pieces[piece] as Cyl;
      const dt = angleStep(c.r, sag), at: number[] = [c.top];
      const sMeet = Math.sqrt(Math.max(0, c.r * c.r - (S - c.zc) ** 2));
      for (let j = 1; j * dt < Math.PI / 2; j++) { const d = c.r * Math.sin(j * dt); if (d < sMeet - 1e-9) at.push(c.top - d); }
      at.push(c.top - sMeet);
      return { coord: (p) => c.nx * p.x + c.ny * p.y - c.d0, at: [...new Set(at)].sort((p, q) => p - q) };
    },
  };
  setModel(f, { cm, S, curve, springPiece: m, wallPieces: faces.map((e) => e.wall) });
  let low = Infinity;
  for (const p of ringSamples(cm.out.pts)) low = Math.min(low, pieceValueOf(f.outer, p));
  f.eave = low;
  return f;
}

/** A local maximum of a surface from `p` (a pattern search): the crown point of
 *  a tent whose top is a short ridge, where the LP's triple point may sit at
 *  one end of it — or past it. */
function climb(S: Surface, p: Vec2): Vec2 {
  const ev = evaluator(S);
  let cur = p, fv = ev.value(p);
  for (let step = 4, it = 0; step > 1e-7 && it < 4000; it++) {
    let moved = false;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
      const q = { x: cur.x + dx * step, y: cur.y + dy * step }, v = ev.value(q);
      if (v > fv + 1e-12) { fv = v; cur = q; moved = true; }
    }
    if (!moved) step /= 2;
  }
  return cur;
}

function ringSamples(ring: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < ring.length; i++) { const a = ring[i], b = ring[(i + 1) % ring.length]; out.push(a, lerp(a, b, 0.5)); }
  return out;
}

const pieceValueOf = (S: Surface, p: Vec2): number => evaluator(S).value(p);

// ---- Truncated pyramid (frustum) -------------------------------------------

function frustumField(inst: ProductInstance, cm: Common): CeilingField {
  const T = cm.T, w = cm.w, E = eaveOf(inst, cm.H, T), H = Math.max(cm.H, E);
  const fr = frameOf(cm.view), n = fr.n;
  const radii = effectiveCornerRadii(cm.view);
  const guard = frustumSetbacks(cm.view, inst.ceiling?.setbacks, w, T, radii);
  if (!guard) return flatField(inst, cm, 'frustum', MSG.frustumInvalid);
  const d = guard.d, vert = d.map((x) => x === 0);
  const F = flatOf(fr, d.map((x) => w + x));
  const Fp = flatOf(fr, d.map((x, k) => (vert[k] ? 0 : w + x)));
  const paths = resolvedOutlinePaths(cm.view, 0);
  interface FFace { a: Vec2; n: Vec2; delta: number; region: Vec2[]; wall: number }
  const faces: FFace[] = [];
  const wallFace = new Array<number>(n).fill(-1);
  for (let v = 0; v < n; v++) {
    const pv = paths[v];
    for (let i = 0; i + 1 < pv.length; i++) {
      const e = ringEdgeOf(pv[i], pv[i + 1], fr.ccw, -1, v);
      if (len(sub(pv[i + 1], pv[i])) < 1e-9) continue;
      faces.push({ a: e.a, n: e.n, delta: sig(e, F[v]), region: [pv[i], pv[i + 1], Fp[v]], wall: -1 });
    }
    if (vert[v]) continue;
    const a = pv[pv.length - 1], b = paths[(v + 1) % n][0], ew = fr.E[v];
    wallFace[v] = faces.length;
    faces.push({
      a: ew.a, n: ew.n, delta: sig(ew, F[v]), wall: v,
      region: len(sub(b, a)) < 1e-9 ? [a, Fp[(v + 1) % n], Fp[v]] : [a, b, Fp[(v + 1) % n], Fp[v]],
    });
  }
  const m = faces.length, FLAT = m, FLOOR = m + 1, Lam = H - T - E;
  const t = faces.map((f) => tentSlope(f.delta, Lam, T));
  const g0 = (i: number, p: Vec2): number => (i === FLAT ? 1 : sig(faces[i], p) / faces[i].delta);
  const flatParts = convexMerge(earClip(Fp));
  const regions = [...faces.map((f, i) => ({ piece: i, ring: f.region })), ...flatParts.map((q) => ({ piece: FLAT, ring: q }))];
  const sets = [...regionSets(regions, m + 1, g0), [FLOOR]];
  const outer: Surface = {
    pieces: [...faces.map((f, k) => plane(t[k] * f.n.x, t[k] * f.n.y, E - t[k] * dot(f.n, f.a))), level(H), level(0)],
    sets,
  };
  const under: Surface = {
    pieces: [...faces.map((f, k) => plane(t[k] * f.n.x, t[k] * f.n.y, E - t[k] * dot(f.n, f.a) - T * sec(t[k]))), level(H - T), level(0)],
    sets,
  };
  const everyVertical = vert.every(Boolean);
  const f: CeilingField = {
    ...baseField(inst, cm, 'frustum'), flat: false,
    eave: everyVertical ? H : E, crown: H,
    outer, under, setbacks: d, flatRing: F, crownAt: insidePoint(F),
    interiorEave: 0, interiorCrown: H - T,
    facePitchDeg: [...t.map((x) => deg(Math.atan(x))), 0, 0],
  };
  setModel(f, {
    cm, E,
    frustum: { fr, d, vert, F, Fp, regions, wallFace, delta: faces.map((q) => q.delta) },
    wallPieces: faces.map((q) => q.wall),
  });
  f.interiorEave = lowestAlong(f, cm.inn.pts, 'under');
  return f;
}

/** The POINTWISE cell sets of a field's T = 0 regions (P1b): each region split
 *  where another of the `count` pieces crosses its own — `g` the pieces' affine
 *  stand-ins, every one 1 along its flat edge —, each cell's set the pieces at or
 *  above its own at its centroid; minimal sets only. A per-region or
 *  lower-envelope rule is wrong on an L. */
function regionSets(regions: ReadonlyArray<{ piece: number; ring: Vec2[] }>, count: number, g0: (i: number, p: Vec2) => number): number[][] {
  const all: number[][] = [];
  for (const R of regions) {
    let cells: Vec2[][] = [R.ring];
    for (let i = 0; i < count; i++) {
      if (i === R.piece) continue;
      const h = R.ring.map((p) => g0(i, p) - g0(R.piece, p));
      if (!h.some((x) => x > 1e-9) || !h.some((x) => x < -1e-9)) continue;
      const next: Vec2[][] = [];
      for (const c of cells) {
        const vals = c.map((p) => g0(i, p) - g0(R.piece, p));
        const parts = splitConvex(c, vals, 1e-9);
        if (parts) next.push(...parts); else next.push(c);
      }
      cells = next;
    }
    for (const c of cells) {
      const ce = centroidOf(c), set: number[] = [];
      const own = g0(R.piece, ce);
      for (let j = 0; j < count; j++) if (g0(j, ce) >= own - 1e-12) set.push(j);
      all.push(set);
    }
  }
  return minimalSets(all);
}

// ---- Coved ceiling -------------------------------------------------------------

/** The coved ceiling: the truncated pyramid's flat F (its setbacks, clamp and
 *  regions), each sloped face a quarter ellipse instead — vertical where it
 *  leaves its wall's INNER face at the springing S, level where it meets the
 *  flat's edge at H − T —, a rounded corner one cove per inner arc chord (a fan
 *  to the flat corner). Each cove's stand-in is its distance from its inner face
 *  over its setback, so the T = 0 regions are the inner ring's, cut from each
 *  inner corner to its flat corner. The outer surface pushes each ellipse out T
 *  square to itself, over the springing plane; a vertical wall rises to the
 *  flat. */
function coveField(inst: ProductInstance, cm: Common): CeilingField {
  const T = cm.T, w = cm.w, S = springingOf(inst, cm.H, T), H = Math.max(cm.H, S + T);
  const fr = frameOf(cm.view), n = fr.n;
  const guard = frustumSetbacks(cm.view, inst.ceiling?.setbacks, w, T, effectiveCornerRadii(cm.view), true);
  if (!guard) return flatField(inst, cm, 'cove', MSG.coveInvalid);
  const d = guard.d, vert = d.map((x) => x === 0);
  const F = flatOf(fr, d.map((x) => w + x));
  const paths = resolvedOutlinePaths(cm.view, w);
  interface CFace { a: Vec2; n: Vec2; delta: number; region: Vec2[]; wall: number }
  const faces: CFace[] = [];
  const wallFace = new Array<number>(n).fill(-1);
  for (let v = 0; v < n; v++) {
    const pv = paths[v];
    for (let i = 0; i + 1 < pv.length; i++) {
      if (len(sub(pv[i + 1], pv[i])) < 1e-9) continue;
      const e = ringEdgeOf(pv[i], pv[i + 1], fr.ccw, -1, v);
      faces.push({ a: e.a, n: e.n, delta: sig(e, F[v]), region: [pv[i], pv[i + 1], F[v]], wall: -1 });
    }
    if (vert[v]) continue;
    const a = pv[pv.length - 1], b = paths[(v + 1) % n][0];
    wallFace[v] = faces.length;
    faces.push({
      a, n: fr.E[v].n, delta: d[v], wall: v,
      region: len(sub(b, a)) < 1e-9 ? [a, F[(v + 1) % n], F[v]] : [a, b, F[(v + 1) % n], F[v]],
    });
  }
  const m = faces.length, FLAT = m, FLOOR = m + 1, rise = H - T - S;
  const flatParts = convexMerge(earClip(F));
  const regions = [...faces.map((f, i) => ({ piece: i, ring: f.region })), ...flatParts.map((q) => ({ piece: FLAT, ring: q }))];
  const everyVertical = vert.every(Boolean);
  const base = {
    ...baseField(inst, cm, 'cove'), flat: false, springing: S, crown: H, setbacks: d, flatRing: F, crownAt: insidePoint(F),
    interiorEave: S, interiorCrown: H - T, facePitchDeg: [],
  };
  const frustum: FrustumModel = { fr, d, vert, F, Fp: F, regions, wallFace, delta: faces.map((q) => q.delta) };
  // No rise: both surfaces level, the flat's marks and grips kept.
  if (rise < 0.5 || everyVertical) {
    const top = everyVertical ? H : S + T;
    const f: CeilingField = {
      ...base, eave: top, outer: { pieces: [level(top)], sets: [[0]] }, under: { pieces: [level(top - T)], sets: [[0]] },
      interiorEave: top - T, interiorCrown: top - T,
    };
    setModel(f, { cm, S, frustum, wallPieces: [] });
    return f;
  }
  const g0 = (i: number, p: Vec2): number => (i === FLAT ? 1 : sig(faces[i], p) / faces[i].delta);
  const sets = [...regionSets(regions, m + 1, g0), [FLOOR]];
  const cove = (q: CFace, t: number): Cove => ({ kind: 'cove', nx: q.n.x, ny: q.n.y, d0: dot(q.n, q.a), top: q.delta, zc: S, a: q.delta, b: rise, t });
  const outer: Surface = { pieces: [...faces.map((q) => cove(q, T)), level(H), level(S)], sets };
  const under: Surface = { pieces: [...faces.map((q) => cove(q, 0)), level(H - T), level(S)], sets };
  const f: CeilingField = { ...base, eave: 0, outer, under };
  const curve: CurveModel = {
    kind: 'cove',
    lines: (which, piece, sag) => {
      if (piece >= m) return null;
      const c = (which === 'outer' ? outer : under).pieces[piece] as Cove;
      const at = [c.top, c.top - c.a - c.t, ...coveOffsets(c, sag).map((o) => c.top - o)];
      return { coord: (p) => c.nx * p.x + c.ny * p.y - c.d0, at: [...new Set(at)].sort((p, q) => p - q) };
    },
  };
  setModel(f, { cm, S, curve, frustum, springPiece: FLOOR, wallPieces: faces.map((q) => q.wall) });
  let low = Infinity;
  for (const p of ringSamples(cm.out.pts)) low = Math.min(low, pieceValueOf(f.outer, p));
  f.eave = low;
  f.interiorEave = lowestAlong(f, cm.inn.pts, 'under');
  return f;
}

// ---- resolveCeiling ----------------------------------------------------------

const MEMO = new Map<string, CeilingField>();
const MEMO_MAX = 64;

/** The room's ceiling, every figure derived (memoised on what it reads). A flat
 *  room's consumers never call it (they keep their flat path); it still answers,
 *  with the flat slab. */
export function resolveCeiling(inst: ProductInstance): CeilingField {
  const cm = commonOf(inst);
  const shape = hasCeiling(inst) ? ceilingShapeOf(inst) : 'flat';
  const key = JSON.stringify([cm.out.pts, cm.inn.pts, cm.view.polygon, cm.w, cm.T, cm.H, inst.ceiling, inst.category, shape]);
  const hit = MEMO.get(key);
  if (hit) { MEMO.delete(key); MEMO.set(key, hit); return hit; }
  let f: CeilingField;
  switch (shape) {
    case 'slope': f = slopeField(inst, cm); break;
    case 'gable': f = gableField(inst, cm); break;
    case 'frustum': f = frustumField(inst, cm); break;
    case 'cove': f = coveField(inst, cm); break;
    case 'barrel': f = barrelField(inst, cm); break;
    case 'pyramid': f = pyramidField(inst, cm); break;
    case 'dome': f = domeField(inst, cm); break;
    default: f = flatField(inst, cm, 'flat');
  }
  MEMO.set(key, f);
  if (MEMO.size > MEMO_MAX) MEMO.delete(MEMO.keys().next().value!);
  return f;
}

/** The outer (roof) surface's height at a local point. */
export function outerAt(cf: CeilingField, p: Vec2): number { return evaluator(cf.outer).value(p); }
/** The underside's height at a local point. */
export function underAt(cf: CeilingField, p: Vec2): number { return evaluator(cf.under).value(p); }
/** Where a wall (or anything standing in its band) meets the ceiling: the
 *  underside, extended over the band to the outer face. */
export function wallTopAt(cf: CeilingField, p: Vec2): number { return evaluator(cf.under).value(p); }
/** The piece giving the surface's value at p (ties: the lowest index). */
export function activePiece(cf: CeilingField, which: 'outer' | 'under', p: Vec2): number {
  return evaluator(which === 'outer' ? cf.outer : cf.under).active(p);
}
/** Piece `piece`'s unit normal (pointing up) at a local point: the mesh's
 *  per-vertex normals, from the same formulas `outerAt` / `underAt` evaluate. */
export function surfaceNormal(cf: CeilingField, which: 'outer' | 'under', piece: number, p: Vec2): [number, number, number] {
  return pieceNormal((which === 'outer' ? cf.outer : cf.under).pieces[piece], p);
}
/** Piece `piece`'s texture coordinates (mm along the true surface) at a local point. */
export function surfaceUV(cf: CeilingField, which: 'outer' | 'under', piece: number, p: Vec2): [number, number] {
  return pieceUV(cf, (which === 'outer' ? cf.outer : cf.under).pieces[piece], p);
}
/** Every piece of both surfaces is a plane (a straight run's kinks are exact). */
export function isPlanarCeiling(cf: CeilingField): boolean {
  return [...cf.outer.pieces, ...cf.under.pieces].every((pc) => pc.kind === 'plane');
}

// ---- Profile along a chord (sections) ---------------------------------------

const isCurved = (pc: Piece): boolean => pc.kind !== 'plane';

/** The surface along the chord a → b: exact breakpoints where the active piece
 *  changes, the clamp kinks, each curved piece's maximum (true vertices) and the
 *  curves' angle-uniform samples (`sample`), `curveNext` on every chord of a curve. */
export function profileAlong(cf: CeilingField, a: Vec2, b: Vec2, which: 'outer' | 'under', sag = SECTION_SAG): ProfilePt[] {
  const S = which === 'outer' ? cf.outer : cf.under, ev = evaluator(S);
  const L = len(sub(b, a));
  if (L < 1e-9) return [{ s: 0, h: ev.value(a) }];
  const dir = mul(sub(b, a), 1 / L), at = (s: number): Vec2 => add(a, mul(dir, s));
  const pts: Array<{ s: number; sample: boolean }> = [{ s: 0, sample: false }, { s: L, sample: false }];
  if (S.pieces.every((pc) => !isCurved(pc))) {
    for (const s of planarBreaks(S, a, dir, L)) pts.push({ s, sample: false });
  } else {
    curvedPoints(S, a, dir, L, sag, pts);
  }
  pts.sort((p, q) => p.s - q.s);
  const merged: Array<{ s: number; sample: boolean }> = [];
  for (const p of pts) {
    const last = merged[merged.length - 1];
    if (last && p.s - last.s < 1e-6) { if (!p.sample) { last.sample = false; if (p.s === L) last.s = L; } continue; }
    merged.push({ ...p });
  }
  if (merged.length > 1 && merged[merged.length - 1].s !== L) merged[merged.length - 1].s = L;
  const out: ProfilePt[] = merged.map((p) => (p.sample ? { s: p.s, h: ev.value(at(p.s)), sample: true } : { s: p.s, h: ev.value(at(p.s)) }));
  for (let i = 0; i + 1 < out.length; i++) {
    const mid = at((out[i].s + out[i + 1].s) / 2), pc = S.pieces[ev.active(mid)];
    // Along its axis a cylinder runs straight.
    if (pc.kind === 'plane' || Math.abs(pc.nx * dir.x + pc.ny * dir.y) < 1e-9) continue;
    if (pc.kind === 'vault' || pc.nx * mid.x + pc.ny * mid.y - pc.d0 < pc.top - 1e-9) out[i].curveNext = true;
  }
  return out;
}

/** Where the active piece changes along a chord of a planar surface — exact:
 *  between two crossings of the active plane with the others the value is that
 *  plane or never was. */
function planarBreaks(S: Surface, a: Vec2, dir: Vec2, L: number): number[] {
  const ev = evaluator(S), m = S.pieces.length;
  const al = new Float64Array(m), be = new Float64Array(m);
  S.pieces.forEach((pc, j) => { const q = pc as Plane; al[j] = q.gx * a.x + q.gy * a.y + q.c; be[j] = q.gx * dir.x + q.gy * dir.y; });
  const at = (s: number): Vec2 => add(a, mul(dir, s));
  const cands = (i: number, from: number): number[] => {
    const out: number[] = [];
    for (let j = 0; j < m; j++) {
      if (j === i) continue;
      const db = be[i] - be[j];
      if (Math.abs(db) < 1e-15) continue;
      const s = (al[j] - al[i]) / db;
      if (s > from + 1e-9 && s < L - 1e-9) out.push(s);
    }
    return [...new Set(out)].sort((p, q) => p - q);
  };
  const breaks: number[] = [];
  let s = 0, i = ev.active(at(Math.min(L / 2, 1e-7 * L)));
  for (let guard = 0; guard < 4 * m + 8; guard++) {
    const c = cands(i, s);
    const first = (c[0] ?? L);
    const k0 = ev.active(at((s + first) / 2));
    if (k0 !== i) { i = k0; continue; }
    let moved = false;
    for (let q = 0; q < c.length; q++) {
      const nx = q + 1 < c.length ? c[q + 1] : L;
      const k = ev.active(at((c[q] + nx) / 2));
      if (k !== i) { breaks.push(c[q]); s = c[q]; i = k; moved = true; break; }
    }
    if (!moved) break;
  }
  return breaks;
}

/** Each curved piece's kinks and angle samples across its arc, as the offsets
 *  σ along its normal they stand at (`curvedPoints` maps them onto a chord);
 *  null for a plane. Worked out once per surface and tolerance. */
interface ArcMarks { kinks: number[]; samples: number[] }
const ARC_MARKS = new WeakMap<Surface, Map<number, Array<ArcMarks | null>>>();
function arcMarks(S: Surface, sag: number): Array<ArcMarks | null> {
  let per = ARC_MARKS.get(S);
  if (!per) ARC_MARKS.set(S, (per = new Map()));
  const hit = per.get(sag);
  if (hit) return hit;
  const spring = S.pieces.find((pc) => pc.kind === 'plane' && pc.gx === 0 && pc.gy === 0) as Plane | undefined;
  const floor = spring ? spring.c : -Infinity;
  const marks = S.pieces.map((pc): ArcMarks | null => {
    if (pc.kind === 'plane') return null;
    if (pc.kind === 'cove') return { kinks: [pc.top, pc.top - pc.a - pc.t], samples: coveOffsets(pc, sag).map((o) => pc.top - o) };
    const meet = pc.r > Math.abs(floor - pc.zc) ? Math.sqrt(pc.r * pc.r - (floor - pc.zc) ** 2) : 0;
    const samples: number[] = [];
    const dt = angleStep(pc.r, sag);
    if (pc.kind === 'vault') {
      for (let q = -Math.ceil(Math.PI / 2 / dt); q <= Math.ceil(Math.PI / 2 / dt); q++) {
        const x = pc.r * Math.sin(q * dt);
        if (Math.abs(q * dt) <= Math.PI / 2 && Math.abs(x) < meet) samples.push(x);
      }
      return { kinks: [0, meet, -meet], samples };
    }
    for (let q = 1; q * dt < Math.PI / 2; q++) { const dd = pc.r * Math.sin(q * dt); if (dd < meet) samples.push(pc.top - dd); }
    return { kinks: [pc.top, pc.top - meet], samples };
  });
  per.set(sag, marks);
  return marks;
}

/** A curved surface's points along a chord: kinks, maxima, angle samples and
 *  the active-piece changes found between them. */
function curvedPoints(S: Surface, a: Vec2, dir: Vec2, L: number, sag: number, pts: Array<{ s: number; sample: boolean }>): void {
  const ev = evaluator(S), marks = arcMarks(S, sag);
  const at = (s: number): Vec2 => add(a, mul(dir, s));
  const inside = (s: number): boolean => s > 1e-9 && s < L - 1e-9;
  const near = (s: number, j: number): boolean => {
    const e = 1e-6 * Math.max(1, L);
    return ev.active(at(clampN(s - e, 0, L))) === j || ev.active(at(clampN(s + e, 0, L))) === j || ev.active(at(s)) === j;
  };
  S.pieces.forEach((pc, j) => {
    if (pc.kind === 'plane') return;
    const x0 = pc.nx * a.x + pc.ny * a.y - (pc.kind === 'vault' ? pc.um : pc.d0), k = pc.nx * dir.x + pc.ny * dir.y;
    if (Math.abs(k) < 1e-12) return;
    const { kinks, samples } = marks[j]!;
    for (const x of kinks) { const s = (x - x0) / k; if (inside(s) && near(s, j)) pts.push({ s, sample: false }); }
    for (const x of samples) { const s = (x - x0) / k; if (inside(s) && ev.active(at(s)) === j) pts.push({ s, sample: true }); }
  });
  pts.sort((p, q) => p.s - q.s);
  const extra: number[] = [];
  // Every change of the active piece in a stretch between two points: walked from
  // its start while that piece is not the one at its end — a piece running straight
  // along the chord (a dome's face parallel to it) has no sample of its own, so one
  // stretch may hold several —, and where both ends agree, a trip onto another piece
  // over the stretch's middle (its halves scanned in turn, twice at most).
  const scan = (lo: number, hi: number, depth: number): void => {
    if (hi - lo < 1e-6) return;
    const kb = ev.active(at(hi - (hi - lo) * 1e-6));
    if (ev.active(at(lo + (hi - lo) * 1e-6)) === kb) {
      const m = (lo + hi) / 2, e = (hi - lo) * 1e-6;
      if (depth < 2 && ev.active(at(m)) !== kb) {
        if (ev.active(at(m - e)) !== ev.active(at(m + e))) extra.push(m);
        scan(lo, m, depth + 1); scan(m, hi, depth + 1);
      }
      return;
    }
    for (let prev = -1, guard = 0; guard <= S.pieces.length; guard++) {
      const ka = ev.active(at(lo + (hi - lo) * 1e-6));
      if (ka === kb || ka === prev) break;
      let l = lo, h = hi;
      for (let it = 0; it < 40; it++) { const mid = (l + h) / 2; if (ev.active(at(mid)) === ka) l = mid; else h = mid; }
      extra.push((l + h) / 2);
      prev = ka;
      lo = h;
    }
  };
  for (let i = 0; i + 1 < pts.length; i++) scan(pts[i].s, pts[i + 1].s, 0);
  for (const s of extra) pts.push({ s, sample: false });
}

// ---- Pieces and triangles (§2.8) ---------------------------------------------

/** Split a convex polygon by the chord that cuts off its first run of vertices
 *  where `vals` (one per vertex) is negative: B is that run between the chord's
 *  two ends, A the rest of the ring. Each end is the vertex on the line
 *  (|v| ≤ eps) beside the run, else the crossing of that edge, computed from its
 *  endpoints in canonical order so a neighbour cut by the same line gets the same
 *  point. A line meets a convex polygon twice at most, but a curve can change
 *  sign round it more often: its other runs stay in A, to be cut in turn, so the
 *  halves always tile the polygon. Null when nothing is cut off.
 *  With `bend` (how far the crease stands off a chord c0 c1 at its middle, in
 *  value: 0 for a straight one), both halves must be cells. A half is none when
 *  it is smaller than `splitExact` keeps, or lies within its chord's band: 1 nm (no
 *  corner stands off it, as `cellTris` tells one), or wider where the chord
 *  cannot place the crease closer — that offset over the steepest the values
 *  fall away from the chord at the vertices — up to a ten-thousandth of the chord:
 *  a needle along it. A run cut off as none lies on the line and the next run is
 *  tried; the rest cut off as none leaves nothing to cut. Values rounded off at a
 *  plane's huge constant or a dome face's vertical springing change sign along a
 *  line the cell already ends on, and two curved creases nearly in line by a
 *  corner chip needles of each other's chord off it in turn: each cut took
 *  nothing a cell holds off and was made again and again. */
function splitConvex(pts: readonly Vec2[], vals0: readonly number[], eps: number,
  cross?: (p: Vec2, q: Vec2, fp: number, fq: number) => Vec2, bend?: (c0: Vec2, c1: Vec2) => number): [Vec2[], Vec2[]] | null {
  const n = pts.length, at = cross ?? ((p: Vec2, q: Vec2, fp: number, fq: number): Vec2 => {
    if (p.x > q.x || (p.x === q.x && p.y > q.y)) { [p, q] = [q, p]; [fp, fq] = [fq, fp]; }
    return lerp(p, q, fp / (fp - fq));
  });
  let vals = vals0;
  for (;;) {
    if (!vals.some((v) => v > eps) || !vals.some((v) => v < -eps)) return null;
    const neg = (k: number): boolean => vals[k % n] < -eps;
    let s0 = 0;
    while (!neg(s0) || neg(s0 + n - 1)) s0++;
    let e = s0;
    while (neg(e + 1)) e++;
    const runLen = e - s0, e0 = e % n, before = (s0 + n - 1) % n, after = (e + 1) % n;
    const inRun = (k: number): boolean => (k - s0 + n) % n <= runLen;
    const A: Vec2[] = [], B: Vec2[] = [];
    let c0 = pts[before], c1 = pts[after];
    for (let k = 0; k < n; k++) {
      const p = pts[k], q = pts[(k + 1) % n], fp = vals[k], fq = vals[(k + 1) % n], run = inRun(k);
      if (!run) A.push(p);
      if (run || ((k === before || k === after) && fp <= eps)) B.push(p);
      if (k === before && fp > eps) { c0 = at(p, q, fp, fq); A.push(c0); B.push(c0); }
      if (k === e0 && fq > eps) { c1 = at(p, q, fp, fq); A.push(c1); B.push(c1); }
    }
    if (A.length < 3 || B.length < 3) return null;
    if (!bend) return [A, B];
    // The band is worked out only for a part no wider than it can be.
    const dx = c1.x - c0.x, dy = c1.y - c0.y, L = Math.hypot(dx, dy), cap = Math.max(1e-6, 1e-4 * L);
    const wA = chordWidth(A, c0, dx, dy, L, cap), wB = chordWidth(B, c0, dx, dy, L, cap);
    let band = 1e-6;
    if (L > 1e-6 && (wA <= cap || wB <= cap)) {
      const err = bend(c0, c1);
      let slope = 0;
      if (err > 0) for (let k = 0; k < n; k++) { const t = chordOff(pts[k], c0, dx, dy, L); if (t > 1e-6) slope = Math.max(slope, Math.abs(vals[k]) / t); }
      if (slope > 0) band = Math.max(band, Math.min(err / slope, 1e-4 * L));
    }
    const thin = (w: number, part: readonly Vec2[]): boolean => w <= band || Math.abs(area2(part)) < 2e-9;
    if (thin(wA, A)) return null;
    if (!thin(wB, B)) return [A, B];
    vals = vals.map((v, k) => (inRun(k) ? 0 : v));
  }
}
/** How far p stands off the chord from c0 along (dx, dy) of length L (off c0 itself when it is a point). */
function chordOff(p: Vec2, c0: Vec2, dx: number, dy: number, L: number): number {
  return L > 1e-6 ? Math.abs(dx * (p.y - c0.y) - dy * (p.x - c0.x)) / L : Math.hypot(p.x - c0.x, p.y - c0.y);
}
/** The farthest a part stands off its chord, or the first offset past `cap`. */
function chordWidth(part: readonly Vec2[], c0: Vec2, dx: number, dy: number, L: number, cap: number): number {
  let w = 0;
  for (const p of part) { const t = chordOff(p, c0, dx, dy, L); if (t > w) { w = t; if (w > cap) break; } }
  return w;
}
/** A straight crease's `bend`. */
const STRAIGHT = (): number => 0;

interface Cell { pts: Vec2[]; piece: number }

let splitCells = 0, splitCapped = 0;
/** Test seam: the cells `splitExact` has looked at and the cuts it gave up at
 *  its depth cap since the last call. */
export function splitWork(): { cells: number; capped: number } {
  const w = { cells: splitCells, capped: splitCapped };
  splitCells = splitCapped = 0;
  return w;
}
/** A cell's vertices' average, a vertex within a millionth of the cell's size of
 *  the one before it counted with it: a crossing a cut leaves nanometres beside a
 *  vertex (a value rounded off at a dome face's springing) pulled the point a
 *  cell's piece is read at toward that corner, across a curved crease. */
function cellCentre(pts: readonly Vec2[]): Vec2 {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, x = 0, y = 0, near = Infinity, q = pts[pts.length - 1];
  for (const p of pts) {
    if (p.x < x0) x0 = p.x; if (p.x > x1) x1 = p.x; if (p.y < y0) y0 = p.y; if (p.y > y1) y1 = p.y;
    x += p.x; y += p.y;
    const d2 = (p.x - q.x) ** 2 + (p.y - q.y) ** 2;
    if (d2 < near) near = d2;
    q = p;
  }
  const tol2 = 1e-12 * ((x1 - x0) ** 2 + (y1 - y0) ** 2);
  if (near > tol2) return { x: x / pts.length, y: y / pts.length };
  let k = 0;
  x = y = 0;
  for (const p of pts) {
    const d2 = (p.x - q.x) ** 2 + (p.y - q.y) ** 2;
    q = p;
    if (d2 <= tol2) continue;
    x += p.x; y += p.y; k++;
  }
  return k ? { x: x / k, y: y / k } : centroidOf(pts);
}
/** Every piece's value at each point a tiling's cells share, worked out once a
 *  point (point k's row is `buf[k·m …]`), and scratch rows: each depth's centroid's.
 *  One surface's, so its packed pieces and whether they are all planes are kept
 *  with them, read once. */
interface PointVals { at: Map<Vec2, number>; buf: Float64Array; n: number; ce: Float64Array[]; low: Float64Array; P: Packed | null; planar: boolean }
const pointVals = (): PointVals => ({ at: new Map(), buf: new Float64Array(0), n: 0, ce: [], low: new Float64Array(0), P: null, planar: false });
/** Split a convex cell until one piece is active over each part. Piece i (the
 *  one active at the centroid) holds the whole cell when (1) the set realising it
 *  there stays ≥ f_i at every vertex and (2) no other set rises above f_i in the
 *  cell; otherwise the cell is cut along f_i = f_j for the piece j that breaks
 *  it. Exact for planes (a straight cut; (2) by clipping the cell with the set's
 *  halfplanes); a curved crease is cut along the chord between bisection points
 *  on the cell's edges, and (2) read at its vertices, edge midpoints and centroid.
 *  A cut must leave two cells (`splitConvex`'s `bend`), else it is no cut. The
 *  centroid is the vertices' average, each place once (`cellCentre`). */
function splitExact(pts: Vec2[], S: Surface, out: Cell[], depth = 0, PV: PointVals = pointVals()): void {
  if (Math.abs(area2(pts)) < 2e-9) return;
  splitCells++;
  if (!PV.P) { PV.P = packed(S); PV.planar = S.pieces.every((pc) => !isCurved(pc)); }
  const P = PV.P, planar = PV.planar, m = P.kind.length, nv = pts.length;
  const f = (j: number, p: Vec2): number => packedValue(P, j, p.x, p.y);
  // Row offsets of the vertices' values.
  const V: number[] = [];
  for (const p of pts) {
    let k = PV.at.get(p);
    if (k === undefined) {
      if ((PV.n + 1) * m > PV.buf.length) { const b = new Float64Array(Math.max(64 * m, 2 * PV.buf.length)); b.set(PV.buf); PV.buf = b; }
      k = PV.n++ * m;
      PV.at.set(p, k);
      packedValues(P, p.x, p.y, PV.buf, k);
    }
    V.push(k);
  }
  const buf = PV.buf;
  if (PV.low.length !== m) PV.low = new Float64Array(m);
  const ce = cellCentre(pts), C = (PV.ce[depth] ??= new Float64Array(m));
  packedValues(P, ce.x, ce.y, C);
  let best = -Infinity, i = -1, Gs: number[] = [];
  for (const G of S.sets) {
    let mn = Infinity, am = -1;
    for (const j of G) { const v = C[j]; if (v < mn || (v === mn && j < am)) { mn = v; am = j; } }
    if (mn > best || (mn === best && am < i)) { best = mn; i = am; Gs = G; }
  }
  // f_i at the vertices (and at a curved surface's probes when asked).
  const fi = V.map((o) => buf[o + i]);
  const fj = (j: number): number[] => V.map((o, k) => buf[o + j] - fi[k]);
  let probes: Vec2[] | null = null, fiP: number[] = [];
  const cutBy = (j: number): boolean => {
    if (depth > 400) { splitCapped++; return false; }
    const bisect = (p: Vec2, q: Vec2, fp: number, fq: number): Vec2 => {
      if (p.x > q.x || (p.x === q.x && p.y > q.y)) { [p, q] = [q, p]; [fp, fq] = [fq, fp]; }
      let lo = 0, hi = 1;
      for (let it = 0; it < 48; it++) {
        const m = (lo + hi) / 2, x = p.x + (q.x - p.x) * m, y = p.y + (q.y - p.y) * m;
        if ((packedValue(P, j, x, y) - packedValue(P, i, x, y) > 0) === (fp > 0)) lo = m; else hi = m;
      }
      return lerp(p, q, (lo + hi) / 2);
    };
    const parts = splitConvex(pts, fj(j), 1e-9, planar ? undefined : bisect, planar ? STRAIGHT : (c0, c1) => {
      const x = c0.x + (c1.x - c0.x) * 0.5, y = c0.y + (c1.y - c0.y) * 0.5;
      return Math.abs(packedValue(P, j, x, y) - packedValue(P, i, x, y));
    });
    if (!parts) return false;
    splitExact(parts[0], S, out, depth + 1, PV);
    splitExact(parts[1], S, out, depth + 1, PV);
    return true;
  };
  // Each piece's least margin under f_i over the vertices, vertex by vertex.
  const lowest = PV.low;
  lowest.fill(Infinity);
  for (let k = 0; k < nv; k++) {
    const o = V[k], fk = fi[k];
    for (let j = 0; j < m; j++) lowest[j] = Math.min(lowest[j], buf[o + j] - fk);
  }
  const low: Array<{ j: number; v: number }> = [];
  for (const j of Gs) if (j !== i && lowest[j] < -1e-9) low.push({ j, v: lowest[j] });
  low.sort((a, b) => a.v - b.v);
  for (const { j } of low) if (cutBy(j)) return;
  for (const G of S.sets) {
    if (G === Gs || G.includes(i)) continue;
    if (G.some((j) => V.every((o, k) => buf[o + j] - fi[k] <= 1e-9))) continue;
    let rises: boolean;
    if (planar) {
      const P = S.pieces as Plane[];
      let R: Vec2[] = pts;
      for (const j of G) {
        R = clipHalfPlane(R, { x: P[j].gx - P[i].gx, y: P[j].gy - P[i].gy }, P[i].c - P[j].c + 1e-9);
        if (R.length < 3) break;
      }
      rises = R.length >= 3 && Math.abs(area2(R)) >= 2e-6;
    } else {
      if (!probes) { probes = [...pts, ...pts.map((p, k) => lerp(p, pts[(k + 1) % nv], 0.5)), ce]; fiP = probes.map((p, k) => (k < nv ? fi[k] : f(i, p))); }
      rises = probes.some((p, k) => G.every((j) => (k < nv ? buf[V[k] + j] : f(j, p)) > fiP[k] + 1e-9));
    }
    if (!rises) continue;
    for (const j of G) {
      if (C[j] - C[i] <= 1e-9 && V.some((o, k) => buf[o + j] - fi[k] > 1e-9) && V.some((o, k) => buf[o + j] - fi[k] < -1e-9) && cutBy(j)) return;
    }
  }
  out.push({ pts, piece: i });
}

/** Cut every cell by the level lines coord(p) = c, for each c in `at`. */
function cutByLines(cells: Vec2[][], coord: (p: Vec2) => number, at: readonly number[]): Vec2[][] {
  const out: Vec2[][] = [];
  for (const cell of cells) {
    let parts: Vec2[][] = [cell];
    const vals = cell.map(coord), lo = Math.min(...vals), hi = Math.max(...vals);
    for (const c of at) {
      if (c <= lo + 1e-9 || c >= hi - 1e-9) continue;
      const next: Vec2[][] = [];
      for (const q of parts) { const sp = splitConvex(q, q.map((p) => coord(p) - c), 1e-9); if (sp) next.push(...sp); else next.push(q); }
      parts = next;
    }
    out.push(...parts);
  }
  return out;
}

/** One point object per location (within 1e-7 mm), and every point that lies on
 *  a cell's edge inserted into that edge — so neighbouring cells meet vertex
 *  for vertex (no T-junction). */
function conform(cells: Cell[]): Cell[] {
  // Points by number from here on: `P[k]` is the k-th point kept.
  const H0 = 1e-5, pool = new Map<number, Map<number, number[]>>(), P: Vec2[] = [];
  const near = (p: Vec2, cx: number, cy: number): number => {
    const l = pool.get(cx)?.get(cy);
    if (l) for (const k of l) { const q = P[k]; if (Math.abs(q.x - p.x) < 1e-7 && Math.abs(q.y - p.y) < 1e-7) return k; }
    return -1;
  };
  const unify = (p: Vec2): number => {
    const fx = p.x / H0, fy = p.y / H0, cx = Math.floor(fx), cy = Math.floor(fy);
    // A neighbouring cell can hold a point within 1e-7 only when p is that close to its side.
    const cx2 = fx - cx < 0.02 ? cx - 1 : fx - cx > 0.98 ? cx + 1 : cx, cy2 = fy - cy < 0.02 ? cy - 1 : fy - cy > 0.98 ? cy + 1 : cy;
    let k = near(p, cx, cy);
    if (k < 0 && cy2 !== cy) k = near(p, cx, cy2);
    if (k < 0 && cx2 !== cx) { k = near(p, cx2, cy); if (k < 0 && cy2 !== cy) k = near(p, cx2, cy2); }
    if (k >= 0) return k;
    let col = pool.get(cx);
    if (!col) pool.set(cx, (col = new Map()));
    (col.get(cy) ?? col.set(cy, []).get(cy)!).push(P.length);
    P.push(p);
    return P.length - 1;
  };
  const cs: Array<{ ids: number[]; piece: number }> = [];
  for (const c of cells) {
    const ids: number[] = [];
    for (const p of c.pts) { const k = unify(p); if (!ids.length || ids[ids.length - 1] !== k) ids.push(k); }
    while (ids.length > 1 && ids[0] === ids[ids.length - 1]) ids.pop();
    if (ids.length >= 3) cs.push({ ids, piece: c.piece });
  }
  // An edge another cell runs back along holds no point of a third: the points
  // to insert, and the edges they go on, are those of the edges with no twin.
  const next: number[][] = P.map(() => []);
  for (const { ids } of cs) for (let k = 0; k < ids.length; k++) next[ids[k]].push(ids[(k + 1) % ids.length]);
  const twinned = (p: number, q: number): boolean => next[q].includes(p);
  const isLoose = new Uint8Array(P.length), all: number[] = [];
  for (const { ids } of cs) for (let k = 0; k < ids.length; k++) {
    const p = ids[k], q = ids[(k + 1) % ids.length];
    if (twinned(p, q)) continue;
    if (!isLoose[p]) { isLoose[p] = 1; all.push(p); }
    if (!isLoose[q]) { isLoose[q] = 1; all.push(q); }
  }
  const ptsOf = (ids: readonly number[]): Vec2[] => ids.map((k) => P[k]);
  if (!all.length) return cs.map((c) => ({ pts: ptsOf(c.ids), piece: c.piece }));
  const b = bboxOf(ptsOf(all)), h = Math.max(1, Math.max(b.x1 - b.x0, b.y1 - b.y0) / 128);
  // Cells of h: a few hundred a side at most.
  const gx0 = Math.floor(b.x0 / h), gy0 = Math.floor(b.y0 / h), nx = Math.floor(b.x1 / h) - gx0 + 1, ny = Math.floor(b.y1 / h) - gy0 + 1;
  const grid: Array<number[] | undefined> = new Array(nx * ny), stamp = new Int32Array(nx * ny);
  const slot = (gx: number, gy: number): number => (gx < gx0 || gy < gy0 || gx - gx0 >= nx || gy - gy0 >= ny ? -1 : (gx - gx0) * ny + gy - gy0);
  for (const k of all) { const s = slot(Math.floor(P[k].x / h), Math.floor(P[k].y / h)); (grid[s] ??= []).push(k); }
  let edge = 0;
  const onEdge = (pk: number, qk: number): number[] => {
    const p = P[pk], q = P[qk], dx = q.x - p.x, dy = q.y - p.y, L2 = dx * dx + dy * dy;
    if (L2 < 1e-18) return [];
    const L = Math.sqrt(L2), found: Array<{ t: number; r: number }> = [];
    const x0 = Math.floor((Math.min(p.x, q.x) - 1e-6) / h), x1 = Math.floor((Math.max(p.x, q.x) + 1e-6) / h);
    const y0 = Math.floor((Math.min(p.y, q.y) - 1e-6) / h), y1 = Math.floor((Math.max(p.y, q.y) + 1e-6) / h);
    const visit = (s: number): void => {
      const list = s < 0 ? undefined : grid[s];
      if (!list) return;
      for (const rk of list) {
        if (rk === pk || rk === qk) continue;
        const r = P[rk], t = ((r.x - p.x) * dx + (r.y - p.y) * dy) / L2;
        // Measured in mm, not as a fraction of the edge: a point a few tenths of a
        // micrometre from the end of a chord metres long is not that end (it was
        // not unified with it) — left out, the edge has no twin along its length.
        if (t * L <= 1e-7 || (1 - t) * L <= 1e-7) continue;
        if (Math.abs(dx * (r.y - p.y) - dy * (r.x - p.x)) / L < 1e-6) found.push({ t, r: rk });
      }
    };
    if ((x1 - x0 + 1) * (y1 - y0 + 1) <= 256) {
      for (let gx = x0; gx <= x1; gx++) for (let gy = y0; gy <= y1; gy++) visit(slot(gx, gy));
    } else {
      const steps = Math.ceil((2 * L) / h);
      edge++;
      for (let s = 0; s <= steps; s++) {
        const u = s / steps, gx = Math.floor((p.x + (q.x - p.x) * u) / h), gy = Math.floor((p.y + (q.y - p.y) * u) / h);
        for (let ox = -1; ox <= 1; ox++) for (let oy = -1; oy <= 1; oy++) {
          const k = slot(gx + ox, gy + oy);
          if (k < 0 || stamp[k] === edge) continue;
          stamp[k] = edge; visit(k);
        }
      }
    }
    found.sort((u, v) => u.t - v.t);
    return [...new Set(found.map((f) => f.r))];
  };
  return cs.map(({ ids, piece }) => {
    const pts: Vec2[] = [];
    for (let k = 0; k < ids.length; k++) {
      const p = ids[k], q = ids[(k + 1) % ids.length];
      pts.push(P[p]);
      if (!twinned(p, q)) for (const r of onEdge(p, q)) pts.push(P[r]);
    }
    return { pts, piece };
  });
}

/** A surface's tiling; its triangles (the mesh's, the volume's) worked out when first read. */
interface Tiling { readonly tris: CeilingTri[]; pieces: CeilingPiece[]; creases: CreaseSeg[] }
const TILINGS = new WeakMap<CeilingField, Map<string, Tiling>>();

function tiling(cf: CeilingField, which: 'outer' | 'under', sag: number): Tiling {
  let per = TILINGS.get(cf);
  if (!per) { per = new Map(); TILINGS.set(cf, per); }
  const key = `${which}:${sag}`;
  const hit = per.get(key);
  if (hit) return hit;
  const t = buildTiling(cf, which, sag);
  per.set(key, t);
  return t;
}

function fanOf(ring: readonly Vec2[], A: Vec2): Vec2[][] {
  return ring.map((p, k) => [A, p, ring[(k + 1) % ring.length]]).filter((t) => Math.abs(area2(t)) > 2e-9);
}

/** The strip of a curve a cell lies in ("" off a curve): cells merge only
 *  within one strip, so each stays within its chord tolerance. */
function stripKey(cf: CeilingField, which: 'outer' | 'under', c: Cell, sag: number): string {
  const S = which === 'outer' ? cf.outer : cf.under, md = modelOf(cf);
  if (!isCurved(S.pieces[c.piece]) || !md.curve) return `${c.piece}`;
  const ln = md.curve.lines(which, c.piece, sag);
  if (!ln) return `${c.piece}`;
  const x = ln.coord(centroidOf(c.pts));
  let i = 0;
  while (i < ln.at.length && ln.at[i] <= x) i++;
  return `${c.piece}:${i}`;
}

/** Neighbouring cells of one piece (and strip) merged while the union stays convex. */
function mergeCells(cells: Cell[], key: (c: Cell) => string): Cell[] {
  const kept = cells.filter((c) => Math.abs(area2(c.pts)) >= 2e-9);
  const rank = new Map<string, number>(), group = kept.map((c) => { const k = key(c); let g = rank.get(k); if (g === undefined) rank.set(k, (g = rank.size)); return g; });
  // Group by group, each in the order its cells came.
  return mergeConvex(kept.map((c) => ccwRing(c.pts)), group)
    .sort((a, b) => group[a.from] - group[b.from] || a.from - b.from)
    .map((r) => ({ pts: r.ring, piece: kept[r.from].piece }));
}

/** Drop the straight-through points a cut left that only one cell still uses —
 *  a cut line that turned out to change nothing — keeping the base cells' own
 *  points, so a cell meets its neighbours, and a ring its seam, vertex for vertex. */
function prune(cells: Cell[], base: readonly Vec2[][]): Cell[] {
  const keep = new Set<Vec2>(base.flat());
  const uses = new Map<Vec2, number>();
  for (const c of cells) for (const p of c.pts) uses.set(p, (uses.get(p) ?? 0) + 1);
  return cells.map((c) => {
    const pts = c.pts.filter((p, k) => {
      if (keep.has(p) || (uses.get(p) ?? 0) > 1) return true;
      const a = c.pts[(k - 1 + c.pts.length) % c.pts.length], b = c.pts[(k + 1) % c.pts.length];
      return Math.abs(crs(sub(p, a), sub(b, p))) > 1e-9 * len(sub(p, a)) * len(sub(b, p));
    });
    return { pts, piece: c.piece };
  });
}

/** A base cell cut by no piece's lines before it is split: its piece is not
 *  known ahead (a cove's outer surface over its inner regions). */
const NO_PRECUT = -2;

/** Base cells split until each lies on one piece of the surface (exact for
 *  planes; curves cut by their angle-uniform lines, so within `sag`) and made
 *  conforming. `sector[k]` names the curved piece whose lines cut base cell k
 *  (−1: the piece active at its centroid; `NO_PRECUT`: none). */
function tileCells(cf: CeilingField, which: 'outer' | 'under', base0: Vec2[][], sector: readonly number[], sag: number): Cell[] {
  const md = modelOf(cf), S = which === 'outer' ? cf.outer : cf.under;
  const planar = S.pieces.every((pc) => !isCurved(pc));
  let base = base0;
  const cells: Cell[] = [];
  if (planar) {
    const vals = pointVals();
    for (const c of base) splitExact(c, S, cells, 0, vals);
  } else {
    const curve = md.curve!;
    if (curve.kind === 'barrel') {
      const ln = curve.lines(which, 0, sag)!;
      base = cutByLines(base, ln.coord, ln.at);
    } else {
      const next: Vec2[][] = [];
      base.forEach((c, k) => {
        if (sector[k] === NO_PRECUT) { next.push(c); return; }
        const ln = curve.lines(which, sector[k] >= 0 ? sector[k] : evaluator(S).active(centroidOf(c)), sag);
        next.push(...(ln ? cutByLines([c], ln.coord, ln.at) : [c]));
      });
      base = next;
    }
    const split: Cell[] = [];
    const vals = pointVals();
    for (const c of base) splitExact(c, S, split, 0, vals);
    for (const c of split) {
      const ln = curve.lines(which, c.piece, sag);
      if (!ln) { cells.push(c); continue; }
      for (const q of cutByLines([c.pts], ln.coord, ln.at)) cells.push({ pts: q, piece: c.piece });
    }
  }
  return prune(mergeCells(conform(cells), (c) => stripKey(cf, which, c, sag)), base0);
}

/** Conforming cells as counter-clockwise triangles. */
function cellTris(cells: readonly Cell[]): CeilingTri[] {
  const tris: CeilingTri[] = [];
  for (const c of cells) {
    // A corner is told from a straight point by how far it stands off (1 nm): a
    // cut can leave a corner a micrometre wide on a cell metres long.
    const tt = c.pts.length === 3 ? [c.pts as [Vec2, Vec2, Vec2]] : earClip(c.pts, [], 1e-6);
    for (const t of tt) {
      const A2 = area2(t);
      if (Math.abs(A2) < 2e-9) continue;
      tris.push(A2 > 0 ? { piece: c.piece, a: t[0], b: t[1], c: t[2] } : { piece: c.piece, a: t[0], b: t[2], c: t[1] });
    }
  }
  return tris;
}

/** Any LOCAL ring tiled the way a surface bends: conforming triangles, each on
 *  one piece — a wall band's or a feature's top cap. */
export function ceilingRingTris(cf: CeilingField, ring: readonly Vec2[], which: 'outer' | 'under' = 'under', sag = SECTION_SAG): CeilingTri[] {
  return cellTris(tileCells(cf, which, convexParts(ring), [], sag));
}

function buildTiling(cf: CeilingField, which: 'outer' | 'under', sag: number): Tiling {
  const md = modelOf(cf), S = which === 'outer' ? cf.outer : cf.under;
  const ring = which === 'outer' ? cf.outerRing : cf.innerRing;
  const planar = S.pieces.every((pc) => !isCurved(pc));
  let base: Vec2[][];
  let sector: number[] = [];
  if (cf.flat || S.pieces.length === 1) base = convexParts(ring);
  else if (cf.shape === 'pyramid') base = fanOf(ring, cf.apex!);
  else if (cf.shape === 'dome') {
    const A = cf.apex!, fan = cf.innerRing.map((p, k) => [A, p, cf.innerRing[(k + 1) % cf.innerRing.length]]);
    base = [];
    fan.forEach((t, k) => { if (Math.abs(area2(t)) > 2e-9) { base.push(t); sector.push(k); } });
    // The wall band; 0 mm walls (a steam room) have none — and a hole equal to
    // its ring would earClip to the whole room a second time.
    const band = Math.abs(area2(cf.outerRing)) - Math.abs(area2(cf.innerRing)) > 2e-6;
    if (which === 'outer' && band) for (const t of earClip(cf.outerRing, [cf.innerRing])) { base.push([...t]); sector.push(-1); }
  } else if (cf.shape === 'frustum' && which === 'outer') {
    base = md.frustum!.regions.flatMap((R) => (isConvexRing(R.ring) ? [ccwRing(R.ring)] : convexMerge(earClip(R.ring))));
  } else if (cf.shape === 'cove') {
    // Its T = 0 regions are the inner ring's: exactly the underside's pieces,
    // each cut along its own cove's lines. The outer surface's creases stand off
    // them, so there a region is split first and each part cut by its own
    // piece's lines — cut by a neighbour's, nearly parallel to its own, the part
    // the neighbour holds left needle-thin cells. The outer ring adds the wall band.
    base = [];
    for (const R of md.frustum!.regions) {
      for (const q of isConvexRing(R.ring) ? [ccwRing(R.ring)] : convexMerge(earClip(R.ring))) {
        if (Math.abs(area2(q)) > 2e-9) { base.push(q); sector.push(which === 'outer' ? NO_PRECUT : R.piece < md.wallPieces!.length ? R.piece : -1); }
      }
    }
    // The band in convex parts: an arc's band ear-clipped is needles from one
    // arc to the other, where the outer surface stands near vertical.
    const band = Math.abs(area2(cf.outerRing)) - Math.abs(area2(cf.innerRing)) > 2e-6;
    if (which === 'outer' && band) for (const q of convexMerge(earClip(cf.outerRing, [cf.innerRing]))) { base.push(ccwRing(q)); sector.push(NO_PRECUT); }
  } else base = convexParts(ring);
  if (cf.shape === 'gable' && cf.uAxis) {
    const ax = cf.uAxis, at = which === 'outer' ? cf.ridgeOut! : cf.ridgeIn!;
    base = cutByLines(base, (p) => dot(ax.n, sub(p, ax.a)), [at]);
  }
  const cells = tileCells(cf, which, base, sector, sag);
  const pieces: CeilingPiece[] = cells.map((c) => ({ piece: c.piece, ring: c.pts, curved: isCurved(S.pieces[c.piece]) }));
  let tris: CeilingTri[] | null = null;
  return { get tris() { return (tris ??= cellTris(cells)); }, pieces, creases: creasesOf(cells, planar, cf.shape === 'slope' || cf.shape === 'gable' ? 'ridge' : 'crease') };
}

/** Hertel–Mehlhorn over convex cells that meet vertex for vertex: two cells of
 *  one group sharing a run of edges become one while the union stays convex.
 *  Each result names the first cell it grew from. */
function mergeConvex(polys0: Vec2[][], group: readonly number[]): Array<{ ring: Vec2[]; from: number }> {
  // One point object per location, so a point is its number: `P[k]`.
  const num = new Map<Vec2, number>(), P: Vec2[] = [];
  const polys: Array<number[] | null> = polys0.map((r) => r.map((p) => {
    let k = num.get(p);
    if (k === undefined) { num.set(p, (k = P.length)); P.push(p); }
    return k;
  }));
  // Which cell runs from p to q: per p, each q it runs to and that cell.
  const to: number[][] = P.map(() => []), by: number[][] = P.map(() => []);
  const ownerOf = (p: number, q: number): number | undefined => { const k = to[p].indexOf(q); return k < 0 ? undefined : by[p][k]; };
  const own = (i: number, on: boolean): void => {
    const r = polys[i]!;
    for (let k = 0; k < r.length; k++) {
      const p = r[k], q = r[(k + 1) % r.length], at = to[p].indexOf(q);
      if (on) { if (at < 0) { to[p].push(q); by[p].push(i); } else by[p][at] = i; } else if (at >= 0 && by[p][at] === i) {
        to[p][at] = to[p][to[p].length - 1]; to[p].pop();
        by[p][at] = by[p][by[p].length - 1]; by[p].pop();
      }
    }
  };
  polys.forEach((_, i) => own(i, true));
  const mark = new Int32Array(P.length);
  let pass = 0;
  const convex = (U: number[]): boolean => {
    const m = U.length;
    if (m < 3) return false;
    pass++;
    for (const k of U) { if (mark[k] === pass) return false; mark[k] = pass; }
    for (let k = 0; k < m; k++) {
      const a = P[U[(k + m - 1) % m]], b = P[U[k]], c = P[U[(k + 1) % m]];
      const ux = b.x - a.x, uy = b.y - a.y, vx = c.x - b.x, vy = c.y - b.y;
      if (ux * vy - uy * vx < -1e-9 * Math.hypot(ux, uy) * Math.hypot(vx, vy)) return false;
    }
    return true;
  };
  for (let i = 0; i < polys.length; i++) {
    for (let merged = true; merged && polys[i];) {
      merged = false;
      const A = polys[i]!;
      for (let e = 0; e < A.length && !merged; e++) {
        const j = ownerOf(A[(e + 1) % A.length], A[e]);
        if (j == null || j === i || !polys[j] || group[j] !== group[i]) continue;
        const B = polys[j]!;
        // The whole run of edges A and B share, from A[s] to A[t] (A's order).
        let s = e, t = (e + 1) % A.length;
        while (ownerOf(A[s], A[(s - 1 + A.length) % A.length]) === j && s !== (t + 1) % A.length) s = (s - 1 + A.length) % A.length;
        while (ownerOf(A[(t + 1) % A.length], A[t]) === j && (t + 1) % A.length !== s) t = (t + 1) % A.length;
        const U: number[] = [];
        for (let k = t; ; k = (k + 1) % A.length) { U.push(A[k]); if (k === s) break; }
        const bs = B.indexOf(A[s]);
        for (let k = (bs + 1) % B.length; B[k] !== A[t]; k = (k + 1) % B.length) U.push(B[k]);
        if (!B.includes(A[t]) || !convex(U)) continue;
        own(i, false); own(j, false);
        polys[i] = U; polys[j] = null;
        own(i, true);
        merged = true;
      }
    }
  }
  const out: Array<{ ring: Vec2[]; from: number }> = [];
  polys.forEach((ring, from) => { if (ring) out.push({ ring: ring.map((k) => P[k]), from }); });
  return out;
}

function isConvexRing(r: readonly Vec2[]): boolean {
  const s = Math.sign(area2(r));
  for (let i = 0; i < r.length; i++) {
    const c = crs(sub(r[(i + 1) % r.length], r[i]), sub(r[(i + 2) % r.length], r[(i + 1) % r.length]));
    if (c * s < -1e-9) return false;
  }
  return true;
}

const ccwRing = (r: readonly Vec2[]): Vec2[] => (area2(r) < 0 ? [...r].reverse() : [...r]);

/** A ring as few convex parts: itself when convex, else its triangles merged. */
const convexParts = (r: readonly Vec2[]): Vec2[][] => (isConvexRing(r) ? [ccwRing(r)] : convexMerge(earClip(r)));

/** Every edge between two cells of different pieces, deduplicated; a planar
 *  crease's collinear runs are joined into one segment. Read off the CELLS, not
 *  their triangles: a triangulation may drop a point that stands a hair off the
 *  line through its neighbours (a crease leaving a capped wall almost along it),
 *  and the crease would lose that edge on one side. */
function creasesOf(cells: readonly Cell[], planar: boolean, kind: CreaseSeg['kind']): CreaseSeg[] {
  // The cells are conforming: one point object per location, so an edge is its two objects.
  const owner = new Map<Vec2, Map<Vec2, number>>();
  for (const c of cells) for (let k = 0; k < c.pts.length; k++) {
    const p = c.pts[k], q = c.pts[(k + 1) % c.pts.length];
    let m = owner.get(p);
    if (!m) owner.set(p, (m = new Map()));
    m.set(q, c.piece);
  }
  const segs: CreaseSeg[] = [];
  for (const c of cells) for (let k = 0; k < c.pts.length; k++) {
    const p = c.pts[k], q = c.pts[(k + 1) % c.pts.length], o = owner.get(q)?.get(p);
    if (o !== undefined && o !== c.piece && c.piece < o) segs.push({ a: p, b: q, pieces: [c.piece, o], kind });
  }
  if (!planar) return chainSegs(segs);
  const byPair = new Map<string, CreaseSeg[]>();
  for (const s of segs) { const k = s.pieces.join(','); (byPair.get(k) ?? byPair.set(k, []).get(k)!).push(s); }
  const out: CreaseSeg[] = [];
  for (const list of byPair.values()) {
    const used = new Set<CreaseSeg>();
    for (const s0 of list) {
      if (used.has(s0)) continue;
      used.add(s0);
      let a = s0.a, b = s0.b;
      const u = mul(sub(b, a), 1 / (len(sub(b, a)) || 1));
      for (let grew = true; grew;) {
        grew = false;
        for (const s of list) {
          if (used.has(s)) continue;
          if (Math.abs(crs(u, sub(s.b, s.a))) > 1e-9 * len(sub(s.b, s.a))) continue;
          const ends = [s.a, s.b];
          if (ends.includes(b)) { b = s.a === b ? s.b : s.a; used.add(s); grew = true; }
          else if (ends.includes(a)) { a = s.a === a ? s.b : s.a; used.add(s); grew = true; }
        }
      }
      out.push({ a, b, pieces: s0.pieces, kind: s0.kind });
    }
  }
  return out;
}

/** A curved crease's chords in order: each pair of pieces' segments walked end
 *  to end from a loose end (a point only one of them reaches), each oriented
 *  along the walk — so a chain is a run of segments where one's `b` is the
 *  next's `a` (`creasePolylines`). */
function chainSegs(segs: readonly CreaseSeg[]): CreaseSeg[] {
  // The tiling is conforming: a point object is its location.
  const byPair = new Map<number, CreaseSeg[]>();
  for (const s of segs) { const k = s.pieces[0] * 65536 + s.pieces[1]; (byPair.get(k) ?? byPair.set(k, []).get(k)!).push(s); }
  const out: CreaseSeg[] = [];
  for (const list of byPair.values()) {
    bridgeGaps(list);
    const at = new Map<Vec2, CreaseSeg[]>();
    for (const s of list) for (const p of [s.a, s.b]) (at.get(p) ?? at.set(p, []).get(p)!).push(s);
    const used = new Set<CreaseSeg>();
    const walk = (s0: CreaseSeg, from: Vec2): void => {
      let s: CreaseSeg | undefined = s0, p = from;
      while (s && !used.has(s)) {
        used.add(s);
        const q: Vec2 = s.a === p ? s.b : s.a;
        out.push({ a: p, b: q, pieces: s.pieces, kind: s.kind });
        const here = at.get(q)!, next = here.filter((t) => !used.has(t));
        s = next.length === 1 && here.length === 2 ? next[0] : undefined;
        p = q;
      }
    };
    for (const s of list) for (const p of [s.a, s.b]) if (!used.has(s) && at.get(p)!.length !== 2) walk(s, p);
    for (const s of list) if (!used.has(s)) walk(s, s.a);
  }
  return out;
}

/** Where two pieces meet almost tangentially (a dome's faces near a capped
 *  wall) the cut points of neighbouring cells can miss each other by tens of
 *  nanometres (their heights agree within the cut's 1e-9 there), leaving a hole
 *  the crease runs round: two of one pair's loose ends that close are joined,
 *  so the crease stays one line. */
const CREASE_GAP = 1e-3;
function bridgeGaps(list: CreaseSeg[]): void {
  const deg = new Map<Vec2, number>();
  for (const s of list) for (const p of [s.a, s.b]) deg.set(p, (deg.get(p) ?? 0) + 1);
  const loose = [...deg].filter(([, n]) => n === 1).map(([p]) => p), done = new Set<Vec2>();
  const pairs: Array<[number, Vec2, Vec2]> = [];
  for (let i = 0; i < loose.length; i++) for (let j = i + 1; j < loose.length; j++) {
    const d = len(sub(loose[i], loose[j]));
    if (d < CREASE_GAP) pairs.push([d, loose[i], loose[j]]);
  }
  for (const [, p, q] of pairs.sort((u, v) => u[0] - v[0])) {
    if (done.has(p) || done.has(q)) continue;
    done.add(p); done.add(q);
    list.push({ a: p, b: q, pieces: list[0].pieces, kind: list[0].kind });
  }
}

/** Whether a crease only joins a rounded corner's facets (to each other or to
 *  the straight faces either side): a seam of the chords the corner's cone is
 *  tiled with — the true surface turns smoothly there — not an edge of the
 *  shape. A facet against the flat or the floor is one (`facetCurvePiece`). */
export function isFacetSeam(cf: CeilingField, s: Pick<CreaseSeg, 'pieces'>): boolean {
  const wp = modelOf(cf)?.wallPieces;
  return !!wp && s.pieces.every((i) => wp[i] !== undefined) && s.pieces.some((i) => wp[i] < 0);
}

/** The piece a rounded corner's facet meets along this crease when that is
 *  the flat or the floor — a chord of a CURVED edge of the shape, where the
 *  cone runs into a plane —, else -1. */
export function facetCurvePiece(cf: CeilingField, s: Pick<CreaseSeg, 'pieces'>): number {
  const wp = modelOf(cf)?.wallPieces;
  if (!wp) return -1;
  const facet = (i: number): boolean => wp[i] !== undefined && wp[i] < 0;
  const [i, j] = s.pieces;
  return facet(i) && wp[j] === undefined ? j : facet(j) && wp[i] === undefined ? i : -1;
}

/** A surface's creases as polylines: consecutive segments of one pair of pieces
 *  joined where one ends at the next's start (`ceilingCreases` lists a curved
 *  crease's chords in that order). */
export function creasePolylines(segs: readonly CreaseSeg[]): Array<{ pts: Vec2[]; pieces: [number, number]; kind: CreaseSeg['kind'] }> {
  const out: Array<{ pts: Vec2[]; pieces: [number, number]; kind: CreaseSeg['kind'] }> = [];
  for (const s of segs) {
    const last = out[out.length - 1], end = last?.pts[last.pts.length - 1];
    if (last && end && end.x === s.a.x && end.y === s.a.y && last.pieces[0] === s.pieces[0] && last.pieces[1] === s.pieces[1]) last.pts.push(s.b);
    else out.push({ pts: [s.a, s.b], pieces: s.pieces, kind: s.kind });
  }
  return out;
}

/** The surface's tiling as conforming counter-clockwise triangles, each on one piece. */
export function ceilingTris(cf: CeilingField, which: 'outer' | 'under', sag = SECTION_SAG): CeilingTri[] {
  return tiling(cf, which, sag).tris;
}
/** The same tiling merged into CONVEX pieces, one piece (and one strip of a curve) each. */
export function ceilingPieces(cf: CeilingField, which: 'outer' | 'under', sag = SECTION_SAG): CeilingPiece[] {
  return tiling(cf, which, sag).pieces;
}
/** Where two pieces of the surface meet: ridges, hips, valleys, the flat's edges. */
export function ceilingCreases(cf: CeilingField, which: 'outer' | 'under'): CreaseSeg[] {
  return tiling(cf, which, SECTION_SAG).creases;
}

/** The rounded corner each edge of `outerRing` / `innerRing` is a chord of, or
 *  −1 for a straight run: edge i runs from point i to point i + 1. */
export function ringArcCorners(cf: CeilingField, which: 'outer' | 'under'): number[] {
  const cm = modelOf(cf)?.cm;
  const ring = which === 'outer' ? cf.outerRing : cf.innerRing;
  const tr = cm ? (which === 'outer' ? cm.out : cm.inn) : null;
  return tr && tr.pts === ring ? tr.edges.map((e) => e.corner) : ring.map(() => -1);
}

// ---- Gross volume (heater sizing) --------------------------------------------

/** The volume under the OUTER roof over the OUTER outline (m³): a flat room
 *  exactly as it always was; a shaped one the same box up to the height it
 *  flattens to (the eave, or the springing plus the shell) plus the roof above it. */
export function roomVolumeM3(inst: ProductInstance): number {
  const areaMm2 = isPolyRoom(inst) && inst.polygon && inst.polygon.length >= 3
    ? Math.abs(polygonArea(inst.polygon)) / 2
    : inst.width * inst.depth;
  if (isFlatCeiling(inst)) return (areaMm2 / 1e6) * (externalHeight(inst) / 1000);
  const cf = resolveCeiling(inst);
  const Z0 = springsInside(cf.shape) ? cf.springing! + cf.thickness : cf.flat ? cf.crown : modelOf(cf).E!;
  const box = (areaMm2 / 1e6) * (Z0 / 1000);
  if (cf.flat || cf.crown - Z0 < 1e-9) return box;
  let roof = 0;
  const ev = evaluator(cf.outer);
  for (const t of ceilingTris(cf, 'outer', VOLUME_SAG)) {
    const A = Math.abs(area2([t.a, t.b, t.c])) / 2;
    roof += (A * (ev.value(lerp(t.a, t.b, 0.5)) + ev.value(lerp(t.b, t.c, 0.5)) + ev.value(lerp(t.c, t.a, 0.5)) - 3 * Z0)) / 3;
  }
  return box + roof / 1e9;
}

// ---- The truncated pyramid's setbacks: THE clamp (§2.4) -----------------------

/** An affine constraint of the setbacks: c0 + co·d ≥ 0. */
export interface Lin { c0: number; co: number[]; kind?: string; e?: number; i?: number; end?: number }
const linVal = (c: Lin, d: readonly number[]): number => { let s = c.c0; for (let k = 0; k < d.length; k++) s += c.co[k] * d[k]; return s; };
const affine = (n: number, fn: (d: number[]) => number): Lin => {
  const z = new Array<number>(n).fill(0), c0 = fn(z);
  return { c0, co: z.map((_, k) => { const q = z.slice(); q[k] = 1; return fn(q) - c0; }) };
};

interface ClampCtx { fr: Frame; w: number; T: number; dPlus: number; radii: number[]; chords: RingEdge[]; clear: number; runs: number[]; hull: boolean[] }

/** A truncated pyramid's corner faces stand on the OUTER arc's chords, so its
 *  flat keeps w + d⁺ from them; a cove's (`cove`) on the INNER arc's, d⁺ from
 *  them — and a cove reaches its wall whatever its thickness, so d⁺ reads none. */
function clampCtx(view: ProductInstance, w: number, T: number, radii: readonly number[], cove = false): ClampCtx {
  const fr = frameOf(view), ccw = fr.ccw, dPlus = leastSlopedSetback(w, cove ? 0 : T);
  const chords = taggedRing(view, cove ? w : 0, ccw).edges.filter((e) => e.corner >= 0);
  const hull = fr.E.map((ek) => fr.E.every((e) => sig(ek, e.a) >= -1e-6));
  return { fr, w, T, dPlus, radii: [...radii], chords, clear: cove ? dPlus : w + dPlus, runs: runsOf(fr), hull };
}

const flatAt = (cx: ClampCtx, d: readonly number[]): Vec2[] => flatOf(cx.fr, d.map((x) => cx.w + x));

/** (v1) a wall that ends at a rounded corner, (v2) a wall the room continues
 *  past: neither may stand vertical. */
function modesOf(cx: ClampCtx, target: readonly number[], cut: number): boolean[] {
  const n = cx.fr.n, vert = target.map((t) => t < cut);
  for (let k = 0; k < n; k++) if ((cx.radii[k] || 0) > 0 || (cx.radii[(k + 1) % n] || 0) > 0 || !cx.hull[k]) vert[k] = false;
  for (let k = 0; k < n; k++) for (let j = 0; j < n; j++) if (cx.runs[j] === cx.runs[k]) vert[j] = vert[j] && vert[k];
  return vert;
}

function constraintsOf(cx: ClampCtx, vert: readonly boolean[], relaxed: boolean): Lin[] {
  const { fr, dPlus } = cx, n = fr.n, C: Lin[] = [];
  for (let k = 0; k < n; k++) C.push({ kind: 'side', ...affine(n, (d) => { const F = flatAt(cx, d); return dot(sub(F[(k + 1) % n], F[k]), fr.E[k].u) - MIN_FLAT_SIDE; }) });
  for (const e of cx.chords) C.push({ kind: 'corner', ...affine(n, (d) => sig(e, flatAt(cx, d)[e.corner]) - cx.clear) });
  for (let k = 0; k < n; k++) {
    const co = new Array<number>(n).fill(0); co[k] = 1;
    if (relaxed) C.push({ kind: 'min', c0: 0, co });
    else if (vert[k]) { C.push({ kind: 'vert', c0: 0, co }); C.push({ kind: 'vert', c0: 0, co: co.map((x) => -x) }); }
    else C.push({ kind: 'min', c0: -dPlus, co });
  }
  for (let k = 0; k < n; k++) for (let j = k + 1; j < n; j++) if (cx.runs[j] === cx.runs[k]) {
    const co = new Array<number>(n).fill(0); co[k] = 1; co[j] = -1;
    C.push({ kind: 'run', c0: 0, co }); C.push({ kind: 'run', c0: 0, co: co.map((x) => -x) });
  }
  return C;
}

interface WidthPair { e: number; i: number; t: number; dist: number }

/** Every reflex flat corner against every flat segment not incident to it. */
function widthPairs(cx: ClampCtx, d: readonly number[]): WidthPair[] {
  const n = cx.fr.n, F = flatAt(cx, d), out: WidthPair[] = [];
  for (let e = 0; e < n; e++) {
    if (!isReflexAt(cx.fr, e)) continue;
    const pe = (e - 1 + n) % n;
    for (let i = 0; i < n; i++) {
      if (i === e || i === pe) continue;
      const a = F[i], ab = sub(F[(i + 1) % n], a), L2 = dot(ab, ab);
      const t = L2 < 1e-18 ? 0 : clampN(dot(sub(F[e], a), ab) / L2, 0, 1);
      out.push({ e, i, t, dist: len(sub(F[e], add(a, mul(ab, t)))) });
    }
  }
  return out;
}

function simplePoly(F: readonly Vec2[]): boolean {
  const n = F.length;
  const X = (p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean => {
    const d1 = crs(sub(p4, p3), sub(p1, p3)), d2 = crs(sub(p4, p3), sub(p2, p3)), d3 = crs(sub(p2, p1), sub(p3, p1)), d4 = crs(sub(p2, p1), sub(p4, p1));
    return d1 * d2 < 0 && d3 * d4 < 0;
  };
  for (let i = 0; i < n; i++) for (let j = i + 2; j < n; j++) {
    if (i === 0 && j === n - 1) continue;
    if (X(F[i], F[(i + 1) % n], F[j], F[(j + 1) % n])) return false;
  }
  return true;
}

function validSetbacks(cx: ClampCtx, C: readonly Lin[], d: readonly number[], tol = 1e-6): boolean {
  for (const c of C) if (linVal(c, d) < -tol) return false;
  for (const p of widthPairs(cx, d)) if (p.dist < MIN_FLAT_SIDE - tol) return false;
  return simplePoly(flatAt(cx, d));
}

/** min ‖E·u − f‖ over u ≥ 0 (Lawson–Hanson NNLS); `E` by columns. A column in
 *  the span of the passive ones is refused (its gradient is 0 in exact
 *  arithmetic), so a degenerate active set never cycles. */
function nnls(E: readonly number[][], f: readonly number[]): number[] {
  const m = E.length, M = f.length, u = new Array<number>(m).fill(0);
  const P: number[] = [], inP = new Array<boolean>(m).fill(false), refused = new Set<number>();
  const resid = (): number[] => {
    const r = f.slice();
    for (const j of P) for (let i = 0; i < M; i++) r[i] -= E[j][i] * u[j];
    return r;
  };
  /** The least-squares coefficients of f on the columns P (modified Gram–Schmidt,
   *  re-orthogonalised), or null when a column is dependent. */
  const lsq = (cols: readonly number[]): number[] | null => {
    const Q: number[][] = [], R: number[][] = cols.map(() => new Array<number>(cols.length).fill(0));
    for (let k = 0; k < cols.length; k++) {
      const v = E[cols[k]].slice(), n0 = Math.hypot(...v);
      for (let pass = 0; pass < 2; pass++) for (let j = 0; j < k; j++) {
        let s = 0;
        for (let i = 0; i < M; i++) s += Q[j][i] * v[i];
        R[j][k] += s;
        for (let i = 0; i < M; i++) v[i] -= s * Q[j][i];
      }
      const nv = Math.hypot(...v);
      if (!(nv > 1e-10 * Math.max(1, n0))) return null;
      R[k][k] = nv;
      Q.push(v.map((x) => x / nv));
    }
    const z = Q.map((q) => q.reduce((s, x, i) => s + x * f[i], 0));
    for (let k = cols.length - 1; k >= 0; k--) {
      for (let j = k + 1; j < cols.length; j++) z[k] -= R[k][j] * z[j];
      z[k] /= R[k][k];
    }
    return z;
  };
  for (let outer = 0; outer < 4 * m + 40; outer++) {
    const r = resid();
    let t = -1, wt = 1e-11;
    for (let j = 0; j < m; j++) {
      if (inP[j] || refused.has(j)) continue;
      let w = 0;
      for (let i = 0; i < M; i++) w += E[j][i] * r[i];
      if (w > wt) { wt = w; t = j; }
    }
    if (t < 0) break;
    P.push(t); inP[t] = true;
    for (let inner = 0; inner < 4 * m + 40; inner++) {
      const z = lsq(P);
      if (!z || (inner === 0 && !(z[P.length - 1] > 0))) {
        P.pop(); inP[t] = false; refused.add(t);
        break;
      }
      if (z.every((x) => x > 0)) { P.forEach((j, q) => { u[j] = z[q]; }); refused.clear(); break; }
      let a = Infinity;
      P.forEach((j, q) => { if (z[q] <= 0) a = Math.min(a, u[j] / (u[j] - z[q])); });
      P.forEach((j, q) => { u[j] += a * (z[q] - u[j]); });
      for (let q = P.length - 1; q >= 0; q--) if (u[P[q]] <= 1e-14) { u[P[q]] = 0; inP[P[q]] = false; P.splice(q, 1); }
    }
  }
  return u;
}

/** The least-squares projection of d0 onto { d : every c(d) ≥ 0 }, or null when
 *  that set is empty: with y = d − d0 a least-distance problem, min ‖y‖ subject
 *  to co·y ≥ −c(d0), solved through its dual (Lawson–Hanson's LDP) — exact on
 *  dependent and degenerate active sets alike. */
export function nearestValid(C: readonly Lin[], d0: readonly number[]): number[] | null {
  const n = d0.length, G: number[][] = [], h: number[] = [];
  for (const c of C) {
    const L = Math.hypot(...c.co), v = linVal(c, d0);
    if (!(L > 1e-12)) { if (v < -1e-9) return null; continue; }
    G.push(c.co.map((x) => x / L)); h.push(-v / L);
  }
  const s = Math.max(1e-12, ...h.map(Math.abs));
  if (!h.some((x) => x > 0)) return d0.slice();
  const u = nnls(G.map((g, i) => [...g, h[i] / s]), [...new Array<number>(n).fill(0), 1]);
  const r = new Array<number>(n + 1).fill(0);
  r[n] = -1;
  u.forEach((x, i) => { if (x) for (let k = 0; k <= n; k++) r[k] += x * (k < n ? G[i][k] : h[i] / s); });
  if (!(Math.abs(r[n]) > 1e-12)) return null;
  return d0.map((x, k) => x - (s * r[k]) / r[n]);
}

/** A width pair's conservative halfspace at d: the signed distance to the flat
 *  edge's LINE when the nearest point is inside it, else along the current
 *  corner-to-corner direction — never more than the true distance. */
function lineariseWidth(cx: ClampCtx, d: readonly number[], p: WidthPair): Lin {
  const n = cx.fr.n, F = flatAt(cx, d);
  if (p.t > 1e-9 && p.t < 1 - 1e-9) {
    const nn = cx.fr.E[p.i].n, s = Math.sign(dot(nn, sub(F[p.e], F[p.i]))) || 1;
    return { kind: 'width', ...affine(n, (dd) => { const G = flatAt(cx, dd); return s * dot(nn, sub(G[p.e], G[p.i])) - MIN_FLAT_SIDE; }) };
  }
  const j = p.t <= 1e-9 ? p.i : (p.i + 1) % n, u = mul(sub(F[p.e], F[j]), 1 / Math.max(1e-12, len(sub(F[p.e], F[j]))));
  return { kind: 'width', ...affine(n, (dd) => { const G = flatAt(cx, dd); return dot(u, sub(G[p.e], G[j])) - MIN_FLAT_SIDE; }) };
}

/** The last valid point on [a, b] before the first invalid one. */
function walk(cx: ClampCtx, C: readonly Lin[], a: readonly number[], b: readonly number[], steps = 64): { d: number[]; s: number } {
  const at = (s: number): number[] => a.map((x, k) => x + s * (b[k] - x));
  let lo = 0, hi = -1;
  for (let k = 1; k <= steps; k++) { const s = k / steps; if (validSetbacks(cx, C, at(s))) lo = s; else { hi = s; break; } }
  if (hi < 0) return { d: at(1), s: 1 };
  for (let it = 0; it < 30; it++) { const m: number = (lo + hi) / 2; if (validSetbacks(cx, C, at(m))) lo = m; else hi = m; }
  return { d: at(lo), s: lo };
}

function anchorOf(cx: ClampCtx, C: readonly Lin[], vert: readonly boolean[], least: number): number[] | null {
  const p = nearestValid(C, vert.map((v) => (v ? 0 : least)));
  if (p && validSetbacks(cx, C, p)) return p;
  const b = bboxOf(cx.fr.E.map((e) => e.a)), top = Math.max(b.x1 - b.x0, b.y1 - b.y0) / 2;
  for (let u = least; u <= top; u += 25) {
    const q = vert.map((v) => (v ? 0 : u));
    if (validSetbacks(cx, C, q)) return q;
    const pq = nearestValid(C, q);
    if (pq && validSetbacks(cx, C, pq)) return pq;
  }
  return null;
}

/** One solve with every wall's mode held: valid as targeted, else the
 *  continuation from a valid anchor that never leaves the valid set. */
function solveModes(cx: ClampCtx, target: readonly number[], vert: readonly boolean[], relaxed: boolean): number[] | null {
  const C0 = constraintsOf(cx, vert, relaxed);
  const t = target.map((x, k) => (relaxed ? Math.max(0, x) : vert[k] ? 0 : Math.max(cx.dPlus, x)));
  if (validSetbacks(cx, C0, t)) return t;
  const a = anchorOf(cx, C0, relaxed ? vert.map(() => true) : vert, relaxed ? 0 : cx.dPlus);
  if (!a) return null;
  const C = C0.slice();
  let cur = a;
  for (let round = 0; round < 24; round++) {
    const tgt = nearestValid(C, target);
    if (!tgt) break;
    if (validSetbacks(cx, C0, tgt) && C.every((c) => linVal(c, tgt) >= -1e-6)) return tgt;
    cur = walk(cx, C0, cur, tgt).d;
    let added = 0;
    for (const p of widthPairs(cx, cur)) {
      if (p.dist >= MIN_FLAT_SIDE + 0.5) continue;
      const end = p.t > 1e-9 && p.t < 1 - 1e-9 ? -1 : p.t;
      if (C.some((c) => c.kind === 'width' && c.e === p.e && c.i === p.i && c.end === end)) continue;
      C.push({ ...lineariseWidth(cx, cur, p), e: p.e, i: p.i, end });
      added++;
    }
    if (!added) break;
  }
  return cur;
}

/** The effective target of a setback write: missing entries 600, never below 0,
 *  every collinear run at its mean. */
function setbackTarget(cx: ClampCtx, target: readonly number[] | undefined): number[] {
  const t = cx.fr.E.map((_, k) => { const x = target?.[k]; return finite(x) ? Math.max(0, x) : FRUSTUM_SETBACK; });
  return meanRuns(cx.runs, t);
}

function clampWith(cx: ClampCtx, target: number[]): { d: number[]; kept: boolean[]; target: number[]; runs: number[] } | null {
  const tried = new Set<string>();
  const modes: Array<() => boolean[] | null> = [
    () => modesOf(cx, target, cx.dPlus / 2),
    () => { const rx = solveModes(cx, target, target.map(() => false), true); return rx ? modesOf(cx, rx, cx.dPlus - 1e-9) : null; },
    () => target.map(() => false),
  ];
  for (const mk of modes) {
    const vert = mk();
    if (!vert) continue;
    const key = vert.join();
    if (tried.has(key)) continue;
    tried.add(key);
    const r = solveModes(cx, target, vert, false);
    if (!r) continue;
    const d0 = r.map((x, k) => (vert[k] ? 0 : Math.max(cx.dPlus, x)));
    // To the nanometre where that stays valid: a solve's last-digit noise
    // (287.9999999999999) is not what a wall's setback is stored as.
    const nm = d0.map((x) => Math.round(x * 1e9) / 1e9);
    const d = validSetbacks(cx, constraintsOf(cx, vert, false), nm) ? nm : d0;
    return { d, kept: target.map((t, k) => t < cx.dPlus / 2 && d[k] > 0), target, runs: cx.runs };
  }
  return null;
}

/** THE clamp: the valid setbacks nearest (least squares) the target — each
 *  wall VERTICAL (0: no face) or SLOPED (≥ d⁺), the flat ≥ 100 mm on every side
 *  and across every waist, clear of the rounded corners, simple. `kept[k]`: wall
 *  k was asked to stand vertical but may not. Null when no setbacks fit. `cove`:
 *  a coved ceiling's (`clampCtx`). */
export function frustumSetbacks(view: ProductInstance, target: readonly number[] | undefined, w: number, T: number, radii: readonly number[], cove = false):
  { d: number[]; kept: boolean[]; target: number[]; runs: number[] } | null {
  if (!isPolyRoom(view)) return null;
  const v = wallView(view), cx = clampCtx(v, w, T, radii.length === v.polygon!.length ? radii : effectiveCornerRadii(v), cove);
  return clampWith(cx, setbackTarget(cx, target));
}

/** The one message rule of every setback write: what changed between the target
 *  and the result, and why (null when nothing did, or only a run took its mean). */
export function setbackFlash(target: readonly number[], result: readonly number[], kept: readonly boolean[], dPlus: number, runs?: readonly number[]): CeilingNote | null {
  const t = runs ? meanRuns(runs, target) : [...target];
  const moved = result.map((r, k) => {
    if (Math.abs(r - t[k]) < 1e-6) return false;
    if (dPlus <= MIN_SLOPED_SETBACK + 1e-9 && t[k] < dPlus && (t[k] < dPlus / 2 ? r === 0 : Math.abs(r - dPlus) < 1e-9)) return false;
    return true;
  });
  if (!moved.some(Boolean)) return null;
  const k = kept.findIndex((x, i) => x && moved[i]);
  if (k >= 0) return note(MSG.cantRise, result[k]);
  if (dPlus > MIN_SLOPED_SETBACK + 1e-9 && moved.some((m, i) => m && t[i] > 0 && t[i] < dPlus)) return note(MSG.thickSetback, dPlus);
  if (moved.some((m, i) => m && result[i] < t[i])) return note(MSG.setbacksAdjusted);
  return note(MSG.setbacksIncreased);
}

function frustumInputs(inst: ProductInstance): { view: ProductInstance; cx: ClampCtx; stored: number[] } {
  const view = wallView(inst), w = wallThicknessOf(inst), T = ceilingThicknessOf(inst);
  const cx = clampCtx(view, w, T, effectiveCornerRadii(view), ceilingShapeOf(inst) === 'cove');
  const s = inst.ceiling?.setbacks;
  return { view, cx, stored: cx.fr.E.map((_, k) => (finite(s?.[k]) ? s![k] : FRUSTUM_SETBACK)) };
}

const sameList = (a: readonly number[], b: readonly number[]): boolean => a.length === b.length && a.every((x, k) => Math.abs(x - b[k]) < 1e-9);

/** Store the setbacks — unless (`always` unset) they are what is already drawn:
 *  a short list's missing entries read as 600, so it is left as it is. */
function writeSetbacks(inst: ProductInstance, d: readonly number[], stored: readonly number[], always = false): void {
  if (!always && sameList(d, stored)) return;
  (inst.ceiling ??= {}).setbacks = d.slice();
}

/** A truncated pyramid's or a coved ceiling's setbacks fitted to the room as it
 *  now stands, WRITTEN when they differ (what is stored is what is drawn): from
 *  `from` (the setbacks a gesture began with) or the stored ones. Its flash, or null. */
export function fitSetbacks(inst: ProductInstance, from?: readonly number[]): CeilingNote | null {
  if (!hasCeiling(inst) || !hasSetbacks(ceilingShapeOf(inst))) return null;
  const { cx, stored } = frustumInputs(inst);
  const r = clampWith(cx, setbackTarget(cx, from ?? inst.ceiling?.setbacks));
  if (!r) return null;
  writeSetbacks(inst, r.d, stored);
  return setbackFlash(r.target, r.d, r.kept, cx.dPlus, r.runs);
}

/** The stored common setback, or NaN while the walls differ ('(mixed)'). */
export function uniformSetback(inst: ProductInstance): number {
  if (!isPolyRoom(inst)) return NaN;
  const { stored } = frustumInputs(inst);
  return stored.every((x) => Math.abs(x - stored[0]) < 1e-9) ? stored[0] : NaN;
}

/** Every wall's TARGET the typed setback (≥ 0), each wall the nearest that fits. */
export function setSetback(inst: ProductInstance, mm: number): CeilingNote | null {
  if (!hasCeiling(inst) || !hasSetbacks(ceilingShapeOf(inst)) || !Number.isFinite(mm)) return null;
  const { cx, stored } = frustumInputs(inst);
  const r = clampWith(cx, setbackTarget(cx, cx.fr.E.map(() => Math.max(0, mm))));
  if (!r) return note(MSG.noFlat);
  writeSetbacks(inst, r.d, stored, true);
  return setbackFlash(r.target, r.d, r.kept, cx.dPlus, r.runs);
}

/** A flat-edge grip: wall `wall`'s (and its collinear run's) setback toward the
 *  cursor's distance from its INNER face line, snapped to another wall's within
 *  `snapMm`; the others held. Stops where the flat would break. */
export function setSetbackAt(inst: ProductInstance, wall: number, local: Vec2, snapMm = 0): CeilingNote | null {
  if (!hasCeiling(inst) || !hasSetbacks(ceilingShapeOf(inst))) return null;
  const { cx, stored: d } = frustumInputs(inst);
  const { fr, dPlus } = cx, n = fr.n;
  if (!(wall >= 0 && wall < n)) return null;
  const mine = (j: number): boolean => cx.runs[j] === cx.runs[wall];
  let want = sig(fr.E[wall], local) - cx.w;
  if (snapMm > 0) {
    let best = Infinity;
    for (let j = 0; j < n; j++) if (!mine(j) && Math.abs(d[j] - want) <= snapMm && Math.abs(d[j] - want) < Math.abs(best - want)) best = d[j];
    if (Number.isFinite(best)) want = best;
  }
  const vert0 = d.map((x) => x === 0);
  const tryMode = (goVert: boolean): { v: boolean[]; C: Lin[]; target: number[] } => {
    const tgt = d.map((x, j) => (mine(j) ? (goVert ? 0 : Math.max(dPlus, want)) : x));
    const v = modesOf(cx, tgt.map((x, j) => (mine(j) ? (goVert ? 0 : dPlus) : vert0[j] ? 0 : Math.max(dPlus, x))), dPlus / 2);
    return { v, C: constraintsOf(cx, v, false), target: tgt.map((x, j) => (v[j] ? 0 : Math.max(dPlus, x))) };
  };
  if (want < dPlus / 2) {
    const m = tryMode(true);
    if (m.v.some((x, j) => mine(j) && x) && validSetbacks(cx, m.C, m.target)) { writeSetbacks(inst, m.target, d, true); return null; }
  }
  const m = tryMode(false);
  const from = d.map((x, j) => (m.v[j] ? 0 : mine(j) ? Math.max(dPlus, d[wall]) : Math.max(dPlus, x)));
  if (!validSetbacks(cx, m.C, from)) return null;
  const r = walk(cx, m.C, from, m.target, 256);
  const out = r.d.map((x, j) => (m.v[j] ? 0 : x));
  writeSetbacks(inst, out, d, true);
  if (want < dPlus / 2) return note(MSG.cantRise, out[wall]);
  if (r.s < 1) return m.target[wall] > from[wall] ? note(MSG.flatNarrow) : note(MSG.setbackKept, out[wall]);
  if (want < dPlus && dPlus > MIN_SLOPED_SETBACK + 1e-9) return note(MSG.thickSetback, dPlus);
  return null;
}

/** One flat edge's setback typed (mm from its wall's inner face): the flat-edge
 *  grip dropped that far in, so its run moves with it and the clamp is the
 *  grip's. */
export function setWallSetback(inst: ProductInstance, wall: number, mm: number): CeilingNote | null {
  if (!hasCeiling(inst) || !hasSetbacks(ceilingShapeOf(inst)) || !Number.isFinite(mm)) return null;
  const { cx } = frustumInputs(inst), e = cx.fr.E[wall];
  return e ? setSetbackAt(inst, wall, add(lerp(e.a, e.b, 0.5), mul(e.n, cx.w + Math.max(0, mm)))) : null;
}

// ---- Availability -------------------------------------------------------------

/** Why a room cannot take a shape (an English key), or null when it can. */
export function shapeAvailability(inst: ProductInstance, shape: CeilingShape): string | null {
  if (!hasCeiling(inst)) return null;
  if (shape === 'pyramid' || shape === 'dome') return kernelOf(commonOf(inst)).length ? null : MSG.noKernel;
  if (hasSetbacks(shape)) {
    const { cx } = frustumInputs(probe(inst, { shape }));
    return clampWith(cx, setbackTarget(cx, inst.ceiling?.setbacks)) ? null : MSG.noFlat;
  }
  return null;
}

// ---- Setters ------------------------------------------------------------------
// Pure: the caller wraps them in beginGesture() / commitGesture(). Each returns
// null, or the note the command bar shows: why it clamped, or what else moved.

/** Drop a ceiling that no longer says anything. */
function pruneCeiling(inst: ProductInstance): void {
  if (inst.ceiling && !Object.keys(inst.ceiling).length) delete inst.ceiling;
}

/** The effective eave / springing a setter holds (read, never written). */
const effEave = (inst: ProductInstance): number => eaveOf(inst, externalHeight(inst), ceilingThicknessOf(inst));
const effSpringing = (inst: ProductInstance): number => springingOf(inst, externalHeight(inst), ceilingThicknessOf(inst));

/** Move the crown to H holding the eave (or springing) it was read with — stored
 *  explicitly then, since an absent one follows the crown. A crown that does not
 *  move writes nothing, so a no-op or refused edit records no change. */
function moveCrown(inst: ProductInstance, H: number, key: 'eave' | 'springing', held: number): void {
  if (H === externalHeight(inst)) return;
  (inst.ceiling ??= {})[key] = held;
  inst.height = H;
}

const probe = (inst: ProductInstance, patch: Partial<NonNullable<ProductInstance['ceiling']>>): ProductInstance =>
  ({ ...inst, ceiling: { ...inst.ceiling, ...patch } });

/** Switch the ceiling's shape. From flat the walls stay (the outside eave is the
 *  old Height, or the springing the old interior height) and the crown rises
 *  400; back to flat the walls stay; between two shapes of a family both stored
 *  figures are kept, across the families the lowest inner wall top is kept. */
export function setCeilingShape(inst: ProductInstance, shape: CeilingShape): CeilingNote | null {
  if (!hasCeiling(inst) || !CEILING_SHAPES.includes(shape)) return null;
  const from = ceilingShapeOf(inst);
  if (shape === from) return null;
  if (shape !== 'flat') { const why = shapeAvailability(inst, shape); if (why) return note(why); }
  const H = externalHeight(inst), T = ceilingThicknessOf(inst);
  if (shape === 'flat') {
    const cf = resolveCeiling(inst);
    inst.height = springsInside(from) ? cf.springing! + T : cf.eave;
    const c = inst.ceiling!;
    delete c.shape; delete c.eave; delete c.springing;
    pruneCeiling(inst);
    return null;
  }
  const c = inst.ceiling ??= {};
  const toSpring = springsInside(shape);
  if (from === 'flat') {
    if (toSpring) { c.springing = interiorHeight(inst); delete c.eave; } else { c.eave = H; delete c.springing; }
    inst.height = H + FIRST_SWITCH_RISE;
  } else if (springsInside(from) !== toSpring) {
    if (toSpring) {
      const S = Math.max(1, resolveCeiling(inst).interiorEave);
      delete c.eave; c.springing = S;
    }
  } else if (toSpring) c.springing = effSpringing(inst); else c.eave = effEave(inst);
  const wv = wallView(inst);
  if (!(Number.isInteger(c.wall) && c.wall! >= 0 && c.wall! < wv.polygon!.length)) c.wall = longestWall(wv);
  const crossing = from !== 'flat' && springsInside(from) && !toSpring;
  const S0 = crossing ? springingOf(inst, H, T) : 0;
  c.shape = shape;
  let fit: CeilingNote | null = null;
  if (hasSetbacks(shape)) fit = c.setbacks ? fitSetbacks(inst) : setSetback(inst, FRUSTUM_SETBACK);
  if (crossing) {
    delete c.springing;
    c.eave = eaveForSpringing(inst, S0);
  }
  return fit ?? resolveCeiling(inst).capped?.reason ?? null;
}

/** The outside eave for which the room's shape, at its stored Height, meets the
 *  inner wall faces at S: a secant from the flat answer S + T. */
function eaveForSpringing(inst: ProductInstance, S: number): number {
  const H = externalHeight(inst), T = ceilingThicknessOf(inst);
  const ie = (E: number): number => resolveCeiling(probe(inst, { eave: E })).interiorEave - S;
  let a = clampN(S + T, T + 1, H), fa = ie(a);
  let b = clampN(a - fa, T + 1, H), fb = ie(b);
  for (let it = 0; it < 12 && Math.abs(fb) > 1e-3 && fb !== fa; it++) {
    const c = clampN(b - (fb * (b - a)) / (fb - fa), T + 1, H);
    a = b; fa = fb; b = c; fb = ie(b);
  }
  return b;
}

/** The room's Height (mm) — the crown. Only the crown moves. */
export function setCeilingHeight(inst: ProductInstance, H: number): CeilingNote | null {
  if (!Number.isFinite(H)) return null;
  const shape = hasCeiling(inst) ? ceilingShapeOf(inst) : 'flat';
  if (shape === 'flat') { inst.height = Math.max(1, H); return null; }
  const T = ceilingThicknessOf(inst);
  if (!springsInside(shape)) {
    const E = effEave(inst);
    moveCrown(inst, Math.max(E, H), 'eave', E);
    return H < E ? note(MSG.crownBelowEave) : null;
  }
  const S = effSpringing(inst);
  if (H < S + T) { moveCrown(inst, S + T, 'springing', S); return note(MSG.crownBelowSpringing); }
  const cap = resolveCeiling(inst).riseCap ?? Infinity;
  if (H > S + T + cap) { moveCrown(inst, S + T + cap, 'springing', S); return note(shape === 'barrel' ? MSG.barrelCrownHeld : MSG.domeCrownHeld, S + T + cap); }
  moveCrown(inst, H, 'springing', S);
  return null;
}

/** The outside eave (slope / gable / frustum / pyramid); the crown holds. */
export function setEaveHeight(inst: ProductInstance, E: number): CeilingNote | null {
  if (!hasCeiling(inst) || !EAVE_SHAPES.includes(ceilingShapeOf(inst)) || !Number.isFinite(E)) return null;
  const H = externalHeight(inst), T = ceilingThicknessOf(inst), c = inst.ceiling!;
  if (E > H) { c.eave = H; return note(MSG.eaveAboveCrown); }
  if (E < T + 1) { c.eave = T + 1; return note(MSG.eaveBelowThickness); }
  c.eave = E;
  return null;
}

/** The inside springing (barrel / dome): only the walls move, the crown holds. */
export function setSpringingHeight(inst: ProductInstance, S: number): CeilingNote | null {
  const shape = ceilingShapeOf(inst);
  if (!hasCeiling(inst) || !springsInside(shape) || !Number.isFinite(S)) return null;
  const H = externalHeight(inst), T = ceilingThicknessOf(inst), c = inst.ceiling!;
  const cap = resolveCeiling(inst).riseCap ?? Infinity;
  let v = S, why: CeilingNote | null = null;
  if (v > H - T) { v = H - T; why = note(MSG.springingAboveCrown); }
  if (v < H - T - cap) { v = H - T - cap; why = note(shape === 'barrel' ? MSG.barrelSpringingHeld : MSG.domeSpringingHeld, v); }
  if (v < 1) { v = 1; why = note(MSG.springingFloor); }
  c.springing = v;
  return why;
}

/** The rise: Height − eave outside, the arch above its springing inside. */
export function setRise(inst: ProductInstance, r: number): CeilingNote | null {
  const shape = hasCeiling(inst) ? ceilingShapeOf(inst) : 'flat';
  if (shape === 'flat' || !Number.isFinite(r)) return null;
  const neg = r < 0 ? note(MSG.riseNegative) : null;
  const rr = Math.max(0, r);
  const base = springsInside(shape) ? effSpringing(inst) + ceilingThicknessOf(inst) : effEave(inst);
  const why = setCeilingHeight(inst, base + rr);
  return neg ?? why;
}

/** The typed pitch (°), 0 up to (not including) 90 on every shape that has one:
 *  the crown moves, the eave stays. Gable: the picked wall's side; pyramid and
 *  truncated pyramid: the shallowest straight-wall face. */
export function setPitch(inst: ProductInstance, deg0: number): CeilingNote | null {
  const shape = hasCeiling(inst) ? ceilingShapeOf(inst) : 'flat';
  if (!EAVE_SHAPES.includes(shape) || !Number.isFinite(deg0)) return null;
  if (deg0 >= 90) return note(MSG.pitchRange);
  const why = deg0 < 0 ? note(MSG.pitchRange) : null;
  const a = rad(Math.max(0, deg0)), tn = Math.tan(a);
  const E = effEave(inst), T = ceilingThicknessOf(inst);
  const cf = resolveCeiling(inst), md = modelOf(cf);
  if (cf.flat) return why;
  let H = E;
  if (shape === 'slope') H = E + tn * (cf.uAxis!.u1Out - cf.uAxis!.u0Out);
  else if (shape === 'gable') {
    const ax = cf.uAxis!, uri = cf.ridgeIn!;
    const pitchAt = (D: number): number => Math.atan(D / Math.max(1e-9, gableRidge(ax, uri, D, T) - ax.u0Out));
    let lo = 0, hi = tn * (ax.u1Out - ax.u0Out) * 1.01 + 1;
    for (let i = 0; i < 100; i++) { const mid = (lo + hi) / 2; if (pitchAt(mid) < a) lo = mid; else hi = mid; }
    H = E + (lo + hi) / 2;
  } else if (shape === 'pyramid') {
    const tm = md.tent!;
    let dl = -Infinity;
    tm.faces.forEach((f, k) => { if (f.wall >= 0) dl = Math.max(dl, tm.delta[k]); });
    if (!Number.isFinite(dl)) dl = Math.max(...tm.delta);
    H = tm.crownOf(dl * tn - T / Math.cos(a)).crown;
  } else {
    const fm = md.frustum!;
    let dl = -Infinity;
    fm.wallFace.forEach((fi) => { if (fi >= 0) dl = Math.max(dl, fm.delta[fi]); });
    if (!Number.isFinite(dl)) return why;
    H = E + dl * tn - T / Math.cos(a) + T;
  }
  moveCrown(inst, Math.max(E, H), 'eave', E);
  return why;
}

/** The pitches (°) of the faces over STRAIGHT walls, in face order — a rounded
 *  corner's facets and a vertical wall (no face) count in none. The Pitch row
 *  types the shallowest of them, the steepest is shown beside it. */
export function wallFacePitches(cf: CeilingField): number[] {
  const wp = modelOf(cf)?.wallPieces;
  if (cf.flat || !wp) return [];
  return wp.flatMap((w, i) => (w >= 0 && cf.under.pieces[i]?.kind === 'plane' ? [cf.facePitchDeg[i]] : []));
}

/** A truncated pyramid whose every wall rises straight to the crown — a box
 *  under its flat: no sloped face, so no eave line, rise or pitch to show (the
 *  resolver's `eave` is then the crown; the STORED eave, which the setters
 *  hold, comes back with the first sloped wall). */
export function everyWallVertical(cf: CeilingField): boolean {
  return hasSetbacks(cf.shape) && !cf.flat && !!modelOf(cf)?.frustum?.vert.every(Boolean);
}

/** Whether two fields draw the same ceiling: their crown and eave, and both
 *  surfaces over the rings' points, their edges' midpoints and a grid. */
function sameSurfaces(a: CeilingField, b: CeilingField): boolean {
  if (a.flat !== b.flat || Math.abs(a.crown - b.crown) > 1e-6 || Math.abs(a.eave - b.eave) > 1e-6) return false;
  const pts = [a.outerRing, a.innerRing].flatMap((r) => r.flatMap((p, i) => [p, lerp(p, r[(i + 1) % r.length], 0.5)]));
  const bb = bboxOf(a.outerRing);
  for (let i = 0; i <= 8; i++) for (let j = 0; j <= 8; j++) pts.push({ x: bb.x0 + ((bb.x1 - bb.x0) * i) / 8, y: bb.y0 + ((bb.y1 - bb.y0) * j) / 8 });
  return pts.every((p) => Math.abs(outerAt(a, p) - outerAt(b, p)) < 1e-6 && Math.abs(underAt(a, p) - underAt(b, p)) < 1e-6);
}

/** Point the ceiling at another wall (nothing written when it already points
 *  there, nor on a barrel turned onto a wall parallel to its own, whose vault
 *  is the same). A gable's ridge re-centres unless the new wall runs parallel to the
 *  old one; the ridge is stored along the direction wall's inward normal, so on
 *  the OPPOSITE wall its sign turns and it stays where it was. */
export function setDirectionWall(inst: ProductInstance, w: number): void {
  if (!hasCeiling(inst)) return;
  const view = wallView(inst), edges = polyEdges(view);
  if (!(Number.isInteger(w) && w >= 0 && w < edges.length)) return;
  const old = directionWall(view, inst.ceiling);
  if (w === old) return;
  const d = dot(edges[old].normal, edges[w].normal);
  if (ceilingShapeOf(inst) === 'barrel' && Math.abs(d) > 0.999 && sameSurfaces(resolveCeiling(inst), resolveCeiling(probe(inst, { wall: w })))) return;
  const c = inst.ceiling ??= {};
  if (ceilingShapeOf(inst) === 'gable' && Math.abs(d) <= 0.999) delete c.ridge;
  else if (d < -0.999 && finite(c.ridge) && c.ridge !== 0) c.ridge = -c.ridge;
  c.wall = w;
}

/** The most anti-parallel wall (ties: the longest, then the lowest index). */
export function reverseDirection(inst: ProductInstance): void {
  if (!hasCeiling(inst)) return;
  const view = wallView(inst), edges = polyEdges(view), w = directionWall(view, inst.ceiling);
  let best = -1;
  edges.forEach((e, j) => {
    if (best < 0) { best = j; return; }
    const v = dot(e.normal, edges[w].normal), bv = dot(edges[best].normal, edges[w].normal);
    if (v < bv - 1e-9 || (Math.abs(v - bv) <= 1e-9 && e.len > edges[best].len + 1e-6)) best = j;
  });
  (inst.ceiling ??= {}).wall = best;
}

/** The wall most nearly square to the current one, the next in order on a tie. */
export function turnDirection90(inst: ProductInstance): void {
  if (!hasCeiling(inst)) return;
  const view = wallView(inst), edges = polyEdges(view), n = edges.length, w = directionWall(view, inst.ceiling);
  let best = -1, bv = -Infinity;
  for (let k = 1; k < n; k++) {
    const j = (w + k) % n, v = Math.abs(crs(edges[w].normal, edges[j].normal));
    if (v > bv + 1e-9) { bv = v; best = j; }
  }
  const c = inst.ceiling ??= {};
  if (best >= 0) c.wall = best;
  if (ceilingShapeOf(inst) === 'gable') delete c.ridge;
}

/** Put the apex (pyramid / dome) at a local point, clamped where it sees every
 *  wall; each coordinate within `tolMm` of where the room puts the apex with
 *  none stored (`defaultApexIn`: the middle on the square or the oblong, deeper
 *  in on an L) snaps to it — the grip's snap, so a drag can always go home.
 *  Nothing is written where the apex already is (a stored apex past the
 *  clearance, drawn clamped, keeps its fractions), and that home is stored as
 *  none, so a drag back to where it began records no change. */
export function setApexAt(inst: ProductInstance, local: Vec2, tolMm = 0): CeilingNote | null {
  const shape = ceilingShapeOf(inst);
  if (!hasCeiling(inst) || (shape !== 'pyramid' && shape !== 'dome')) return null;
  const cm = commonOf(inst), K = kernelOf(cm);
  if (!K.length) return note(MSG.noKernel);
  const home = apexIn(cm, K, undefined).A;
  const at = { x: Math.abs(local.x - home.x) <= tolMm ? home.x : local.x, y: Math.abs(local.y - home.y) <= tolMm ? home.y : local.y };
  const { A, clamped } = nearestIn(K, at);
  const why = clamped ? note(MSG.apexKept) : null;
  const was = resolveCeiling(inst).apex;
  if (was && len(sub(A, was)) < 1e-9) return why;
  const c = inst.ceiling ??= {};
  if (len(sub(A, home)) < 1e-9) delete c.apex;
  else { const { c: o, W, D } = apexFrame(cm.view); c.apex = { x: (A.x - o.x) / W, y: (A.y - o.y) / D }; }
  return why;
}

/** The middle of the room's outline (local): where the apex's offsets are measured from. */
export function apexOrigin(inst: ProductInstance): Vec2 {
  return apexFrame(wallView(inst)).c;
}

/** The effective apex's offset from the middle of the room's outline (mm, in
 *  its own frame): what `setApexFromCentre` takes. Null for anything else, or a
 *  pyramid / dome drawn flat. */
export function apexFromCentre(cf: CeilingField): Vec2 | null {
  if ((cf.shape !== 'pyramid' && cf.shape !== 'dome') || cf.flat || !cf.apex) return null;
  return sub(cf.apex, apexFrame(modelOf(cf).cm.view).c);
}

/** The apex's offset from the room's centre (mm); a missing coordinate keeps its own. */
export function setApexFromCentre(inst: ProductInstance, xMm?: number, yMm?: number): CeilingNote | null {
  const shape = ceilingShapeOf(inst);
  if (!hasCeiling(inst) || (shape !== 'pyramid' && shape !== 'dome')) return null;
  const c = apexOrigin(inst), A = resolveCeiling(inst).apex ?? c;
  return setApexAt(inst, { x: finite(xMm) ? c.x + xMm : A.x, y: finite(yMm) ? c.y + yMm : A.y });
}

function setRidgeU(inst: ProductInstance, u: number): CeilingNote | null {
  if (!hasCeiling(inst) || ceilingShapeOf(inst) !== 'gable') return null;
  const cm = commonOf(inst), ax = axisOf(cm, directionWall(cm.view, inst.ceiling));
  const { side, mid, span } = gableInnerRidge(ax, 0);
  const v = clampN(u, ax.u0In + side, ax.u1In - side);
  const c = inst.ceiling!;
  const r = span > 1e-9 ? (v - mid) / span : 0;
  if (Math.abs(r) < 1e-12) delete c.ridge; else c.ridge = r;
  if (Math.abs(v - u) < 1e-9) return null;
  return note(side < MIN_RIDGE_SIDE ? MSG.ridgeMiddle : MSG.ridgeSide);
}

/** Put a gable's inner ridge under a local point (projected across its wall). */
export function setRidgeAt(inst: ProductInstance, local: Vec2): CeilingNote | null {
  if (!hasCeiling(inst) || ceilingShapeOf(inst) !== 'gable') return null;
  const cm = commonOf(inst), ax = axisOf(cm, directionWall(cm.view, inst.ceiling));
  return setRidgeU(inst, dot(ax.n, sub(local, ax.a)));
}

/** A gable's inner ridge, `mm` from the middle of the inside span. */
export function setRidgeFromCentre(inst: ProductInstance, mm: number): CeilingNote | null {
  if (!hasCeiling(inst) || ceilingShapeOf(inst) !== 'gable' || !Number.isFinite(mm)) return null;
  const cm = commonOf(inst), ax = axisOf(cm, directionWall(cm.view, inst.ceiling));
  return setRidgeU(inst, gableInnerRidge(ax, 0).mid + mm);
}

/** How far a gable's inner ridge stands from the middle of the inside span (mm):
 *  what `setRidgeFromCentre` takes. Null for anything else. */
export function ridgeFromCentre(cf: CeilingField): number | null {
  if (cf.shape !== 'gable' || cf.flat || !cf.uAxis || cf.ridgeIn == null) return null;
  return cf.ridgeIn - gableInnerRidge(cf.uAxis, 0).mid;
}

/** Set the ceiling's own thickness (mm), or `null` to follow the walls again.
 *  Rounded and kept in the category's wall range (0 = no ceiling, steam only).
 *  Every stored height is held — but a barrel / dome grows OUTWARD: its
 *  springing and inside rise stay, so the crown moves by the change. A truncated
 *  pyramid's setbacks are fitted to the new thickness. */
export function setCeilingThickness(inst: ProductInstance, T: number | null): CeilingNote | null {
  if (T != null && !Number.isFinite(T)) return null;
  const shape = hasCeiling(inst) ? ceilingShapeOf(inst) : 'flat';
  const H0 = externalHeight(inst), T0 = ceilingThicknessOf(inst);
  const S = springsInside(shape) ? springingOf(inst, H0, T0) : 0;
  let range: CeilingNote | null = null;
  if (T == null) {
    if (inst.ceiling) { delete inst.ceiling.thickness; pruneCeiling(inst); }
  } else {
    const { min, max } = wallThicknessRange(inst.category);
    const typed = Math.round(T);
    const t = Math.max(min, Math.min(max, typed));
    (inst.ceiling ??= {}).thickness = t;
    if (t !== typed) range = { text: MSG.thickness, min, max };
  }
  const T1 = ceilingThicknessOf(inst);
  let moved: CeilingNote | null = null;
  if (springsInside(shape) && T1 !== T0) {
    const rho = Math.max(0, H0 - T0 - S);
    inst.ceiling!.springing = S;
    inst.height = S + T1 + rho;
    moved = note(MSG.crownMoved, inst.height);
  } else if (shape === 'frustum') moved = fitSetbacks(inst);
  return moved ? (range ? { ...moved, then: range } : moved) : range;
}

/** Give the ceiling its own colour, or `null` to follow the walls again. A
 *  string that is no `#rrggbb(aa)` colour changes nothing. */
export function setCeilingColor(inst: ProductInstance, hex: string | null): void {
  if (hex != null && !COLOR_RE.test(hex)) return;
  if (hex == null) {
    if (inst.ceiling) { delete inst.ceiling.color; pruneCeiling(inst); }
    return;
  }
  (inst.ceiling ??= {}).color = hex;
}

/** A mirror flips the room's local x: the apex follows. (Walls, ridge and
 *  setbacks are measured along each wall's own normal, which the winding gives.) */
export function mirrorCeiling(inst: ProductInstance): void {
  const a = inst.ceiling?.apex;
  if (a && finite(a.x)) a.x = -a.x;
}

/** Make a ceiling read from a file, the clipboard or a template valid: drop a
 *  ceiling on anything that cannot have one, then what cannot be read (an
 *  unknown shape, a non-finite figure, an out-of-range wall, a figure the shape
 *  does not store, a malformed setback list, setbacks past the wall count, a
 *  colour that is no `#rrggbb(aa)`, an empty ceiling), keep the thickness in the
 *  category's wall range as its setter does, then raise the crown to its floor —
 *  never below the eave (slope / gable / frustum / pyramid), nor below the
 *  springing plus the ceiling thickness (barrel / dome) — and fit a truncated
 *  pyramid's setbacks to the room. The walls never move. A valid ceiling is
 *  left exactly as it is. */
export function normalizeCeiling(inst: ProductInstance): void {
  const raw = inst.ceiling as unknown;
  if (raw === undefined) return;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw) || !hasCeiling(inst)) { delete inst.ceiling; return; }
  const c = inst.ceiling!;
  if (c.shape !== undefined && !CEILING_SHAPES.includes(c.shape)) delete c.shape;
  const shape = ceilingShapeOf(inst);
  if (c.eave !== undefined && !(finite(c.eave) && EAVE_SHAPES.includes(shape))) delete c.eave;
  if (c.springing !== undefined && !(finite(c.springing) && SPRINGING_SHAPES.includes(shape))) delete c.springing;
  if (c.thickness !== undefined) {
    if (!finite(c.thickness)) delete c.thickness;
    else {
      const { min, max } = wallThicknessRange(inst.category);
      if (c.thickness < min || c.thickness > max) c.thickness = Math.max(min, Math.min(max, c.thickness));
    }
  }
  if (c.color !== undefined && !(typeof c.color === 'string' && COLOR_RE.test(c.color))) delete c.color;
  const walls = wallRuns(inst).length;
  if (c.wall !== undefined && !(Number.isInteger(c.wall) && c.wall >= 0 && c.wall < walls)) delete c.wall;
  if (c.setbacks !== undefined) {
    if (!Array.isArray(c.setbacks) || !c.setbacks.every((d) => finite(d) && d >= 0)) delete c.setbacks;
    else if (c.setbacks.length > walls) c.setbacks = c.setbacks.slice(0, walls);
    if (c.setbacks && !c.setbacks.length) delete c.setbacks;
  }
  const H = externalHeight(inst);
  if (c.eave !== undefined && H < c.eave) inst.height = c.eave;
  if (c.springing !== undefined && H < c.springing + ceilingThicknessOf(inst)) inst.height = c.springing + ceilingThicknessOf(inst);
  pruneCeiling(inst);
  if (inst.ceiling && hasSetbacks(shape)) fitSetbacks(inst);
}

// ---- Cut by the ceiling --------------------------------------------------------

/** The underside's lowest point over a LOCAL polygon: along its edges (where they
 *  change piece, and every curve sample) and at the underside tiling's vertices
 *  inside it — exact for the planar shapes and for concave curved pieces. */
export function minUnderOver(cf: CeilingField, poly: readonly Vec2[]): number {
  let m = Infinity;
  for (let i = 0; i < poly.length; i++) {
    for (const p of profileAlong(cf, poly[i], poly[(i + 1) % poly.length], 'under')) m = Math.min(m, p.h);
  }
  if (poly.length >= 3) {
    for (const t of ceilingTris(cf, 'under')) for (const p of [t.a, t.b, t.c]) if (pointInPolygon(p, poly)) m = Math.min(m, underAt(cf, p));
  }
  return m;
}

/** The ids of the panes, openings, doors, heater walls and partitions with a
 *  height of their own whose top rises above the underside over them: the
 *  ceiling clips them (section, 3D), their stored heights are kept. A partition
 *  with no height follows the underside, so it is never cut. A flat room clips
 *  nothing of its own. */
export function ceilingCuts(inst: ProductInstance): Set<string> {
  const out = new Set<string>();
  if (isFlatCeiling(inst)) return out;
  const cf = resolveCeiling(inst);
  const above = (top: number, polys: readonly Vec2[][]): boolean => polys.some((p) => p.length >= 3 && top > minUnderOver(cf, p) + 1e-6);
  for (const g of inst.glass ?? []) if (g.id && above(glassPaneTop(inst, g), glassPolysLocal(inst, g))) out.add(g.id);
  for (const o of inst.openings ?? []) if (o.id && above(openingArchitrave(inst, o), [openingBandLocal(inst, o)])) out.add(o.id);
  for (const d of roomDoors(inst)) {
    const band = d.id ? roomDoor(inst, d)?.gap ?? null : null;
    if (band && above(doorTop(inst, d), [band])) out.add(d.id!);
  }
  for (const hb of heaterWallBands(inst)) if (hb.wall.id && above(heaterWallTop(hb.wall), [hb.poly])) out.add(hb.wall.id);
  for (const iw of inst.innerWalls ?? []) {
    if (iw.id && iw.edge != null && iw.height != null && above(innerWallTop(inst, iw), polyInnerWallFill(inst, iw))) out.add(iw.id);
  }
  return out;
}

// ---- Warnings (D9: information only, nothing is refused) ---------------------------

/** A sauna's least clear headroom over its top bench (mm). */
export const MIN_HEADROOM = 1000;
/** A steam room's ceiling face sheds its condensation from this pitch (°) up. */
export const MIN_DRIP_PITCH = 10;

/** The headroom note: under `MIN_HEADROOM` by more than 1e-6 mm, so that the
 *  figure, shown rounded down (`limit`), always reads under the limit. */
const headroomNote = (h: number): CeilingNote | null =>
  h < MIN_HEADROOM - 1e-6 ? { text: MSG.headroom, n: h, limit: MIN_HEADROOM } : null;

/** The clear headroom over each TOP bench of a sauna — the benches, on its walls
 *  (`polyBenchBands`) or on a partition (`polyInnerBenchBands`), of the greatest
 *  seat height: the underside's lowest point over its band less its seat (a
 *  flat room's level underside, `interiorHeight`; never below 0 — an underside
 *  hung below the seat leaves none). Empty for anything else. */
export function topBenchHeadrooms(inst: ProductInstance): Array<{ id: string; headroom: number }> {
  if (!hasCeiling(inst) || inst.category !== 'sauna') return [];
  const seats = roomBenchSeats(inst);
  if (!seats.length) return [];
  const top = Math.max(...seats.map((q) => q.seat));
  const cf = isFlatCeiling(inst) ? null : resolveCeiling(inst);
  return seats.filter((q) => q.seat >= top - 1e-9).map(({ id, poly, seat }) => ({ id, headroom: Math.max(0, (cf ? minUnderOver(cf, poly) : interiorHeight(inst)) - seat) }));
}

/** Every bench of a room, on its walls (`polyBenchBands`) or on a partition
 *  (`polyInnerBenchBands`), with its band (local) and its seat height above the
 *  room's floor — a stepped profile bench's lift included. */
export function roomBenchSeats(inst: ProductInstance): Array<{ id: string; poly: Vec2[]; seat: number }> {
  if (!isPolyRoom(inst)) return [];
  const src = { benchTierHeights: inst.benchTierHeights };
  const lifts = polyBenchLifts(inst);
  return [
    ...polyBenchBands(inst).map((b) => ({ id: b.id, poly: b.poly, seat: benchHeight({ tier: b.level, height: inst.polyBenches?.find((x) => x.id === b.id)?.height }, src) + (lifts.get(b.id) ?? 0) })),
    ...polyInnerBenchBands(inst).map((b) => ({ id: b.id, poly: b.poly, seat: benchHeight({ tier: b.level, height: b.leg.height }, src) + b.lift })),
  ];
}

/** The headroom note of one bench: when it is a top bench under `MIN_HEADROOM`. */
export function benchHeadroomNote(inst: ProductInstance, benchId: string): CeilingNote | null {
  const h = topBenchHeadrooms(inst).find((q) => q.id === benchId);
  return h ? headroomNote(h.headroom) : null;
}

/** Whether a steam room's ceiling has a face flatter than `MIN_DRIP_PITCH`: a
 *  flat ceiling (or a shape drawn flat) is one face at 0°, a slope's and a
 *  gable's faces all count, a pyramid's and a truncated pyramid's only those
 *  over STRAIGHT walls (`wallFacePitches`: a rounded corner's facets, O6, the
 *  truncated pyramid's flat, F4, and a vertical wall, which has no face, U1,
 *  count in none); a barrel or a dome always sheds it (D9), and a 0 mm ceiling
 *  has no face to drip from (O2). */
export function ceilingDrips(inst: ProductInstance): boolean {
  if (!hasCeiling(inst) || inst.category !== 'steamroom' || ceilingThicknessOf(inst) <= 0) return false;
  if (isFlatCeiling(inst)) return true;
  const cf = resolveCeiling(inst);
  if (cf.flat) return true;
  // Drawn level all over (no rise; a truncated pyramid whose every wall stands
  // vertical): a flat ceiling whatever its shape, so it drips like one.
  if (cf.crown - cf.eave <= 1e-6) return true;
  if (springsInside(cf.shape)) return false;
  const pitches = cf.shape === 'slope' || cf.shape === 'gable' ? cf.facePitchDeg : wallFacePitches(cf);
  return pitches.some((p) => p < MIN_DRIP_PITCH - 1e-9);
}

/** The Ceiling card's notes, in order: why a shape draws flat, a capped rise, a
 *  sauna's top bench under 1000 mm of headroom (the least of them), a steam
 *  room's face that drips. A flat room builds no field. */
export function ceilingWarnings(inst: ProductInstance): CeilingNote[] {
  const out: CeilingNote[] = [];
  if (!hasCeiling(inst)) return out;
  if (!isFlatCeiling(inst)) {
    const cf = resolveCeiling(inst);
    if (cf.invalid) out.push(cf.invalid);
    if (cf.capped) out.push(cf.capped.reason);
  }
  const hr = topBenchHeadrooms(inst);
  const head = hr.length ? headroomNote(Math.min(...hr.map((q) => q.headroom))) : null;
  if (head) out.push(head);
  if (ceilingDrips(inst)) out.push({ text: MSG.drip, min: MIN_DRIP_PITCH });
  return out;
}

// ---- Plan marks -------------------------------------------------------------------

/** What the plan shows of a shaped ceiling, in the room's LOCAL frame: the
 *  underside's lines, the fall arrows (unit DOWNHILL direction), the grips and
 *  the crown figure. The ONE list the canvas, a print and an export draw. */
export interface CeilingMarks {
  /** `seam`: a rounded corner's fan seam — drawn, but a chord of the tiling, not
   *  an edge of the shape (`isFacetSeam`): its outer end is a sample of the arc. */
  lines: Array<{ pts: Vec2[]; kind: 'ridge' | 'crease' | 'crown' | 'springing' | 'flat'; closed?: boolean; seam?: boolean }>;
  arrows: Array<{ at: Vec2; dir: Vec2; drag: boolean }>;
  grips: Array<{ kind: 'apex' | 'ridge' | 'setback'; at: Vec2; walls?: number[] }>;
  /** True points of the shape that no drawn line offers as an end — a pyramid's
   *  or a dome's apex, which on a room rounded at every corner only fan seams reach: snap
   *  points (`ceilingSnapMarks`). */
  points?: Vec2[];
  /** Where the crown figure goes. `side`: the crown point lies inside the room ON
   *  a line the plan draws (a gable's ridge, a barrel's crown line) — a LOCAL unit
   *  normal of that line; the figure is set 14 px off it, on whichever side its
   *  text runs away from the line. `around`: the point is where lines meet (a
   *  pyramid's or a dome's apex, its grip) — the LOCAL unit directions from it of what the plan
   *  draws there (creases, arrows); the figure goes into the widest gap between
   *  them that opens to the right ON SCREEN, chosen once projected (`crownGap`).
   *  Neither: the point is on the outline and the figure goes inward. */
  crown: { at: Vec2; value: number; side?: Vec2; around?: Vec2[] } | null;
}

const noMarks = (): CeilingMarks => ({ lines: [], arrows: [], grips: [], crown: null });

/** The part of each convex polygon where n·p ≥ c. */
function clipHalves(parts: readonly Vec2[][], n: Vec2, c: number): Vec2[][] {
  return parts.map((q) => clipHalfPlane(q, n, c)).filter((q) => q.length >= 3);
}

/** The centroid of the largest triangle of convex polygons' fans (null: none). */
function largestTriCentroid(parts: readonly Vec2[][]): Vec2 | null {
  let best: Vec2 | null = null, bestA = 1e-6;
  for (const q of parts) {
    for (let i = 1; i + 1 < q.length; i++) {
      const t = [q[0], q[i], q[i + 1]], A = Math.abs(area2(t));
      if (A > bestA) { bestA = A; best = centroidOf(t); }
    }
  }
  return best;
}

/** Every mark of a room's ceiling; nothing for a flat one (or one drawn flat). */
export function ceilingPlanMarks(inst: ProductInstance): CeilingMarks {
  if (isFlatCeiling(inst)) return noMarks();
  const cf = resolveCeiling(inst);
  if (cf.flat) return noMarks();
  if (cf.shape === 'slope') {
    const n = cf.uAxis!.n;
    return {
      lines: [],
      arrows: [{ at: insidePoint(cf.innerRing), dir: { x: -n.x, y: -n.y }, drag: true }],
      grips: [],
      crown: cf.crownAt ? { at: cf.crownAt, value: cf.crown } : null,
    };
  }
  if (cf.shape === 'gable') {
    const ax = cf.uAxis!, uri = cf.ridgeIn!, s = ringStretch(cf.innerRing, ax, uri);
    const m = sideMarks(cf, uri, 'ridge');
    return { ...m, grips: s ? [{ kind: 'ridge', at: add(s.o, mul(s.dir, (s.t0 + s.t1) / 2)) }] : [] };
  }
  if (hasSetbacks(cf.shape)) return frustumMarks(cf);
  if (cf.shape === 'pyramid' || cf.shape === 'dome') return apexMarks(cf);
  if (cf.shape === 'barrel') {
    const ax = cf.uAxis!, m = sideMarks(cf, (ax.u0In + ax.u1In) / 2, 'crown');
    const springing = [ax.u0In, ax.u1In].flatMap((u) => ringRunsOn(cf.innerRing, ax, u)).map((pts) => ({ pts, kind: 'springing' as const }));
    return { ...m, lines: [...m.lines, ...springing], grips: [] };
  }
  return noMarks();
}

/** The marks of a line u = `u` across a ceiling's axis — a gable's ridge, a
 *  barrel's crown line: one line per stretch of it inside the room, an arrow
 *  down each side of it, each at the largest triangle of the inside cut there
 *  (0: the picked wall's side, u ≤ `u`) so it stays inside a concave room, and
 *  the crown figure set off square to the line. */
function sideMarks(cf: CeilingField, u: number, kind: 'ridge' | 'crown'): Omit<CeilingMarks, 'grips'> {
  const ax = cf.uAxis!, n = ax.n, c = u + dot(n, ax.a), back = (): Vec2 => ({ x: -n.x, y: -n.y });
  const parts = convexParts(cf.innerRing);
  const at = [clipHalves(parts, back(), -c), clipHalves(parts, n, c)].map(largestTriCentroid);
  const arrows: CeilingMarks['arrows'] = [];
  if (at[0]) arrows.push({ at: at[0], dir: back(), drag: true });
  if (at[1]) arrows.push({ at: at[1], dir: { x: n.x, y: n.y }, drag: true });
  return {
    lines: ringStretches(cf.innerRing, ax, u).map((l) => ({ pts: [add(l.o, mul(l.dir, l.t0)), add(l.o, mul(l.dir, l.t1))], kind })),
    arrows,
    crown: cf.crownAt ? { at: cf.crownAt, value: cf.crown, side: back() } : null,
  };
}

/** The straight runs of a ring that lie ON the line u = `at` (a barrel's
 *  springing lines: its inner wall faces at the extremes of the span), each one
 *  line end to end however many vertices it holds. */
function ringRunsOn(ring: readonly Vec2[], ax: { n: Vec2; a: Vec2 }, at: number): Vec2[][] {
  const N = ring.length, on = ring.map((p) => Math.abs(dot(ax.n, sub(p, ax.a)) - at) < 1e-6);
  const edgeOn = (i: number): boolean => on[i] && on[(i + 1) % N];
  let s = 0;
  while (s < N && edgeOn((s + N - 1) % N)) s++;
  if (s >= N) return [];
  const out: Vec2[][] = [];
  let cur: Vec2[] | null = null;
  for (let k = 0; k < N; k++) {
    const i = (s + k) % N;
    if (edgeOn(i)) { cur ??= [ring[i]]; cur[1] = ring[(i + 1) % N]; }
    else if (cur) { out.push(cur); cur = null; }
  }
  if (cur) out.push(cur);
  return out;
}

/** The walls of each collinear run, in ring order, the runs from wall 0's on. */
function runWalls(fr: Frame): number[][] {
  const runs = runsOf(fr), out: number[][] = [];
  let s = 0;
  while (s < fr.n && inLineAt(fr, s)) s++;
  if (s >= fr.n) return [fr.E.map((_, k) => k)];
  for (let k = 0; k < fr.n; k++) {
    const v = (s + k) % fr.n, last = out[out.length - 1];
    if (last && runs[last[0]] === runs[v]) last.push(v); else out.push([v]);
  }
  return out.sort((a, b) => Math.min(...a) - Math.min(...b));
}

/** A polyline without the points that lie in line with their neighbours: a
 *  cove's crease is straight (two coves meet where their distances over their
 *  setbacks agree), but its tiling's strips chop it at every sample. */
function straightened(pts: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [pts[0]];
  for (let i = 1; i + 1 < pts.length; i++) {
    const a = out[out.length - 1], b = pts[i], c = pts[i + 1], L = len(sub(c, a));
    if (L < 1e-9 || Math.abs(crs(sub(b, a), sub(c, a))) / L > 1e-6) out.push(b);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/** A truncated pyramid or a coved ceiling on the plan: its flat (closed), the
 *  corner creases, one arrow down each SLOPED flat edge (a collinear run is one
 *  edge) in the middle of its band, one setback grip per flat edge — a vertical
 *  wall's on its inner face —, and the crown inside the flat. */
function frustumMarks(cf: CeilingField): CeilingMarks {
  const md = modelOf(cf), fm = md.frustum!, fr = fm.fr, F = cf.flatRing!, n = fr.n;
  const FLAT = md.wallPieces!.length;
  const inner = flatOf(fr, fr.E.map(() => md.cm.w));
  const pc = cf.under.pieces;
  // Two walls in line make two faces of ONE surface (a corner inserted in line).
  const same = (i: number, j: number): boolean => {
    const a = pc[i], b = pc[j];
    if (a.kind === 'plane' && b.kind === 'plane') return Math.abs(a.gx - b.gx) < 1e-12 && Math.abs(a.gy - b.gy) < 1e-12 && Math.abs(a.c - b.c) < 1e-6;
    return a.kind === 'cove' && b.kind === 'cove' && Math.abs(a.nx - b.nx) < 1e-12 && Math.abs(a.ny - b.ny) < 1e-12
      && Math.abs(a.d0 - b.d0) < 1e-6 && Math.abs(a.top - b.top) < 1e-6 && Math.abs(a.a - b.a) < 1e-6;
  };
  const creases = ceilingCreases(cf, 'under').filter((s) => !s.pieces.includes(FLAT) && (md.springPiece === undefined || !s.pieces.includes(md.springPiece)) && !same(s.pieces[0], s.pieces[1]));
  const lines: CeilingMarks['lines'] = [
    { pts: F.slice(), kind: 'flat', closed: true },
    ...creasePolylines(creases).map((l) => (cf.shape === 'cove' ? { ...l, pts: straightened(l.pts) } : l))
      .map((l) => (isFacetSeam(cf, l) ? { pts: l.pts, kind: 'crease' as const, seam: true } : { pts: l.pts, kind: 'crease' as const })),
  ];
  const arrows: CeilingMarks['arrows'] = [], grips: CeilingMarks['grips'] = [];
  for (const run of runWalls(fr)) {
    const k0 = run[0], k1 = (run[run.length - 1] + 1) % n;
    const flatMid = lerp(F[k0], F[k1], 0.5);
    grips.push({ kind: 'setback', at: flatMid, walls: run.slice() });
    if (fm.vert[k0]) continue;
    const e = fr.E[k0];
    arrows.push({ at: lerp(lerp(inner[k0], inner[k1], 0.5), flatMid, 0.5), dir: { x: -e.n.x, y: -e.n.y }, drag: false });
  }
  return { lines, arrows, grips, crown: cf.crownAt ? { at: cf.crownAt, value: cf.crown } : null };
}

/** A pyramid or a dome on the plan: its underside's creases from the apex — a
 *  dome's curved, each one polyline from its inner corner to the apex — (a
 *  rounded corner's fan seams flagged), one arrow per straight wall face square
 *  to its wall — walls in line are one face, with one arrow — halfway from the
 *  apex to the middle of the face's inner wall run, or, where that point lies on
 *  another face (a steep face in a concave room), inside its own; the apex grip,
 *  the apex as a snap point, and the crown at the apex, its figure set off into
 *  the widest gap between what leaves the apex (`around`). */
function apexMarks(cf: CeilingField): CeilingMarks {
  const md = modelOf(cf), A = cf.apex!, inner = md.cm.inn.edges;
  // A pyramid's faces stand on the OUTER ring, a dome's on the inner one.
  const faces = md.tent ? md.tent.faces : inner;
  const at0 = (p: Vec2): boolean => len(sub(p, A)) < 1e-6;
  // A dome's crease from its inner corner: a face capped at its quarter circle
  // rises vertically there, so the computed meeting stops a few nanometres short.
  const fromCorner = (pts: Vec2[]): Vec2[] => {
    const c = cf.innerRing.find((p) => len(sub(p, pts[0])) < CREASE_GAP);
    return c ? [c, ...pts.slice(1)] : pts;
  };
  const polys = creasePolylines(ceilingCreases(cf, 'under').filter((s) => md.springPiece === undefined || !s.pieces.includes(md.springPiece)))
    .map((l) => (cf.shape === 'dome' ? { ...l, pts: fromCorner(at0(l.pts[0]) ? l.pts.slice().reverse() : l.pts) } : l));
  const lines: CeilingMarks['lines'] = polys
    .map((l) => (isFacetSeam(cf, l) ? { pts: l.pts, kind: 'crease' as const, seam: true } : { pts: l.pts, kind: 'crease' as const }));
  const arrows: CeilingMarks['arrows'] = [];
  for (const run of runWalls(frameOf(md.cm.view))) {
    const ks = run.map((w) => faces.findIndex((f) => f.wall === w)).filter((k) => k >= 0);
    if (!ks.length) continue;
    const ins = run.map((w) => inner.find((e) => e.wall === w)).filter((e): e is RingEdge => !!e);
    const n = faces[ks[0]].n;
    const mid = ins.length
      ? lerp(ins[0].a, ins[ins.length - 1].b, 0.5)
      : add(lerp(faces[ks[0]].a, faces[ks[ks.length - 1]].b, 0.5), mul(n, md.cm.w));
    let at = lerp(A, mid, 0.5);
    // A dome with no rise has no faces to stand on.
    if (md.wallPieces && !ks.includes(activePiece(cf, 'under', at))) {
      at = largestTriCentroid(ceilingPieces(cf, 'under').filter((pc) => ks.includes(pc.piece)).map((pc) => pc.ring)) ?? at;
    }
    arrows.push({ at, dir: { x: -n.x, y: -n.y }, drag: false });
  }
  // What leaves the apex: each line's first run from it (a curve's tangent there), each arrow.
  const leaving = polys.flatMap((l) => {
    const P = l.pts, k = P.length;
    return at0(P[0]) ? [P[1]] : at0(P[k - 1]) ? [P[k - 2]] : [P[0], P[k - 1]];
  });
  const around = [...leaving, ...arrows.map((a) => a.at)]
    .filter((p) => len(sub(p, A)) > 1e-6).map((p) => mul(sub(p, A), 1 / len(sub(p, A))));
  return { lines, arrows, grips: [{ kind: 'apex', at: A }], points: [A], crown: { at: A, value: cf.crown, around } };
}

/** Where a figure set beside a point goes, given the unit directions from it of
 *  what is drawn there (in the frame the figure is drawn in, +x its text's
 *  direction): the bisector of the widest gap between them weighted by how far
 *  it opens toward +x, else — no gap opens there — of the widest gap; +x when
 *  nothing is drawn. */
export function crownGap(dirs: readonly Vec2[]): Vec2 {
  const angs = dirs.map((d) => Math.atan2(d.y, d.x)).sort((a, b) => a - b);
  let side: Vec2 = { x: 1, y: 0 }, best = -1;
  for (const right of [true, false]) {
    angs.forEach((a, i) => {
      const b = i + 1 < angs.length ? angs[i + 1] : angs[0] + 2 * Math.PI, mid = (a + b) / 2;
      const d = { x: Math.cos(mid), y: Math.sin(mid) }, score = (b - a) * (right ? Math.max(0, d.x) : 1);
      if (score > best + 1e-9 || (Math.abs(score - best) <= 1e-9 && d.x > side.x)) { best = score; side = d; }
    });
    if (best > 1e-9) break;
  }
  return side;
}

/** A room's marks projected through `S` (local → screen): its lines, each arrow
 *  as a 28 px shaft centred on its anchor (tail → tip, downhill), and the crown
 *  figure's point with its directions on screen — `side` projected, `toward` the
 *  gap `around` opens (`crownGap`, chosen after the projection, so a turned room
 *  keeps its figure clear). What render.ts's `ceilingMarksScreen` returns. */
export function ceilingMarksProjected(marks: CeilingMarks, S: (p: Vec2) => Vec2): {
  lines: Array<{ pts: Vec2[]; closed?: boolean }>;
  arrows: Array<{ tail: Vec2; tip: Vec2; drag: boolean }>;
  crown: { at: Vec2; value: number; side?: Vec2; toward?: Vec2 } | null;
} {
  const unit = (at: Vec2, d: Vec2): Vec2 => {
    const c = S(at), f = S({ x: at.x + d.x * 100, y: at.y + d.y * 100 });
    const L = Math.hypot(f.x - c.x, f.y - c.y) || 1;
    return { x: (f.x - c.x) / L, y: (f.y - c.y) / L };
  };
  const cr = marks.crown;
  return {
    lines: marks.lines.map((l) => ({ pts: l.pts.map(S), closed: l.closed })),
    arrows: marks.arrows.map((a) => {
      const c = S(a.at), { x: ux, y: uy } = unit(a.at, a.dir);
      return { tail: { x: c.x - ux * 14, y: c.y - uy * 14 }, tip: { x: c.x + ux * 14, y: c.y + uy * 14 }, drag: a.drag };
    }),
    crown: cr
      ? {
        at: S(cr.at), value: cr.value,
        ...(cr.side ? { side: unit(cr.at, cr.side) } : {}),
        ...(cr.around ? { toward: crownGap(cr.around.map((d) => unit(cr.at, d))) } : {}),
      }
      : null,
  };
}

/** What a room's shown marks offer the object snap, in its local frame: the
 *  marks' own `points`, an open line's two ends and a closed line's corners
 *  (`endpoint`s), and their runs — a straight line one run end to end, a bent
 *  one its chords flagged as a curve (no chord midpoint, no crossing of two of
 *  its chords). A fan seam offers nothing: its outer end is a sample of the arc,
 *  its midpoint an artefact (its inner end, a pyramid's or a dome's apex, is in `points`). */
export function ceilingSnapMarks(m: Pick<CeilingMarks, 'lines' | 'points'>): { points: Vec2[]; runs: Array<[Vec2, Vec2, boolean]> } {
  const points: Vec2[] = [], runs: Array<[Vec2, Vec2, boolean]> = [], seen = new Set<string>();
  const point = (p: Vec2): void => { const k = `${p.x},${p.y}`; if (!seen.has(k)) { seen.add(k); points.push(p); } };
  for (const p of m.points ?? []) point(p);
  for (const l of m.lines) {
    const P = l.pts, n = P.length;
    if (l.seam || n < 2) continue;
    if (l.closed) {
      P.forEach(point);
      for (let i = 0; i < n; i++) runs.push([P[i], P[(i + 1) % n], false]);
      continue;
    }
    const a = P[0], b = P[n - 1];
    point(a); point(b);
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    const straight = L > 1e-9 && P.every((q) => Math.abs((b.x - a.x) * (q.y - a.y) - (b.y - a.y) * (q.x - a.x)) / L <= 1e-6 * Math.max(1, L));
    if (straight || n === 2) runs.push([a, b, false]);
    else for (let i = 0; i + 1 < n; i++) runs.push([P[i], P[i + 1], true]);
  }
  return { points, runs };
}

/** What is DRAWN of a room's marks: a selected room shows its lines (and their
 *  snap `points`), arrows and crown figure (the single selection's arrows drag
 *  and its grips show); with `all` (Show ceilings) every other room shows its
 *  lines and arrows. A 0 mm
 *  ceiling does not exist: no line and no figure anywhere, only a selected
 *  room's arrows and grips, by which its shape is still set. */
export function ceilingMarksShown(inst: ProductInstance, o: { all: boolean; selected: boolean; single: boolean }): CeilingMarks {
  if (!o.selected && !o.all) return noMarks();
  const T = ceilingThicknessOf(inst);
  if (!o.selected && !(T > 0)) return noMarks();
  const m = ceilingPlanMarks(inst);
  const arrows = m.arrows.map((a) => ({ ...a, drag: a.drag && o.selected && o.single }));
  const points = T > 0 && m.points ? { points: m.points } : {};
  if (o.selected) return { lines: T > 0 ? m.lines : [], ...points, arrows, grips: o.single ? m.grips : [], crown: T > 0 ? m.crown : null };
  return { lines: m.lines, ...points, arrows, grips: [], crown: null };
}
